import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import api from '../api/api';
import TopNav from '../components/TopNav';
import CustomerCrm from '../components/CustomerCrm';
import EmailActions from '../components/EmailActions';
import InvoicePrintEmailActions from '../components/InvoicePrintEmailActions';
import useBusinessAddress from '../hooks/useBusinessAddress';
import { useAuth } from '../context/AuthContext';
import { can, hasPermission } from '../accessControl';
import { paymentPermissionResource } from '../utils/paymentPermissionResource';
import { isCreditMemoPayment, savePaymentReviewChanges } from '../utils/paymentReview';
import {
  formatCalendarDate,
  formatDateInputValue,
  formatDateParts,
  formatDateTimeConsistent,
  getDatePartsFromString,
} from '../utils/dateDisplay';
import {
  invoiceEditTaxOptions,
  ShipDocInvoiceDocument,
  shippingInvoiceEditDraftFromDocument,
  transferToEmailInvoice,
} from './TransfersPage';
import './css/InventoryPage.css';
import './css/InvoicesPage.css';
import './css/TransfersPage.css';
import './css/PaymentsPage.css';

const currency = (value) => Number(value || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const BUSINESS_TIME_ZONE = 'America/New_York';
const customerName = (customer) => customer?.name || customer?.companyname || [customer?.firstname, customer?.lastname].filter(Boolean).join(' ') || 'Unnamed customer';
const customerId = (customer) => customer?.nccustomerid || customer?.id;
const customerTypeLabel = (customer, customerTypes = []) => {
  const explicit = String(customer?.customerTypeName || customer?.customer_type_name || '').trim();
  if (explicit) return explicit;
  const typeId = String(customer?.type ?? customer?.customertypeid ?? customer?.customerTypeId ?? '');
  return customerTypes.find((type) => String(type.id) === typeId)?.name || '';
};
const isPhoneLikeAddressPart = (value) => {
  const text = String(value || '').trim();
  const digits = text.replace(/\D/g, '');
  return digits.length >= 7 && /^[\d\s().+-]+$/.test(text);
};
const customerAddressParts = (customer, prefix) => {
  const street = [
    customer?.[`${prefix}addr1`] || customer?.[`${prefix}_addr_1`] || customer?.[`${prefix}Addr1`],
    customer?.[`${prefix}addr2`] || customer?.[`${prefix}_addr_2`] || customer?.[`${prefix}Addr2`],
    customer?.[`${prefix}addr3`] || customer?.[`${prefix}_addr_3`] || customer?.[`${prefix}Addr3`],
    customer?.[`${prefix}addr4`] || customer?.[`${prefix}_addr_4`] || customer?.[`${prefix}Addr4`],
  ].map((value) => String(value || '').trim()).filter((value) => value && !isPhoneLikeAddressPart(value)).join(', ');
  const cityStateZip = [
    customer?.[`${prefix}city`] || customer?.[`${prefix}_city`] || customer?.[`${prefix}City`],
    customer?.[`${prefix}state`] || customer?.[`${prefix}_state`] || customer?.[`${prefix}State`],
    customer?.[`${prefix}zip`] || customer?.[`${prefix}_zip`] || customer?.[`${prefix}Zip`],
  ].map((value) => String(value || '').trim()).filter(Boolean).join(' ');
  const country = String(customer?.[`${prefix}country`] || customer?.[`${prefix}_country`] || customer?.[`${prefix}Country`] || '').trim();
  return [street, cityStateZip, country].filter(Boolean);
};
const customerAddressLine = (customer) => customerAddressParts(customer, 'bill').join(', ');
const addressSignature = (parts = []) => parts.join(' ').toLowerCase().replace(/[^a-z0-9]+/g, '');
const customerShippingAddressLine = (customer) => {
  const shippingParts = customerAddressParts(customer, 'ship');
  if (!shippingParts.length) return '';
  const billingParts = customerAddressParts(customer, 'bill');
  return addressSignature(shippingParts) && addressSignature(shippingParts) !== addressSignature(billingParts)
    ? shippingParts.join(', ')
    : '';
};
const customerPhoneLine = (customer) => String(customer?.phone || customer?.altphone || '').trim();
const customerContactLines = (customer) => {
  const fields = [
    ['Contact', customer?.contact],
    ['Alt Contact', customer?.altcontact || customer?.alt_contact],
    ['Phone', customer?.phone],
    ['Alt Phone', customer?.altphone || customer?.alt_phone],
    ['Email', customer?.email],
  ];
  const seen = new Set();
  return fields.reduce((lines, [label, rawValue]) => {
    const value = String(rawValue || '').trim();
    const key = value.toLowerCase();
    if (!value || seen.has(key)) return lines;
    seen.add(key);
    lines.push({ label, value });
    return lines;
  }, []);
};
const customerInvoiceAddress = (customer) => ({
  name: customerName(customer),
  addr1: customer?.billaddr1 || customer?.bill_addr_1 || customer?.billAddr1 || '',
  addr2: customer?.billaddr2 || customer?.bill_addr_2 || customer?.billAddr2 || '',
  addr3: customer?.billaddr3 || customer?.bill_addr_3 || customer?.billAddr3 || '',
  addr4: customer?.billaddr4 || customer?.bill_addr_4 || customer?.billAddr4 || '',
  city: customer?.billcity || customer?.bill_city || customer?.billCity || '',
  state: customer?.billstate || customer?.bill_state || customer?.billState || '',
  zip: customer?.billzip || customer?.bill_zip || customer?.billZip || '',
  country: customer?.billcountry || customer?.bill_country || customer?.billCountry || '',
  phone: customer?.phone || customer?.altphone || '',
  email: customer?.email || '',
  website: customer?.website || '',
});
const customerListBalance = (summary = {}) => Number(summary.amountOwed || 0) - Number(summary.walletBalance || 0);
const customerSummaryMap = (rows = []) => Object.fromEntries(rows.map((summary) => [String(summary.customerId), summary]));
const isOtherServiceName = (value) => /^other[-\s]/i.test(String(value || '').trim());
const isCreditCardFeeServiceName = (value) => /^other[-\s]*cc\s+fee$/i.test(String(value || '').trim());
const isExplicitBaseServiceName = (value) => (
  isCreditCardFeeServiceName(value)
  || /^other[-\s]*payment\s+discount$/i.test(String(value || '').trim())
);
const getServiceKey = (service = {}) => String(service.serviceId || service.id || service.itemId || service.name || '');
const getDiscountKey = (discount = {}) => String(discount.discountId || discount.id || discount.item || '');
const adjustmentInputValue = (price, priceType) => (
  String(priceType || '').toLowerCase() === 'percent'
    ? `${Number(price || 0)}%`
    : String(Number(price || 0))
);
const serviceAdjustmentInput = (service = {}) => adjustmentInputValue(
  service.defaultPrice ?? service.price ?? 0,
  service.defaultPriceType || service.priceType
);
const discountAdjustmentInput = (discount = {}) => adjustmentInputValue(
  discount.price ?? 0,
  discount.priceType || discount.price_type
);
const parseAdjustmentInput = (value, fallbackPriceType = 'amount') => {
  const raw = String(value ?? '').trim();
  if (!raw) throw new Error('Enter a value for each selected service or discount.');
  const priceType = raw.includes('%') ? 'percent' : String(fallbackPriceType || '').toLowerCase() === 'percent' ? 'percent' : 'amount';
  const numeric = Number(raw.replace(/[$,%\s]/g, ''));
  if (!Number.isFinite(numeric)) throw new Error('Enter a valid service or discount value.');
  return { price: numeric, priceType };
};
const parseOptionalMoneyInput = (value, label = 'Amount') => {
  const raw = String(value ?? '').replace(/[$,\s]/g, '');
  if (!raw) return null;
  const number = Number(raw);
  if (!Number.isFinite(number) || number < 0) throw new Error(`${label} must be zero or greater.`);
  return Math.round(number * 100) / 100;
};
const PAYMENT_CUSTOMER_ROW_HEIGHT = 58;
const PAYMENT_CUSTOMER_VISIBLE_BUFFER = 36;
const PAYMENT_CUSTOMER_OVERSCAN = 6;
const AVAILABLE_PAYMENT_TYPE = 'Available Payments';
const PAYMENT_METHOD_OPTIONS = [['Cash','$'],['Check','\u2713'],['Credit Card','\u25A3'],['Zelle','Z'],['Wire','\u21C4'],['Shopify','S'],[AVAILABLE_PAYMENT_TYPE,'\u25BE']];
const emptyPayment = () => ({ amount: '', paymentType: 'Check', referenceNo: '', notes: '', receivedAt: today() });
// Compare editable values, ignoring numeric formatting and empty allocation entries.
const paymentEditSnapshot = (form, allocations) => JSON.stringify({
  ...form,
  amount: Number(form.amount || 0),
  allocations: Object.entries(allocations)
    .map(([key, amount]) => [key, Number(amount || 0)])
    .filter(([, amount]) => amount !== 0)
    .sort(([left], [right]) => left.localeCompare(right)),
});
const emptyCustomInvoiceItem = () => ({ item: '', description: '', rate: '', amount: '' });
const parseCustomInvoiceAmountInput = (value) => {
  const number = Number(String(value ?? '').replace(/[$,\s]/g, ''));
  return Number.isFinite(number) ? number : NaN;
};
const parseCustomInvoiceRateInput = (value) => {
  const raw = String(value ?? '').trim();
  const number = Number(raw.replace(/[$,%\s]/g, ''));
  return {
    raw,
    priceType: raw.includes('%') ? 'percent' : 'amount',
    value: Number.isFinite(number) ? number : NaN,
  };
};
const customInvoiceLineTotal = (item = {}) => {
  const amount = parseCustomInvoiceAmountInput(item.amount);
  const rate = parseCustomInvoiceRateInput(item.rate);
  if (!Number.isFinite(amount) || amount <= 0 || !Number.isFinite(rate.value) || rate.value <= 0) return 0;
  return Math.round((rate.priceType === 'percent' ? amount * rate.value / 100 : amount) * 100) / 100;
};
const emptyCustomInvoice = (customer = null) => ({
  customer,
  query: customer ? customerName(customer) : '',
  items: [emptyCustomInvoiceItem()],
  documentType: 'invoice',
  error: '',
  loading: false,
});
const hasDisplayTime = (date) => Boolean(date && (date.getHours() || date.getMinutes() || date.getSeconds() || date.getMilliseconds()));
const paymentDateValue = (value) => {
  const localParts = localTimestampParts(value);
  if (localParts) {
    return {
      date: new Date(localParts.year, localParts.month - 1, localParts.day, localParts.hour, localParts.minute, localParts.second),
      parts: hasLocalTimestampTime(localParts) ? null : localParts,
      hasTime: hasLocalTimestampTime(localParts),
      localParts,
    };
  }
  const date = value ? new Date(value) : null;
  const parts = getDatePartsFromString(value);
  if (!date || Number.isNaN(date.getTime())) {
    return parts ? { date: new Date(parts.year, parts.month - 1, parts.day), parts, hasTime: false } : { date: null, parts: null, hasTime: false };
  }
  return {
    date,
    parts,
    hasTime: !parts && hasDisplayTime(date),
  };
};
const paymentDateDisplay = (payment) => {
  const received = paymentDateValue(payment.receivedAt);
  if (!received.date) return { primary: '-', secondary: '' };
  const createdLocal = localTimestampParts(payment.createdAt);
  const created = createdLocal
    ? new Date(createdLocal.year, createdLocal.month - 1, createdLocal.day, createdLocal.hour, createdLocal.minute, createdLocal.second)
    : payment.createdAt ? new Date(payment.createdAt) : null;
  const createdIsReal = created && !Number.isNaN(created.getTime()) && hasDisplayTime(created);
  const receivedTime = received.hasTime ? received.date.toLocaleTimeString() : '';
  return {
    primary: formatDateParts(received.parts) || received.date.toLocaleDateString(),
    secondary: receivedTime || (!payment.imported && createdIsReal ? created.toLocaleTimeString() : ''),
  };
};
const tableDateDisplay = (value, options = undefined) => {
  const localFormatted = formatLocalTimestamp(value);
  if (localFormatted) {
    const [primary, ...secondaryParts] = localFormatted.split(', ');
    return { primary, secondary: secondaryParts.join(', ') };
  }
  const parsed = paymentDateValue(value);
  if (!parsed.date) return { primary: '-', secondary: '' };
  return {
    primary: formatDateParts(parsed.parts) || parsed.date.toLocaleDateString('en-US', options),
    secondary: parsed.hasTime ? parsed.date.toLocaleTimeString('en-US', options) : '',
  };
};
const paymentDisplayType = (payment) => payment?.displayType || (payment?.status === 'voided' ? 'Voided Payment' : isCreditMemoPayment(payment) ? 'Credit Memo' : 'Payment');
const paymentReferenceDisplay = (payment) => {
  const reference = String(payment?.referenceNo || '').trim();
  return reference ? `#${reference}` : '-';
};
const canApplyAvailablePayment = (payment) => Number(payment?.availableAmount || 0) > 0.001 && Number.isInteger(Number(payment?.id));
const invoiceDeleteNumber = (invoice, document = null) => String(invoice?.docNo || invoice?.invoiceNo || document?.invoiceNo || invoice?.id || '').trim();
const canDeleteShipDocInvoice = (invoice, document = null) => (
  invoiceDeleteNumber(invoice, document)
  && !invoice?.customInvoice
  && !invoice?.importedOnly
  && Number(invoice?.transactionTypeId) === 4
);
const transactionDateDisplay = (value, options = undefined) => {
  const localFormatted = formatLocalTimestamp(value);
  if (localFormatted) return localFormatted;
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '-';
  const parts = getDatePartsFromString(value);
  if (parts) return formatDateParts(parts);
  return hasDisplayTime(date) ? formatDateTimeConsistent(value, options) : formatCalendarDate(value);
};
const paymentCreditDate = (payment) => {
  const received = paymentDateValue(payment?.receivedAt);
  return received.date ? (received.hasTime ? received.date.toLocaleDateString() : formatDateParts(received.parts) || received.date.toLocaleDateString()) : '-';
};
const historyTimestampDisplay = (value) => formatLocalTimestamp(value, { includeDateOnlyTime: true }) || formatDateTimeConsistent(value) || '-';
const quickBooksImportLabel = (payment) => {
  const externalSource = String(payment?.externalSource || '').trim();
  if (externalSource && externalSource !== 'QuickBooks') return externalSource;
  if (!payment.sourceAsOf) return 'QuickBooks import';
  const sourceDate = new Date(`${payment.sourceAsOf}T00:00:00`);
  return Number.isNaN(sourceDate.getTime()) ? 'QuickBooks import' : `QuickBooks as of ${sourceDate.toLocaleDateString()}`;
};
const localDateInput = (date) => {
  const year=date.getFullYear();
  const month=String(date.getMonth()+1).padStart(2,'0');
  const day=String(date.getDate()).padStart(2,'0');
  return `${year}-${month}-${day}`;
};
const today = () => localDateInput(new Date());
const TIME_ZONE_PATTERN = /(?:Z|[+-]\d{2}:?\d{2})$/i;
const LOCAL_TIMESTAMP_PATTERN = /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?$/;
const localTimestampParts = (value) => {
  if (!value) return null;
  const text = String(value).trim();
  if (!text || TIME_ZONE_PATTERN.test(text)) return null;
  const match = text.match(LOCAL_TIMESTAMP_PATTERN);
  if (!match) return null;
  const [, year, month, day, hour, minute, second = '00'] = match;
  return {
    year: Number(year),
    month: Number(month),
    day: Number(day),
    hour: Number(hour),
    minute: Number(minute),
    second: Number(second),
  };
};
const hasLocalTimestampTime = (parts) => Boolean(parts && (parts.hour || parts.minute || parts.second));
const formatLocalTimestamp = (value, { includeDateOnlyTime = false } = {}) => {
  const parts = localTimestampParts(value);
  if (!parts) return '';
  const dateText = `${parts.month}/${parts.day}/${parts.year}`;
  if (!includeDateOnlyTime && !hasLocalTimestampTime(parts)) return dateText;
  const hour12 = ((parts.hour + 11) % 12) + 1;
  const suffix = parts.hour >= 12 ? 'PM' : 'AM';
  return `${dateText}, ${hour12}:${String(parts.minute).padStart(2, '0')}:${String(parts.second).padStart(2, '0')} ${suffix}`;
};
const localDateKey = (value) => {
  if (!value) return '';
  return formatDateInputValue(value);
};
const transactionPresetRange = (preset) => {
  const now=new Date();
  const start=new Date(now.getFullYear(),now.getMonth(),now.getDate());
  const end=new Date(start);
  if (preset==='All') return {start:'',end:''};
  if (preset==='Today') return {start:localDateInput(start),end:localDateInput(end)};
  if (preset==='Yesterday') { start.setDate(start.getDate()-1); end.setDate(end.getDate()-1); }
  if (preset==='This Week') { start.setDate(start.getDate()-start.getDay()); end.setDate(start.getDate()+6); }
  if (preset==='Last Week') { start.setDate(start.getDate()-start.getDay()-7); end.setTime(start.getTime()); end.setDate(end.getDate()+6); }
  if (preset==='This Month') { start.setDate(1); end.setMonth(end.getMonth()+1,0); }
  if (preset==='Last Month') { start.setMonth(start.getMonth()-1,1); end.setDate(0); }
  if (preset==='This Fiscal Year') { start.setMonth(0,1); end.setMonth(11,31); }
  if (preset==='Last Fiscal Year') { start.setFullYear(start.getFullYear()-1,0,1); end.setFullYear(end.getFullYear()-1,11,31); }
  return {start:localDateInput(start),end:localDateInput(end)};
};
const DEFAULT_TRANSACTION_DATE_PRESET = 'This Month';
const TRANSACTION_DATE_PRESETS = ['Today','This Week','This Month','This Fiscal Year','Yesterday','Last Week','Last Month','Last Fiscal Year','All'];
const DEFAULT_TRANSACTION_DATE_RANGE = transactionPresetRange(DEFAULT_TRANSACTION_DATE_PRESET);
const INVOICE_LEVEL_ALLOCATION_KEY = '__invoice__';
const DEFAULT_TRANSACTION_PAGE_SIZE = 250;
const PRINT_TRANSACTION_PAGE_SIZE = 500;
const MAX_PRINT_TRANSACTION_ROWS = 25000;
const emptyTransactionResult = () => ({ rows: [], page: 1, pageSize: DEFAULT_TRANSACTION_PAGE_SIZE, total: 0, totalAmount: 0, totalUnappliedAmount: 0 });
const normalizeTransactionResult = (data) => {
  if (Array.isArray(data)) return { ...emptyTransactionResult(), rows: data, total: data.length };
  return {
    ...emptyTransactionResult(),
    ...(data || {}),
    rows: data?.rows || [],
    total: Number(data?.total || 0),
    totalAmount: Number(data?.totalAmount || 0),
    totalUnappliedAmount: Number(data?.totalUnappliedAmount || 0),
  };
};
const transactionParams = (range = {}, transactionType = '', options = {}) => {
  const params = {};
  if (range.start) params.start = range.start;
  if (range.end) params.end = range.end;
  if (transactionType) params.transactionType = transactionType;
  if (options.paymentType) params.paymentType = options.paymentType;
  if (options.status) params.status = options.status;
  if (options.page) params.page = options.page;
  if (options.pageSize) params.pageSize = options.pageSize;
  if (options.sortKey) params.sortKey = options.sortKey;
  if (options.sortDirection) params.sortDirection = options.sortDirection;
  if (options.exportAll) params.exportAll = 'true';
  return params;
};
const fetchPaymentTransactions = async (range = {}, transactionType = '', options = {}) => {
  const { data } = await api.get('/payments/transactions', { params: transactionParams(range, transactionType, options) });
  return normalizeTransactionResult(data);
};
const FilterIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="filter-icon">
    <path d="M4 5h16l-6.5 7.5V18l-3 1v-6.5L4 5z" />
  </svg>
);
const ClearIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="clear-icon">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);
const INVOICE_STATUS_OPTIONS = [['pending', 'Pending'], ['paid', 'Paid']];
const INVOICE_TYPE_OPTIONS = [['sale','Shipping'],['repair','Repair Order'],['custom','Custom Invoice']];

const customInvoicePrintItemName = (item = {}) => String(item.item || item.referenceNo || (item.serial ? item.description : '') || '').trim();
const customInvoicePrintDescription = (item = {}) => {
  const explicit = String(item.lineDescription || '').trim();
  if (explicit) return explicit;
  const serialDescription = String(item.serial || '').trim();
  if (serialDescription) return serialDescription;
  const combined = String(item.description || '').trim();
  const itemName = customInvoicePrintItemName(item);
  if (itemName && combined.toLowerCase().startsWith(`${itemName.toLowerCase()} - `)) {
    return combined.slice(itemName.length + 3).trim();
  }
  if (/^\s*(Sales Tax|Other-CC|Discount)\b/i.test(combined)) return '';
  return combined;
};

const customInvoiceDetailToDocument = ({ invoice, detail, customer, businessAddress }) => {
  if (!invoice?.customInvoice || !detail) return null;
  const items = (detail.items || [])
    .filter((item) => item.lineKey !== 'custom-invoice-tax')
    .map((item, index) => ({
    id: item.id || item.lineKey || `custom-invoice-item-${index}`,
    item: customInvoicePrintItemName(item) || 'Custom invoice item',
    serial: customInvoicePrintDescription(item),
    description: customInvoicePrintItemName(item) || 'Custom invoice item',
    quantity: 1,
    price: Number(item.lineTotal || item.price || 0),
    invoiceLineType: item.invoiceLineType || '',
    serviceId: item.serviceId || item.service_id || null,
    discountId: item.discountId || item.discount_id || null,
    servicePrice: item.servicePrice,
    servicePriceType: item.servicePriceType,
    serviceBaseAmount: item.serviceBaseAmount ?? item.baseAmount ?? null,
    lineDescription: item.invoiceLineType === 'other_service' ? item.description || '' : '',
  }));
  const otherServices = items.filter((item) => item.invoiceLineType === 'other_service').map((item) => ({
    serviceId: item.serviceId,
    discountId: item.discountId,
    name: item.description,
    description: item.lineDescription,
    price: item.servicePrice,
    priceType: item.servicePriceType,
    baseAmount: item.serviceBaseAmount,
    calculatedAmount: item.price,
  }));
  const subtotalAmount = items
    .filter((item) => item.invoiceLineType !== 'other_service')
    .reduce((sum, item) => sum + Number(item.price || 0), 0);
  const taxAmount = Number(detail.taxAmount || 0);
  const totalAmount = Number(detail.originalAmount || invoice.originalAmount || subtotalAmount + taxAmount);
  const to = {
    name: customerName(customer),
    addr1: customer?.bill_addr_1 || customer?.billAddr1 || customer?.address || '',
    addr2: customer?.bill_addr_2 || customer?.billAddr2 || '',
    addr3: customer?.bill_addr_3 || customer?.billAddr3 || customer?.phone || customer?.altphone || '',
    city: customer?.bill_city || customer?.billCity || '',
    state: customer?.bill_state || customer?.billState || '',
    zip: customer?.bill_zip || customer?.billZip || '',
    country: customer?.bill_country || customer?.billCountry || '',
    phone: customer?.phone || customer?.altphone || '',
    email: customer?.email || '',
  };
  return {
    id: invoice.id,
    invoiceNo: invoice.invoiceNo || invoice.docNo || invoice.id,
    date: invoice.date || detail.date,
    type: 'Custom Invoice',
    documentTitle: 'Custom Invoice',
    transactionTypeId: 4,
    customInvoice: true,
    from: businessAddress,
    businessAddress,
    to,
    billTo: to,
    clientName: to.name,
    customerName: to.name,
    memo: detail.memo || '',
    comment: detail.memo || '',
    items,
    otherServices,
    totalQuantity: items.length,
    subtotalAmount,
    totalAmount,
    taxAmount,
    payments: Number(detail.paidAmount || invoice.appliedAmount || 0),
    credits: 0,
    balanceDue: Number(detail.amountDue ?? invoice.amountDue ?? Math.max(0, totalAmount - Number(detail.paidAmount || 0))),
    paidDate: detail.paidDate || invoice.paidDate || null,
  };
};

const importedInvoiceDetailToDocument = ({ invoice, detail, customer, businessAddress }) => {
  const documentNo = invoice?.invoiceNo || invoice?.docNo || invoice?.id || detail?.invoiceNo || detail?.documentId || '';
  const isCreditMemo = Number(invoice?.transactionTypeId) === 10
    || detail?.documentType === 'credit_memo'
    || /^credit memo$/i.test(String(invoice?.type || ''));
  const items = (detail?.items || []).map((item, index) => {
    const quantity = Math.abs(Number(item.quantity) || 0) || 1;
    const price = Number(item.price ?? item.lineTotal ?? 0) || 0;
    return {
      id: item.id || item.allocationKey || `imported-invoice-item-${index}`,
      item: item.item || item.referenceNo || item.serialNo || item.serial || '',
      description: item.item || item.referenceNo || item.serialNo || item.serial || '',
      lineDescription: item.lineDescription || item.description || invoice?.type || 'Imported invoice balance',
      serial: item.serialNo || item.serial || '',
      quantity,
      costPer: price,
      actualPrice: price,
      actualTotal: quantity * price,
    };
  });
  const totalAmount = Number(invoice?.originalAmount ?? detail?.originalAmount ?? invoice?.totalAmount ?? items.reduce((sum, item) => sum + item.actualTotal, 0));
  const to = {
    name: customerName(customer),
    addr1: customer?.bill_addr_1 || customer?.billaddr1 || customer?.address || '',
    addr2: customer?.bill_addr_2 || customer?.billaddr2 || '',
    addr3: customer?.bill_addr_3 || customer?.billaddr3 || '',
    addr4: customer?.bill_addr_4 || customer?.billaddr4 || '',
    city: customer?.bill_city || customer?.billcity || '',
    state: customer?.bill_state || customer?.billState || '',
    zip: customer?.bill_zip || customer?.billZip || '',
    country: customer?.bill_country || customer?.billCountry || '',
    phone: customer?.phone || customer?.altphone || '',
    email: customer?.email || '',
  };
  return {
    id: documentNo,
    invoiceNo: documentNo,
    docNo: documentNo,
    date: detail?.date || invoice?.date,
    type: isCreditMemo ? 'Credit Memo' : invoice?.type || 'Invoice',
    documentTitle: isCreditMemo ? 'Credit Memo' : invoice?.customInvoice ? 'Custom Invoice' : 'Invoice',
    transactionTypeId: isCreditMemo ? 10 : invoice?.transactionTypeId || 4,
    from: businessAddress,
    businessAddress,
    to,
    billTo: to,
    clientName: to.name,
    customerName: to.name,
    items,
    totalQuantity: items.length,
    totalAmount,
    taxAmount: Number(detail?.taxAmount || 0),
    memo: detail?.memo || '',
    comment: detail?.memo || '',
    payments: Number(detail?.paidAmount ?? invoice?.appliedAmount ?? 0),
    credits: 0,
    balanceDue: Number(detail?.amountDue ?? invoice?.amountDue ?? Math.max(0, totalAmount - Number(detail?.paidAmount || 0))),
    paidDate: detail?.paidDate || invoice?.paidDate || null,
  };
};

const isShippingInvoiceTaxLine = (item = {}) => String(item.lineKey || item.id || '') === 'shipping-invoice-tax';
const isInvoiceOtherServiceLine = (item = {}) => (
  item.invoiceLineType === 'other_service'
  || /^other[-\s]/i.test(String(item.description || item.item || '').trim())
);
const invoiceServiceMatchKey = (item = {}) => (
  String(item.serviceId || item.service_id || item.description || item.item || item.referenceNo || '').trim().toLowerCase()
);
const invoiceDiscountTotal = (document) => {
  const adjustmentRows = (document?.otherServices || []).length ? document.otherServices : (document?.items || []);
  return adjustmentRows
    .filter((item) => item.discountId || item.discount_id)
    .reduce((sum, item) => sum + Math.abs(Number(item.calculatedAmount ?? item.price ?? item.actualTotal ?? item.lineTotal ?? 0) || 0), 0);
};

const invoiceTaxSubtotal = (document) => {
  const discountTotal = invoiceDiscountTotal(document);
  const explicitSubtotal = Number(document?.subtotalAmount ?? document?.subtotal);
  if (Number.isFinite(explicitSubtotal) && explicitSubtotal >= 0) return Math.max(0, explicitSubtotal - discountTotal);
  const items = (document?.items || []).filter((item) => (
    !isShippingInvoiceTaxLine(item)
    && !isInvoiceOtherServiceLine(item)
  ));
  const lineSubtotal = items.reduce((sum, item) => {
    if (Number.isFinite(Number(item.actualTotal))) return sum + Math.abs(Number(item.actualTotal));
    const quantity = Math.abs(Number(item.quantity) || 0) || 1;
    const price = Number(item.price ?? item.costPer ?? item.actualPrice ?? item.lineTotal ?? 0) || 0;
    return sum + (quantity * price);
  }, 0);
  if (lineSubtotal > 0) return Math.max(0, lineSubtotal - discountTotal);
  return Math.max(0, Number(document?.totalAmount ?? document?.total ?? document?.originalAmount ?? 0) - Number(document?.taxAmount || 0) - discountTotal);
};

const taxAmountForRate = (document, rate) => Math.round(invoiceTaxSubtotal(document) * Number(rate || 0)) / 100;

const paymentLineSerial = (item = {}) => item.referenceNo || item.serialNo || item.serial || '';

const invoiceDocumentBalanceDue = (document, paidAmount) => {
  const totalAmount = Number(document?.totalAmount ?? document?.total);
  const appliedAmount = Number(paidAmount ?? document?.payments ?? 0) + Number(document?.credits ?? 0);
  if (Number.isFinite(totalAmount)) {
    return Math.max(0, Math.round((totalAmount - appliedAmount) * 100) / 100);
  }
  const balanceDue = Number(document?.balanceDue);
  return Number.isFinite(balanceDue) ? balanceDue : 0;
};

const mergeImportedDetailIntoInvoiceDocument = (document, detail) => {
  if (!document || !detail) return document;
  const detailItems = detail.items || [];
  const hasImportedAmounts = detailItems.some((item) => Number(item.lineTotal ?? item.price ?? 0) > 0)
    || Number(detail.taxAmount || 0) > 0;
  if (!hasImportedAmounts) return document;
  const nonTaxDetailItems = detailItems.filter((item) => !isShippingInvoiceTaxLine(item));
  const serviceDetailItems = nonTaxDetailItems.filter(isInvoiceOtherServiceLine);
  const regularDetailItems = nonTaxDetailItems.filter((item) => !isInvoiceOtherServiceLine(item));
  let regularIndex = 0;
  const mergedItems = (document.items || []).map((item) => {
    const detailItem = isInvoiceOtherServiceLine(item)
      ? serviceDetailItems.find((candidate) => (
        String(candidate.serviceId || candidate.service_id || '')
        && String(item.serviceId || item.service_id || '')
        && String(candidate.serviceId || candidate.service_id || '') === String(item.serviceId || item.service_id || '')
      )) || serviceDetailItems.find((candidate) => invoiceServiceMatchKey(candidate) === invoiceServiceMatchKey(item))
      : regularDetailItems[regularIndex++];
    const detailAmount = Number(detailItem?.lineTotal ?? detailItem?.price ?? 0);
    if (!detailItem || detailAmount <= 0) return item;
    const quantity = Math.abs(Number(item.quantity) || 0) || 1;
    const unitAmount = detailAmount / quantity;
    return {
      ...item,
      serial: item.serial || detailItem.serialNo || detailItem.serial || detailItem.referenceNo || '',
      item: item.item || detailItem.referenceNo || detailItem.serialNo || detailItem.serial || '',
      price: unitAmount,
      costPer: unitAmount,
      actualPrice: unitAmount,
      actualTotal: detailAmount,
      lineTotal: detailAmount,
      totalCost: detailAmount,
    };
  });
  const totalAmount = Number(document.totalAmount ?? document.total ?? detail.originalAmount ?? 0);
  const paidAmount = Number(detail.paidAmount ?? document.payments ?? 0);
  return {
    ...document,
    items: mergedItems,
    total: totalAmount,
    totalAmount,
    taxAmount: Number(detail.taxAmount || document.taxAmount || 0),
    payments: paidAmount,
    credits: Number(document.credits ?? 0),
    balanceDue: invoiceDocumentBalanceDue({ ...document, totalAmount }, paidAmount),
    paidDate: detail.paidDate || document.paidDate || null,
  };
};

const printShipDocInvoiceOutput = (source, title = 'Invoice') => {
  if (!source) return;

  const printNode = source.cloneNode(true);
  printNode.querySelectorAll('.invoice-modal-header, .invoice-modal-actions, .invoice-modal-bottom-actions').forEach((node) => node.remove());

  const iframe = document.createElement('iframe');
  iframe.title = `${title} print preview`;
  iframe.style.position = 'fixed';
  iframe.style.left = '-10000px';
  iframe.style.top = '0';
  iframe.style.width = '816px';
  iframe.style.height = '1056px';
  iframe.style.border = '0';
  document.body.appendChild(iframe);

  const cleanup = () => {
    setTimeout(() => iframe.remove(), 250);
  };
  const runPrint = () => {
    iframe.contentWindow.focus();
    iframe.contentWindow.addEventListener('afterprint', cleanup, { once: true });
    iframe.contentWindow.print();
    setTimeout(cleanup, 1000);
  };

  const doc = iframe.contentDocument || iframe.contentWindow.document;
  doc.open();
  doc.write(`
    <!doctype html>
    <html>
      <head>
        <title>${title}</title>
        <style>
          @page { size: portrait; margin: 12mm; }
          * { box-sizing: border-box; }
          html, body { background: #fff; color: #000; margin: 0; padding: 0; }
          .shipdoc-invoice-workspace { background: #fff; padding: 0; }
          .shipdoc-invoice-sheet { background: #fff; box-shadow: none; color: #000; font-family: "Times New Roman", Times, serif; margin: 0; min-height: auto; padding: 50px 28px 40px; position: relative; width: 100%; }
          .shipdoc-paid-watermark { align-items: center; color: rgba(0, 0, 0, 0.72); display: flex; flex-direction: column; font-family: "Courier New", Courier, monospace; font-weight: 900; left: 50%; letter-spacing: 0; line-height: 0.92; pointer-events: none; position: absolute; text-align: center; text-transform: uppercase; top: 178px; transform: translateX(-50%) rotate(-18deg); z-index: 2; }
          .shipdoc-paid-watermark strong { border: 5px double currentColor; font-size: 44px; line-height: 0.9; padding: 3px 10px 0; }
          .shipdoc-paid-watermark span { font-size: 23px; margin-top: 2px; }
          .shipdoc-invoice-top { align-items: start; display: grid; grid-template-columns: 1fr 230px; margin: 0 auto 28px; width: 78%; }
          .shipdoc-company { font-size: 16px; line-height: 1.12; padding-top: 4px; }
          .shipdoc-title-block h1 { font-family: Arial, Helvetica, sans-serif; font-size: 30px; font-weight: 700; letter-spacing: 0; margin: 0 0 8px; text-align: right; }
          .shipdoc-invoice-meta { border-collapse: collapse; font-family: Arial, Helvetica, sans-serif; font-size: 12px; margin-left: auto; table-layout: fixed; width: 190px; }
          .shipdoc-invoice-meta th, .shipdoc-invoice-meta td { border: 2px solid #000; height: 30px; padding: 5px 7px; text-align: center; }
          .shipdoc-invoice-meta th { background: #fff; font-weight: 400; }
          .shipdoc-bill-to-box { border: 2px solid #000; margin: 0 0 34px 34px; min-height: 145px; width: 360px; }
          .shipdoc-bill-to-box h2 { border-bottom: 2px solid #000; font-family: Arial, Helvetica, sans-serif; font-size: 12px; font-weight: 400; margin: 0; padding: 8px 16px; }
          .shipdoc-bill-to { font-size: 12px; line-height: 1.15; min-height: 0; padding: 5px 6px; }
          .shipdoc-lines-area { margin-top: 8px; position: relative; }
          .shipdoc-detail-strip { border-collapse: collapse; font-family: Arial, Helvetica, sans-serif; font-size: 12px; margin-left: auto; margin-right: 0; table-layout: fixed; width: 48.5%; }
          .shipdoc-detail-strip th, .shipdoc-detail-strip td { border: 2px solid #000; height: 30px; padding: 6px; text-align: center; }
          .shipdoc-detail-strip th { background: #fff; font-weight: 400; }
          .shipdoc-invoice-items { border-collapse: collapse; font-size: 12px; margin-top: -2px; table-layout: fixed; width: 100%; }
          .shipdoc-invoice-items th { background: #fff; border: 2px solid #000; font-family: Arial, Helvetica, sans-serif; font-weight: 400; height: 32px; padding: 6px; text-align: center; }
          .shipdoc-invoice-items td { border-left: 2px solid #000; border-right: 2px solid #000; line-height: 1.12; padding: 2px 6px; text-align: left; vertical-align: top; }
          .shipdoc-invoice-items tbody tr:first-child td { padding-top: 5px; }
          .shipdoc-invoice-items tbody tr:last-child td { padding-bottom: 260px; }
          .shipdoc-invoice-items th:nth-child(1), .shipdoc-invoice-items td:nth-child(1) { width: 30%; }
          .shipdoc-invoice-items th:nth-child(2), .shipdoc-invoice-items td:nth-child(2) { width: 35%; }
          .shipdoc-invoice-items th:nth-child(3), .shipdoc-invoice-items td:nth-child(3) { text-align: right; width: 19%; }
          .shipdoc-invoice-items th:nth-child(4), .shipdoc-invoice-items td:nth-child(4) { text-align: right; width: 16%; }
          .shipdoc-serial-list { display: block; overflow-wrap: anywhere; word-break: break-word; white-space: normal; }
          .shipdoc-serial-value { display: inline; }
          .shipdoc-serial-value:not(:last-child)::after { content: ","; }
          .shipdoc-highlight-row td { background: #fff3bf; }
          .shipdoc-summary-row { display: grid; grid-template-columns: 1fr 35%; margin-top: -2px; }
          .shipdoc-summary-spacer { border: 2px solid #000; border-right: 0; min-height: 38px; }
          .shipdoc-summary { border-bottom: 2px solid #000; border-right: 2px solid #000; box-sizing: border-box; margin: 0; min-width: 0; width: auto; }
          .shipdoc-summary div { align-items: center; border-left: 2px solid #000; border-top: 2px solid #000; box-sizing: border-box; display: grid; grid-template-columns: minmax(0, 1fr) 104px; min-height: 38px; overflow: hidden; }
          .shipdoc-summary span { font-family: Arial, Helvetica, sans-serif; font-size: 18px; font-weight: 700; min-width: 0; padding-left: 16px; white-space: nowrap; }
          .shipdoc-summary strong { display: block; font-size: 12px; font-weight: 400; min-width: 0; padding-right: 10px; text-align: right; white-space: nowrap; }
          .shipdoc-summary div:last-child span { font-size: 21px; }
        </style>
      </head>
      <body>${printNode.outerHTML}</body>
    </html>
  `);
  doc.close();
  setTimeout(runPrint, 100);
};

export default function PaymentsPage() {
  const [searchParams] = useSearchParams();
  const linkedCustomerId = searchParams.get('customerId');
  const crmNavigationKey = useLocation().key;
  const openedLinkedCustomerId = useRef(null);
  const businessAddress = useBusinessAddress();
  const { user } = useAuth();
  const canCreatePayment = hasPermission(user, 'payments.create');
  const canEditPayment = can(user, 'payments.edit');
  const canVoidPayment = can(user, 'payments.void');
  const canDeleteInvoice = can(user, 'invoices.delete');
  const canCreateReturn = can(user, 'returns.create');
  const [customers, setCustomers] = useState([]);
  const [customerTypes, setCustomerTypes] = useState([]);
  const [selectedCustomerTypeIds, setSelectedCustomerTypeIds] = useState([]);
  const [typeFilterOpen, setTypeFilterOpen] = useState(false);
  const [customerListSort, setCustomerListSort] = useState({key:'name',direction:'asc'});
  const [leftTab, setLeftTab] = useState('customers');
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionsError, setTransactionsError] = useState('');
  const [transactionPageInfo, setTransactionPageInfo] = useState(emptyTransactionResult());
  const [selectedTransactionType, setSelectedTransactionType] = useState('Received Payment');
  const [transactionPaymentType, setTransactionPaymentType] = useState('All');
  const [transactionInvoiceStatusFilter, setTransactionInvoiceStatusFilter] = useState('Pending');
  const [transactionDatePreset, setTransactionDatePreset] = useState(DEFAULT_TRANSACTION_DATE_PRESET);
  const [transactionDateRange, setTransactionDateRange] = useState(DEFAULT_TRANSACTION_DATE_RANGE);
  const [transactionSort, setTransactionSort] = useState({ key: 'date', direction: 'desc' });
  const [selectedTransactionCustomers, setSelectedTransactionCustomers] = useState(null);
  const [transactionCustomerFilterSearch, setTransactionCustomerFilterSearch] = useState('');
  const [transactionCustomerFilterOpen, setTransactionCustomerFilterOpen] = useState(false);
  const [customerSummaries, setCustomerSummaries] = useState({});
  const [customerListScrollTop, setCustomerListScrollTop] = useState(0);
  const [query, setQuery] = useState('');
  const [lookupOpen, setLookupOpen] = useState(false);
  const [activeLookupIndex, setActiveLookupIndex] = useState(0);
  const [selected, setSelected] = useState(null);
  const canReceiveCustomerPayment = Boolean(selected && can(user, 'payments.create', paymentPermissionResource(selected, customerTypes)));
  const canApplyPaymentToInvoice = useCallback((invoice) => {
    const invoiceCustomerId = invoice?.customerId ?? invoice?.customer_id;
    const customer = invoiceCustomerId && String(invoiceCustomerId) !== String(customerId(selected))
      ? customers.find((row) => String(customerId(row)) === String(invoiceCustomerId)) : selected;
    return can(user, 'payments.create', paymentPermissionResource(customer, customerTypes, invoice));
  }, [user, selected, customerTypes, customers]);
  const selectedCustomerTypeName = customerTypeLabel(selected, customerTypes);
  const invoiceCreatePermissionResource = () => ({
    ...selected,
    customerId: customerId(selected),
    customerTypeName: selected?.customerTypeName || selected?.customer_type_name || selectedCustomerTypeName,
  });
  const invoiceEditPermissionResource = (invoice, document) => ({
    ...invoiceCreatePermissionResource(),
    documentType: document?.documentType || document?.document_type || invoice?.documentType || invoice?.document_type || invoice?.type,
    transactionTypeId: invoice?.transactionTypeId ?? invoice?.transaction_type_id ?? document?.transactionTypeId ?? document?.transaction_type_id,
    documentId: invoice?.docNo || invoice?.documentId || invoice?.document_id || document?.invoiceNo || document?.id || invoice?.id,
    id: invoice?.id || document?.id,
  });
  const canCreateInvoice = can(user, 'invoices.create', invoiceCreatePermissionResource());
  const canEditPaymentInvoice = (invoice, document) => can(user, 'invoices.edit', invoiceEditPermissionResource(invoice, document));
  const [tab, setTab] = useState('invoices');
  const canViewCustomerCrm = can(user, 'payments.crm', paymentPermissionResource(selected, customerTypes));

  const [payments, setPayments] = useState([]);
  const [paymentDatePreset, setPaymentDatePreset] = useState(DEFAULT_TRANSACTION_DATE_PRESET);
  const [paymentDateRange, setPaymentDateRange] = useState(DEFAULT_TRANSACTION_DATE_RANGE);
  const [paymentTypeFilter, setPaymentTypeFilter] = useState('Payment');
  const [paymentSort, setPaymentSort] = useState({ key: 'date', direction: 'desc' });
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoiceTypes, setSelectedInvoiceTypes] = useState(['sale','repair','custom']);
  const [invoiceTypeFilterOpen, setInvoiceTypeFilterOpen] = useState(false);
  const [selectedInvoiceStatuses, setSelectedInvoiceStatuses] = useState(['pending']);
  const [invoiceStatusFilterOpen, setInvoiceStatusFilterOpen] = useState(false);
  const [invoiceNumberFilter, setInvoiceNumberFilter] = useState('');
  const [invoiceSort, setInvoiceSort] = useState({ key: 'date', direction: 'desc' });
  const [invoiceDatePreset, setInvoiceDatePreset] = useState('All');
  const [invoiceDateRange, setInvoiceDateRange] = useState({ start: '', end: today() });
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (!loading && tab === 'crm' && !canViewCustomerCrm) setTab('invoices'); }, [tab, canViewCustomerCrm, loading]);
  const [refreshingCustomerTab, setRefreshingCustomerTab] = useState(false);
  const [status, setStatus] = useState('');
  const [modal, setModal] = useState(false);
  const [paymentModalMode, setPaymentModalMode] = useState('full');
  const [paymentModalHighlightedInvoiceId, setPaymentModalHighlightedInvoiceId] = useState(null);
  const [paymentModalSelectedInvoiceIds, setPaymentModalSelectedInvoiceIds] = useState([]);
  const [activePaymentReview, setActivePaymentReview] = useState(null);
  const [savedPaymentSnapshot, setSavedPaymentSnapshot] = useState(null);
  const [form, setForm] = useState(emptyPayment());
  const [details, setDetails] = useState({});
  const [allocations, setAllocations] = useState({});
  const [activity, setActivity] = useState({ payment: null, rows: [] });
  const [invoiceHistory, setInvoiceHistory] = useState({ invoice: null, rows: [], loading: false, error: '' });
  const [applyModal, setApplyModal] = useState({ open: false, invoice: null, detail: null, paymentIds: [], amounts: {}, loading: false, creditMenuOpen: false });
  const [invoicePreviewModal, setInvoicePreviewModal] = useState({ open: false, invoice: null, detail: null, invoiceDocument: null, loading: false, error: '' });
  const [paymentInvoiceEditor, setPaymentInvoiceEditor] = useState({ open: false, docNo: '', draft: null, saving: false, error: '' });
  const [taxEditor, setTaxEditor] = useState({ open: false, source: '', value: '0', saving: false, error: '' });
  const [services, setServices] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [invoiceServiceModal, setInvoiceServiceModal] = useState({ open: false, source: '', activeTab: 'services', serviceIds: [], discountIds: [], serviceValues: {}, serviceBaseAmounts: {}, discountValues: {}, saving: false, error: '' });
  const [preferencesTaxRate, setPreferencesTaxRate] = useState(0);
  const [creditMemoModal, setCreditMemoModal] = useState({ open:false, docNo:'', transfer:null, invoice:null, loading:false, error:'' });
  const [invoiceEmailLoading, setInvoiceEmailLoading] = useState(false);
  const [paymentEmailLoading, setPaymentEmailLoading] = useState(false);
  const [creditMemoEmailLoading, setCreditMemoEmailLoading] = useState(false);
  const [listingEmailLoading, setListingEmailLoading] = useState(false);
  const [listingPreviewLoading, setListingPreviewLoading] = useState(false);
  const [listingPreview, setListingPreview] = useState(null);
  const [moveModal, setMoveModal] = useState({ open:false, payment:null, query:'', target:null, error:'', loading:false });
  const [refundModal, setRefundModal] = useState({ open:false, payment:null, choice:'remaining', error:'', loading:false });
  const [creditApplyModal, setCreditApplyModal] = useState({ open:false, sourcePayment:null, paymentIds:[], paymentAmounts:{}, creditMenuOpen:false, error:'', loading:false, returnTab:null });
  const [customInvoiceModal, setCustomInvoiceModal] = useState({ open:false, ...emptyCustomInvoice() });
  const [deleteInvoiceConfirm, setDeleteInvoiceConfirm] = useState({ open:false, invoice:null, value:'', error:'', loading:false });
  const [deletePaymentConfirm, setDeletePaymentConfirm] = useState({ open:false, payment:null, value:'', error:'', loading:false });
  const [moveLookupOpen, setMoveLookupOpen] = useState(false);
  const [moveLookupIndex, setMoveLookupIndex] = useState(0);
  const [customInvoiceLookupOpen, setCustomInvoiceLookupOpen] = useState(false);
  const [customInvoiceLookupIndex, setCustomInvoiceLookupIndex] = useState(0);
  const [invoiceCustomerLookupOpen, setInvoiceCustomerLookupOpen] = useState(false);
  const [invoiceCustomerLookupIndex, setInvoiceCustomerLookupIndex] = useState(0);
  const lookupMenuRef = useRef(null);
  const customerListRef = useRef(null);
  const creditPickerRef = useRef(null);
  const creditApplyPickerRef = useRef(null);
  const transactionRequestRef = useRef(0);

  useEffect(() => {
    document.title = "Payments - Services Irene's Wigs.com";
  }, []);

  useEffect(() => {
    if (!transactionCustomerFilterOpen && !invoiceStatusFilterOpen && !invoiceTypeFilterOpen) return undefined;
    const dropdowns = document.querySelectorAll('.payments-page .filter-dropdown');
    const closeFilters = () => {
      setTransactionCustomerFilterOpen(false);
      setInvoiceStatusFilterOpen(false);
      setInvoiceTypeFilterOpen(false);
    };
    dropdowns.forEach((dropdown) => dropdown.addEventListener('mouseleave', closeFilters));
    return () => dropdowns.forEach((dropdown) => dropdown.removeEventListener('mouseleave', closeFilters));
  }, [transactionCustomerFilterOpen, invoiceStatusFilterOpen, invoiceTypeFilterOpen]);

  const toggleTransactionCustomerFilter = () => {
    setTransactionCustomerFilterOpen((open) => !open);
    setInvoiceStatusFilterOpen(false);
    setInvoiceTypeFilterOpen(false);
  };

  const toggleInvoiceStatusFilter = () => {
    setInvoiceStatusFilterOpen((open) => !open);
    setTransactionCustomerFilterOpen(false);
    setInvoiceTypeFilterOpen(false);
  };

  const toggleInvoiceTypeFilter = () => {
    setInvoiceTypeFilterOpen((open) => !open);
    setTransactionCustomerFilterOpen(false);
    setInvoiceStatusFilterOpen(false);
  };

  useEffect(() => {
    const hasOpenModal = modal || applyModal.open || invoicePreviewModal.open || taxEditor.open || invoiceServiceModal.open || creditMemoModal.open || moveModal.open || refundModal.open || customInvoiceModal.open || deleteInvoiceConfirm.open || deletePaymentConfirm.open || Boolean(activity.payment) || Boolean(invoiceHistory.invoice) || Boolean(listingPreview);
    document.body.classList.toggle('payments-modal-open', hasOpenModal);
    return () => document.body.classList.remove('payments-modal-open');
  }, [modal, applyModal.open, invoicePreviewModal.open, taxEditor.open, invoiceServiceModal.open, creditMemoModal.open, moveModal.open, refundModal.open, customInvoiceModal.open, deleteInvoiceConfirm.open, deletePaymentConfirm.open, activity.payment, invoiceHistory.invoice, listingPreview]);

  useEffect(() => {
    if (!status) return undefined;
    const timeoutId = window.setTimeout(() => setStatus(''), 3000);
    return () => window.clearTimeout(timeoutId);
  }, [status]);

  useEffect(() => {
    Promise.all([api.get('/customers/types'), api.get('/payments/customers/summary'), api.get('/preferences'), api.get('/services'), api.get('/discounts')])
      .then(([typeResult,summaryResult,preferencesResult,servicesResult,discountsResult]) => {
        const summaries = summaryResult.data || [];
        setCustomers(summaries);
        setCustomerTypes(typeResult.data || []);
        setSelectedCustomerTypeIds((typeResult.data || []).map((type) => String(type.id)));
        setCustomerSummaries(customerSummaryMap(summaries));
        setPreferencesTaxRate(Number(preferencesResult.data?.taxRate || 0));
        setServices(servicesResult.data || []);
        setDiscounts(discountsResult.data || []);
      }).catch(() => setStatus('Could not load customer payment summaries.'));
  }, []);

  useEffect(() => {
    if (!applyModal.creditMenuOpen) return undefined;
    const handleClickOutside = (event) => {
      if (creditPickerRef.current?.contains(event.target)) return;
      setApplyModal((current) => ({...current, creditMenuOpen: false}));
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [applyModal.creditMenuOpen]);

  useEffect(() => {
    if (!creditApplyModal.creditMenuOpen) return undefined;
    const handleClickOutside = (event) => {
      if (creditApplyPickerRef.current?.contains(event.target)) return;
      setCreditApplyModal((current) => ({...current, creditMenuOpen: false}));
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [creditApplyModal.creditMenuOpen]);

  const lookupOptions = useMemo(() => {
    const term = query.trim().toLowerCase();
    const digits = query.replace(/\D/g, '');
    if (!term) return customers.slice(0, 30);
    return customers.filter((customer) => customerName(customer).toLowerCase().includes(term)
      || (digits.length >= 3 && [customer.phone, customer.altphone].some((value) => String(value || '').replace(/\D/g, '').includes(digits)))).slice(0, 30);
  }, [customers, query]);

  const rankedCustomers = useMemo(() => {
    const text = query.trim().toLowerCase();
    const digits = query.replace(/\D/g,'');
    const selectedTypes = new Set(selectedCustomerTypeIds);
    return customers.filter((customer) => {
      const typeId = String(customer?.type ?? customer?.customertypeid ?? customer?.customerTypeId ?? '');
      const matchesType = !customerTypes.length || selectedTypes.has(typeId);
      const matchesText = !text || customerName(customer).toLowerCase().includes(text)
        || (digits.length >= 3 && [customer.phone,customer.altphone].some((value) => String(value || '').replace(/\D/g,'').includes(digits)));
      return matchesType && matchesText;
    }).map((customer) => ({...customer,summary:customerSummaries[String(customerId(customer))] || customer || {amountOwed:0,walletBalance:0}}))
      .sort((a,b) => {
        const comparison=customerListSort.key==='balance'
          ? customerListBalance(a.summary)-customerListBalance(b.summary)
          : customerName(a).localeCompare(customerName(b),undefined,{numeric:true,sensitivity:'base'});
        if (comparison!==0) return customerListSort.direction==='asc'?comparison:-comparison;
        return customerName(a).localeCompare(customerName(b),undefined,{numeric:true,sensitivity:'base'});
      });
  }, [customers,customerSummaries,customerTypes,selectedCustomerTypeIds,query,customerListSort]);
  const customerListStartIndex = Math.max(0, Math.floor(customerListScrollTop / PAYMENT_CUSTOMER_ROW_HEIGHT) - PAYMENT_CUSTOMER_OVERSCAN);
  const visibleCustomers = rankedCustomers.slice(customerListStartIndex, customerListStartIndex + PAYMENT_CUSTOMER_VISIBLE_BUFFER);
  const customerListOffset = customerListStartIndex * PAYMENT_CUSTOMER_ROW_HEIGHT;
  const customerListHeight = rankedCustomers.length * PAYMENT_CUSTOMER_ROW_HEIGHT;
  useEffect(() => {
    setCustomerListScrollTop(0);
    if (customerListRef.current) customerListRef.current.scrollTop = 0;
  }, [query, selectedCustomerTypeIds, customerListSort]);

  const sortCustomerList = (key) => setCustomerListSort((current) => ({
    key,
    direction:current.key===key&&current.direction==='asc'?'desc':'asc',
  }));
  const moveLookupOptions = useMemo(() => {
    const text=moveModal.query.trim().toLowerCase();
    const digits=moveModal.query.replace(/\D/g,'');
    return customers.filter((customer)=>String(customerId(customer))!==String(customerId(selected))
      && (!text || customerName(customer).toLowerCase().includes(text)
        || (digits.length>=3 && [customer.phone,customer.altphone].some((value)=>String(value||'').replace(/\D/g,'').includes(digits))))).slice(0,30);
  },[customers,moveModal.query,selected]);
  const customInvoiceLookupOptions = useMemo(() => {
    const text=customInvoiceModal.query.trim().toLowerCase();
    const digits=customInvoiceModal.query.replace(/\D/g,'');
    return customers.filter((customer)=>!text || customerName(customer).toLowerCase().includes(text)
      || (digits.length>=3 && [customer.phone,customer.altphone].some((value)=>String(value||'').replace(/\D/g,'').includes(digits)))).slice(0,30);
  },[customers,customInvoiceModal.query]);
  const invoiceCustomerLookupOptions = useMemo(() => {
    const text=String(paymentInvoiceEditor.draft?.customerQuery || '').trim().toLowerCase();
    const digits=String(paymentInvoiceEditor.draft?.customerQuery || '').replace(/\D/g,'');
    return customers.filter((customer)=>!text || customerName(customer).toLowerCase().includes(text)
      || (digits.length>=3 && [customer.phone,customer.altphone].some((value)=>String(value||'').replace(/\D/g,'').includes(digits)))).slice(0,30);
  },[customers,paymentInvoiceEditor.draft?.customerQuery]);
  const otherServiceOptions = useMemo(
    () => services.filter((service) => isOtherServiceName(service.name)),
    [services]
  );
  const customInvoiceTotal = useMemo(() => customInvoiceModal.items.reduce((sum, item) => sum + customInvoiceLineTotal(item), 0), [customInvoiceModal.items]);
  const baseFilteredTransactions = useMemo(() => transactions.filter((transaction) => {
    const date=localDateKey(transaction.date);
    return transaction.transactionType === selectedTransactionType
      && (selectedTransactionType==='Invoice'
        ? (transactionInvoiceStatusFilter==='All'||transaction.status===transactionInvoiceStatusFilter)
        : ['Received Payment','Voided Payment','Refund'].includes(selectedTransactionType)
          ? (transactionPaymentType==='All'||transaction.paymentType===transactionPaymentType)
          : true)
      && (!transactionDateRange.start||date>=transactionDateRange.start)
      && (!transactionDateRange.end||date<=transactionDateRange.end);
  }),[transactions,selectedTransactionType,transactionPaymentType,transactionInvoiceStatusFilter,transactionDateRange]);
  const transactionCustomerOptions = useMemo(() => [
    ...new Set(baseFilteredTransactions.map((transaction) => transaction.customerName || '-')),
  ].sort((a,b)=>a.localeCompare(b,undefined,{numeric:true,sensitivity:'base'})), [baseFilteredTransactions]);
  const filteredTransactions = useMemo(() => baseFilteredTransactions.filter((transaction) => (
    !selectedTransactionCustomers || selectedTransactionCustomers.includes(transaction.customerName || '-')
  )), [baseFilteredTransactions, selectedTransactionCustomers]);
  const filteredTransactionTotals = useMemo(() => filteredTransactions.reduce((totals, transaction) => ({
    amount: totals.amount + Number(transaction.amount || 0),
    unappliedAmount: totals.unappliedAmount + Number(transaction.unappliedAmount || 0),
  }), { amount: 0, unappliedAmount: 0 }), [filteredTransactions]);
  const transactionTotalPages = Math.max(1, Math.ceil(Number(transactionPageInfo.total || 0) / Number(transactionPageInfo.pageSize || DEFAULT_TRANSACTION_PAGE_SIZE)));
  const transactionPageStart = transactionPageInfo.total ? ((transactionPageInfo.page - 1) * transactionPageInfo.pageSize) + 1 : 0;
  const transactionPageEnd = Math.min(transactionPageInfo.total, transactionPageInfo.page * transactionPageInfo.pageSize);
  const changeTransactionPage = (page) => {
    const nextPage = Math.min(transactionTotalPages, Math.max(1, page));
    if (nextPage === transactionPageInfo.page || transactionsLoading) return;
    loadTransactions({ page: nextPage });
  };
  const visibleTransactionCustomerOptions = useMemo(() => {
    const search = transactionCustomerFilterSearch.trim().toLowerCase();
    if (!search) return transactionCustomerOptions;
    return transactionCustomerOptions.filter((value)=>String(value || '-').toLowerCase().includes(search));
  }, [transactionCustomerOptions, transactionCustomerFilterSearch]);
  const hasTransactionCustomerSearch = transactionCustomerFilterSearch.trim().length > 0;
  const transactionCustomerFilterSelection = selectedTransactionCustomers || (hasTransactionCustomerSearch ? [] : transactionCustomerOptions);
  const allTransactionCustomersSelected = !hasTransactionCustomerSearch && (!selectedTransactionCustomers || (
    transactionCustomerOptions.length > 0
    && transactionCustomerOptions.every((value)=>transactionCustomerFilterSelection.includes(value))
  ));
  const toggleTransactionCustomerFilterValue = (value) => {
    setSelectedTransactionCustomers((current) => {
      const selected = current || (hasTransactionCustomerSearch ? [] : transactionCustomerOptions);
      return selected.includes(value)
        ? selected.filter((item)=>item!==value)
        : [...selected,value];
    });
  };
  const clearTransactionCustomerFilter = () => {
    setSelectedTransactionCustomers(null);
    setTransactionCustomerFilterSearch('');
  };
  const toggleAllTransactionCustomerFilterOptions = () => {
    setSelectedTransactionCustomers(allTransactionCustomersSelected ? [] : null);
  };
  const sortedTransactions = useMemo(() => [...filteredTransactions].sort((a,b) => {
    const valueFor = (transaction) => {
      if (transactionSort.key === 'date') {
        const time = transaction.date ? new Date(transaction.date).getTime() : 0;
        return Number.isNaN(time) ? 0 : time;
      }
      if (transactionSort.key === 'amount') return Number(transaction.amount || 0);
      return Number(transaction.unappliedAmount || 0);
    };
    const comparison = valueFor(a) - valueFor(b);
    if (comparison !== 0) return transactionSort.direction === 'asc' ? comparison : -comparison;
    return String(a.id || '').localeCompare(String(b.id || ''), undefined, { numeric: true, sensitivity: 'base' });
  }), [filteredTransactions, transactionSort]);
  const sortTransactionsBy = (key) => {
    const nextSort = {
      key,
      direction: transactionSort.key === key && transactionSort.direction === 'asc' ? 'desc' : 'asc',
    };
    setTransactionSort(nextSort);
    loadTransactions({ sort: nextSort, page: 1 });
  };
  const transactionSortArrow = (key) => (
    transactionSort.key === key && transactionSort.direction === 'asc' ? 'asc' : 'desc'
  );
  const transactionPaymentTypes = useMemo(() => ['All',...Array.from(new Set(transactions
    .filter((transaction)=>['Received Payment','Voided Payment','Refund'].includes(transaction.transactionType))
    .map((transaction)=>transaction.paymentType)
    .filter(Boolean))).sort()], [transactions]);
  const loadTransactions = async (overrides = {}) => {
    const range = overrides.range || transactionDateRange;
    const type = overrides.type || selectedTransactionType;
    const sort = overrides.sort || transactionSort;
    const page = overrides.page || transactionPageInfo.page || 1;
    const requestId = transactionRequestRef.current + 1;
    transactionRequestRef.current = requestId;
    setTransactionsLoading(true);
    try {
      let result = await fetchPaymentTransactions(range, type, {
        page,
        pageSize: transactionPageInfo.pageSize || DEFAULT_TRANSACTION_PAGE_SIZE,
        sortKey: sort.key,
        sortDirection: sort.direction,
        paymentType: ['Received Payment','Voided Payment','Refund'].includes(type) ? (overrides.paymentType ?? transactionPaymentType) : '',
        status: type === 'Invoice' ? (overrides.status ?? transactionInvoiceStatusFilter) : '',
      });
      const resultTotalPages = Math.max(1, Math.ceil(Number(result.total || 0) / Number(result.pageSize || DEFAULT_TRANSACTION_PAGE_SIZE)));
      if (page > resultTotalPages && result.total > 0 && !(result.rows || []).length) {
        result = await fetchPaymentTransactions(range, type, {
          page: resultTotalPages,
          pageSize: transactionPageInfo.pageSize || DEFAULT_TRANSACTION_PAGE_SIZE,
          sortKey: sort.key,
          sortDirection: sort.direction,
          paymentType: ['Received Payment','Voided Payment','Refund'].includes(type) ? (overrides.paymentType ?? transactionPaymentType) : '',
          status: type === 'Invoice' ? (overrides.status ?? transactionInvoiceStatusFilter) : '',
        });
      }
      if (transactionRequestRef.current === requestId) {
        setTransactions(result.rows || []);
        setTransactionPageInfo(result);
        setTransactionsError('');
      }
    } catch (error) {
      if (transactionRequestRef.current === requestId) {
        setTransactions([]);
        setTransactionPageInfo({ ...emptyTransactionResult(), page, pageSize: transactionPageInfo.pageSize || DEFAULT_TRANSACTION_PAGE_SIZE });
        setTransactionsError(error.response?.data?.error || 'Could not load transactions.');
      }
    } finally {
      if (transactionRequestRef.current === requestId) setTransactionsLoading(false);
    }
  };
  const changeTransactionDatePreset = async (preset) => {
    const range = transactionPresetRange(preset);
    setTransactionDatePreset(preset);
    setTransactionDateRange(range);
    await loadTransactions({ range, page: 1 });
  };

  const changeTransactionType = async (type) => {
    setSelectedTransactionType(type);
    setTransactionPaymentType('All');
    setTransactionInvoiceStatusFilter('Pending');
    setSelectedTransactionCustomers(null);
    setTransactionCustomerFilterSearch('');
    await loadTransactions({ type, page: 1, paymentType: 'All', status: 'Pending' });
  };
  const openTransactionsTab = () => {
    setLeftTab('transactions');
    if (!transactions.length && !transactionsLoading) loadTransactions({ page: 1 });
  };

  useEffect(() => {
    if (!lookupOpen) return;
    const menu = lookupMenuRef.current;
    const option = menu?.children?.[activeLookupIndex];
    option?.scrollIntoView({ block: 'nearest' });
  }, [activeLookupIndex, lookupOpen]);

  const loadCustomer = useCallback(async (customer, options = {}) => {
    setLookupOpen(false); setActiveLookupIndex(0);
    setSelected(customer); setTab(options.tab ?? tab); setLoading(true); setStatus(''); setActivity({ payment: null, rows: [] });
    try {
      const id = customerId(customer);
      const [customerResult, paymentResult, invoiceResult, summaryResult] = await Promise.all([
        api.get(`/customers/${id}`).catch(error => {
          if (error.response?.status === 403 && options.tab === 'crm') return api.get(`/crm-inbox/customer/${id}`);
          throw error;
        }),
        api.get(`/payments/customer/${id}`),
        api.get(`/payments/customer/${id}/documents`, { params: { includeClosed: true } }),
        api.get('/payments/customers/summary', { params: { customerId: id } }),
      ]);
      const fullCustomer = customerResult.data || customer;
      if (options.filterByCustomerName) setQuery(customerName(fullCustomer));
      const loadedInvoices = invoiceResult.data || [];
      const loadedPayments = paymentResult.data || [];
      const summaryRows = summaryResult.data || [];
      const summary = summaryRows[0] || customerSummaries[String(id)] || customer || {};
      const loadedInvoiceBalance = loadedInvoices.reduce((sum, invoice) => sum + Number(invoice.amountDue || 0), 0);
      const loadedSummary = { ...summary, amountOwed: loadedInvoiceBalance };
      setSelected({...fullCustomer, ...loadedSummary, id: customerId(fullCustomer) || id, nccustomerid: customerId(fullCustomer) || id});
      setCustomers((current) => current.map((item) => String(customerId(item)) === String(id) ? {...item, ...loadedSummary} : item));
      setCustomerSummaries((current) => ({...current, ...customerSummaryMap(summaryRows), [String(id)]: loadedSummary}));
      setPayments(loadedPayments); setInvoices(loadedInvoices);
      return { payments: loadedPayments, invoices: loadedInvoices };
    } catch (error) { setStatus(error.response?.data?.error || 'Could not load customer payments.'); }
    finally { setLoading(false); }
    return null;
  }, [tab, customerSummaries]);

  useEffect(() => {
    const linkKey = `${crmNavigationKey}:${linkedCustomerId}:${searchParams.get('tab') || ''}:${searchParams.get('messageId') || ''}`;
    if (!linkedCustomerId || openedLinkedCustomerId.current === linkKey) return;
    openedLinkedCustomerId.current = linkKey;
    loadCustomer({ nccustomerid: linkedCustomerId }, { filterByCustomerName: true, ...(searchParams.get('tab') === 'crm' ? { tab: 'crm' } : {}) });
  }, [linkedCustomerId, loadCustomer, searchParams, crmNavigationKey]);

  const refreshSelectedCustomerTab = async () => {
    if (!selected) return;
    setRefreshingCustomerTab(true);
    try {
      const loaded = await loadCustomer(selected, { tab });
      if (loaded) setStatus('Data refreshed.');
    } finally {
      setRefreshingCustomerTab(false);
    }
  };

  const handleLookupKeyDown = (event) => {
    if (event.key === 'ArrowDown') { event.preventDefault(); setLookupOpen(true); setActiveLookupIndex((index) => Math.min(index + 1, Math.max(lookupOptions.length - 1, 0))); }
    else if (event.key === 'ArrowUp') { event.preventDefault(); setActiveLookupIndex((index) => Math.max(index - 1, 0)); }
    else if (event.key === 'Escape') { event.preventDefault(); setLookupOpen(false); setActiveLookupIndex(0); }
    else if (event.key === 'Enter' && lookupOptions[activeLookupIndex]) { event.preventDefault(); loadCustomer(lookupOptions[activeLookupIndex]); }
    else if (event.key === 'Tab' && lookupOpen && lookupOptions[activeLookupIndex]) loadCustomer(lookupOptions[activeLookupIndex]);
  };

  const resetCreditApplyState = () => setCreditApplyModal({ open:false, sourcePayment:null, paymentIds:[], paymentAmounts:{}, creditMenuOpen:false, error:'', loading:false, returnTab:null });
  const clearCreditApplyError = () => setCreditApplyModal((current) => current.error ? {...current, error:''} : current);
  const closePaymentModal = () => {
    setModal(false);
    resetCreditApplyState();
    setPaymentModalSelectedInvoiceIds([]);
  };
  const openPayment = () => {
    if (!canReceiveCustomerPayment) {
      setStatus('You do not have permission to receive payments for this customer.');
      return;
    }
    setActivePaymentReview(null); setPaymentModalMode('full'); setPaymentModalHighlightedInvoiceId(null); setPaymentModalSelectedInvoiceIds([]); setForm(emptyPayment()); setAllocations({}); setDetails({}); setStatus(''); resetCreditApplyState(); setModal(true);
  };
  const openPaymentForApplyInvoice = () => {
    if (!canReceiveCustomerPayment) {
      setStatus('You do not have permission to receive payments for this customer.');
      return;
    }
    const invoice = applyModal.invoice;
    if (!invoice) return openPayment();
    if (!canApplyPaymentToInvoice(invoice)) {
      setStatus('You do not have permission to apply payments to this customer or document.');
      return;
    }
    setActivePaymentReview(null);
    setPaymentModalMode('full');
    setPaymentModalHighlightedInvoiceId(invoice.id);
    setPaymentModalSelectedInvoiceIds([String(invoice.id)]);
    setForm(emptyPayment());
    setAllocations({});
    setDetails({});
    setStatus('');
    resetCreditApplyState();
    setApplyModal((current) => ({ ...current, open: false, creditMenuOpen: false }));
    setModal(true);
  };
  const documentDetailParams = () => ({
    ...(activePaymentReview?.id ? { paymentId: activePaymentReview.id } : {}),
    ...(customerId(selected) ? { customerId: customerId(selected) } : {}),
  });
  const loadDocumentDetail = async (invoice) => {
    if (details[invoice.id]) return details[invoice.id];
    const documentType=Number(invoice.transactionTypeId)===640?'repair':'invoice';
    const { data } = await api.get(`/payments/documents/${documentType}/${encodeURIComponent(invoice.id)}`, { params: documentDetailParams() });
    setDetails((old) => ({ ...old, [invoice.id]: data }));
    return data;
  };
  const detailFor = async (invoice) => {
    try { await loadDocumentDetail(invoice); }
    catch { setStatus(`Could not open invoice ${invoice.invoiceNo}.`); }
  };
  const allocationKey = (invoiceId, lineKey = '') => `${invoiceId}|${lineKey}`;
  const isCustomInvoiceAllocationLine = (lineKey) => /^custom-invoice-/i.test(String(lineKey || ''));
  const allocationSerialNo = (lineKey, serialNo) => (
    isCustomInvoiceAllocationLine(lineKey) ? '' : String(serialNo || '')
  );
  const findAllocationLineItem = (documentId, lineKey, serialNo = '') => {
    const normalizedSerialNo = allocationSerialNo(lineKey, serialNo);
    const lookupKey = `${lineKey}|${normalizedSerialNo}`;
    return details[documentId]?.items?.find((row) => row.allocationKey === lookupKey)
      || (!normalizedSerialNo
        ? details[documentId]?.items?.find((row) => String(row.lineKey || '') === String(lineKey))
        : null);
  };
  const allocationLineWarningTarget = (item, lineKey, serialNo = '') => {
    const normalizedSerialNo = allocationSerialNo(lineKey, serialNo);
    if (normalizedSerialNo || item?.referenceNo || item?.serialNo) {
      return `serial ${item?.referenceNo || item?.serialNo || normalizedSerialNo}`;
    }
    return `item ${item?.description || 'this item'}`;
  };
  const invoiceLineAllocationTotal = (values, invoiceId) => Object.entries(values)
    .filter(([key]) => {
      const [keyInvoiceId, lineKey] = String(key).split('|');
      return keyInvoiceId === String(invoiceId) && Boolean(lineKey);
    })
    .reduce((sum, [, value]) => sum + Number(value || 0), 0);
  const invoiceAllocationTotal = (values, invoiceId) => {
    const invoiceKey = allocationKey(invoiceId);
    if (values[invoiceKey] !== undefined) return Number(values[invoiceKey] || 0);
    return invoiceLineAllocationTotal(values, invoiceId);
  };
  const sumAllocations = (values) => Object.entries(values)
    .filter(([key]) => {
      const [invoiceId, lineKey] = String(key).split('|');
      return !lineKey || values[allocationKey(invoiceId)] === undefined;
    })
    .reduce((sum, [, value]) => sum + Number(value || 0), 0);
  const allocationTotal = sumAllocations(allocations);
  const capAllocationsToTotal = (values, total) => {
    const target = Math.max(0, Math.round(Number(total || 0) * 100) / 100);
    if (sumAllocations(values) <= target + 0.001) return values;
    let remaining = target;
    const next = {};
    Object.entries(values).forEach(([key, value]) => {
      if (remaining <= 0.001) return;
      const amount = Number(value || 0);
      if (amount <= 0.001) return;
      const applied = Math.min(amount, remaining);
      next[key] = applied.toFixed(2);
      remaining = Math.round((remaining - applied) * 100) / 100;
    });
    return next;
  };
  const syncCreditApplyAllocationsToTotal = (values, total, previousTotal) => {
    const target = Math.max(0, Math.round(Number(total || 0) * 100) / 100);
    const previous = Math.max(0, Math.round(Number(previousTotal || 0) * 100) / 100);
    const capped = capAllocationsToTotal(values, target);
    const currentTotal = sumAllocations(capped);
    if (target <= currentTotal + 0.001) return capped;
    if (Math.abs(currentTotal - previous) > 0.001) return capped;

    const creditApplyInvoiceIds = paymentModalSelectedInvoiceIds.map(String);
    const allocatedInvoiceIds = [...new Set(Object.entries(capped)
      .filter(([, value]) => Number(value || 0) > 0.001)
      .map(([key]) => String(key).split('|')[0]))];
    const targetInvoiceIds = creditApplyInvoiceIds.length
      ? creditApplyInvoiceIds
      : (paymentModalHighlightedInvoiceId ? [String(paymentModalHighlightedInvoiceId)] : allocatedInvoiceIds);
    if (!targetInvoiceIds.length) return capped;

    let remaining = Math.round((target - currentTotal) * 100) / 100;
    const next = { ...capped };
    paymentModalInvoices
      .filter((row) => targetInvoiceIds.includes(String(row.id)))
      .forEach((invoice) => {
        if (remaining <= 0.001) return;
        const invoiceKey = allocationKey(invoice.id);
        const currentInvoiceTotal = invoiceAllocationTotal(next, invoice.id);
        const lineTotal = invoiceLineAllocationTotal(next, invoice.id);
        const availableAmount = Math.max(0, invoicePaymentLimit(invoice) - currentInvoiceTotal);
        const additionalAmount = Math.min(availableAmount, remaining);
        const invoiceTarget = Math.max(lineTotal, currentInvoiceTotal + additionalAmount);
        if (invoiceTarget > 0.001) next[invoiceKey] = invoiceTarget.toFixed(2);
        remaining = Math.round((remaining - additionalAmount) * 100) / 100;
      });
    return next;
  };
  const distributeInvoiceAmountToSerials = (invoice, amount, detail, existingRows = []) => {
    const invoiceAmount = Math.max(0, Math.round(Number(amount || 0) * 100) / 100);
    if (invoiceAmount <= 0.001) return [];
    const items = Array.isArray(detail?.items) ? detail.items : [];
    if (detail?.linePaymentDetailAvailable === false || !items.length) {
      return [{ documentId: invoice.id, documentType: Number(invoice.transactionTypeId) === 640 ? 'repair' : 'invoice', lineKey: null, serialNo: null, amount: invoiceAmount }];
    }
    const explicitByKey = new Map(existingRows.map((row) => [
      `${row.lineKey || ''}|${allocationSerialNo(row.lineKey, row.serialNo)}`,
      Number(row.amount || 0),
    ]));
    let remaining = invoiceAmount;
    const rows = [];
    items.forEach((item, index) => {
      if (remaining <= 0.001) return;
      const itemKey = `${item.lineKey || ''}|${allocationSerialNo(item.lineKey, item.serialNo)}`;
      const explicitAmount = explicitByKey.get(itemKey) || 0;
      const limit = Math.max(0, Number(item.paymentLimit ?? item.dueAmount ?? item.lineTotal ?? 0) - explicitAmount);
      if (limit <= 0.001) return;
      const applied = Math.min(limit, remaining);
      rows.push({
        documentId: invoice.id,
        documentType: Number(invoice.transactionTypeId) === 640 ? 'repair' : 'invoice',
        lineKey: item.lineKey || null,
        serialNo: allocationSerialNo(item.lineKey, item.serialNo) || null,
        amount: Math.round(applied * 100) / 100,
      });
      remaining = Math.round((remaining - applied) * 100) / 100;
    });
    if (remaining > 0.001) {
      rows.push({ documentId: invoice.id, documentType: Number(invoice.transactionTypeId) === 640 ? 'repair' : 'invoice', lineKey: null, serialNo: null, amount: remaining });
    }
    return rows;
  };
  const setPaymentAllocation = (key, value) => {
    clearCreditApplyError();
    setAllocations((current) => {
      const next = { ...current };
      const [invoiceId, lineKey] = String(key).split('|');
      if (isAvailablePaymentMode && selectedCreditApplyTotal <= 0.001) return next;
      const invoiceKey = allocationKey(invoiceId);
      if (value === '' || value == null) delete next[key];
      else next[key] = value;
      if (lineKey) {
        const lineTotal = invoiceLineAllocationTotal(next, invoiceId);
        if (lineTotal > 0.001) next[invoiceKey] = lineTotal.toFixed(2);
        else delete next[invoiceKey];
      }
      return next;
    });
  };
  const setInvoiceTotalAllocation = (invoiceId, value) => {
    clearCreditApplyError();
    setAllocations((current) => {
      const next = { ...current };
      const invoiceKey = allocationKey(invoiceId);
      const lineTotal = invoiceLineAllocationTotal(next, invoiceId);
      if (value === '' || value == null) {
        delete next[invoiceKey];
      } else {
        const invoiceAmount = Math.max(lineTotal, Number(value || 0));
        if (invoiceAmount > 0) next[invoiceKey] = invoiceAmount.toFixed(2);
        else delete next[invoiceKey];
      }
      return next;
    });
  };
  const clearInvoiceAllocations = (invoiceId) => {
    clearCreditApplyError();
    setAllocations((current) => {
      const next = Object.fromEntries(Object.entries(current)
        .filter(([key]) => String(key).split('|')[0] !== String(invoiceId)));
      return next;
    });
  };
  const existingReviewInvoiceAmount = (invoice) => activePaymentReview?.allocations
    ?.filter((allocation) => String(allocation.documentId) === String(invoice.id))
    .reduce((sum, allocation) => sum + Number(allocation.amount || 0), 0) || 0;
  const invoicePaymentLimit = (invoice) => Number(invoice.amountDue || 0) + existingReviewInvoiceAmount(invoice);
  const toggleInvoiceAllocation = (invoice, checked) => {
    if (!checked) {
      setPaymentModalSelectedInvoiceIds((current) => current.map(String).filter((invoiceId) => invoiceId !== String(invoice.id)));
      clearInvoiceAllocations(invoice.id);
      syncCreditApplyAmountsToDue(Math.max(0, selectedPaymentAmountDue - invoicePaymentLimit(invoice)));
      return;
    }
    setPaymentModalSelectedInvoiceIds((current) => current.map(String).includes(String(invoice.id)) ? current : [...current, String(invoice.id)]);
    const currentValue = invoiceAllocationTotal(allocations, invoice.id);
    const currentAmount = isAvailablePaymentMode ? selectedCreditApplyTotal : Number(form.amount || 0);
    const otherAllocations = allocationTotal - currentValue;
    const invoiceAssignableAmount = invoicePaymentLimit(invoice);
    const amount = Math.min(invoiceAssignableAmount, Math.max(0, currentAmount - otherAllocations));
    setInvoiceTotalAllocation(invoice.id, amount ? amount.toFixed(2) : '');
    if (currentValue <= 0.001) {
      syncCreditApplyAmountsToDue(selectedPaymentAmountDue + invoiceAssignableAmount);
    }
  };
  const filteredInvoices = invoices.filter((invoice) => {
    if (Number(invoice.transactionTypeId) === 10) return false;
    const invoiceType = invoice.customInvoice ? 'custom' : (Number(invoice.transactionTypeId) === 640 ? 'repair' : Number(invoice.transactionTypeId) === 10 ? 'credit' : 'sale');
    const invoiceStatus = invoice.closed ? 'paid' : 'pending';
    const matchesType = selectedInvoiceTypes.includes(invoiceType);
    const matchesStatus = selectedInvoiceStatuses.includes(invoiceStatus);
    const date = localDateKey(invoice.date);
    const matchesInvoice = !invoiceNumberFilter.trim() || String(invoice.docNo || invoice.invoiceNo || '').toLowerCase().includes(invoiceNumberFilter.trim().toLowerCase());
    return matchesType && matchesStatus && matchesInvoice && (!invoiceDateRange.start || date >= invoiceDateRange.start) && (!invoiceDateRange.end || date <= invoiceDateRange.end);
  }).sort((a,b) => {
    const invoiceNumber = (invoice) => String(invoice.docNo || invoice.invoiceNo || '');
    const valueFor = (invoice) => {
      if (invoiceSort.key === 'date') return paymentDateValue(invoice.date).date?.getTime() || 0;
      if (invoiceSort.key === 'amount') return Number(invoice.originalAmount || 0);
      if (invoiceSort.key === 'applied') return Number(invoice.appliedAmount || 0);
      if (invoiceSort.key === 'due') return Number(invoice.amountDue || 0);
      return invoiceNumber(invoice);
    };
    const leftValue = valueFor(a);
    const rightValue = valueFor(b);
    const comparison = typeof leftValue === 'string'
      ? leftValue.localeCompare(String(rightValue), undefined, { numeric: true, sensitivity: 'base' })
      : leftValue - Number(rightValue || 0);
    if (comparison !== 0) return invoiceSort.direction === 'asc' ? comparison : -comparison;
    return invoiceNumber(a).localeCompare(invoiceNumber(b), undefined, { numeric: true, sensitivity: 'base' });
  });
  const filteredPayments = useMemo(() => payments.filter((payment) => {
    const date = localDateKey(payment.receivedAt);
    const matchesType = paymentTypeFilter === 'All'
      || (paymentTypeFilter === 'Unapplied Payments'
        ? Number(payment.availableAmount || 0) > 0.001
        : paymentDisplayType(payment) === paymentTypeFilter);
    return matchesType
      && (!paymentDateRange.start || date >= paymentDateRange.start)
      && (!paymentDateRange.end || date <= paymentDateRange.end);
  }).sort((left, right) => {
    const valueFor = (payment) => {
      if (paymentSort.key === 'date') return paymentDateValue(payment.receivedAt).date?.getTime() || 0;
      if (paymentSort.key === 'amount') return Number(payment.amount || 0);
      if (paymentSort.key === 'applied') return Number(payment.appliedAmount || 0);
      if (paymentSort.key === 'refunded') return Number(payment.refundedAmount || 0);
      if (paymentSort.key === 'available') return Number(payment.availableAmount || 0);
      return 0;
    };
    const comparison = valueFor(left) - valueFor(right);
    return paymentSort.direction === 'asc' ? comparison : -comparison;
  }), [payments, paymentTypeFilter, paymentDateRange, paymentSort]);
  const sortPaymentsBy = (key) => setPaymentSort((current) => ({
    key,
    direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
  }));
  const paymentSortArrow = (key) => (
    paymentSort.key === key && paymentSort.direction === 'asc' ? 'asc' : 'desc'
  );
  const sortInvoicesBy = (key) => setInvoiceSort((current) => ({
    key,
    direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
  }));
  const invoiceSortArrow = (key) => (
    invoiceSort.key === key && invoiceSort.direction === 'asc' ? 'asc' : 'desc'
  );
  const filteredPaymentTotals = useMemo(() => filteredPayments.reduce((totals, payment) => ({
    amount: totals.amount + Number(payment.amount || 0),
    applied: totals.applied + Number(payment.appliedAmount || 0),
    refunded: totals.refunded + Number(payment.refundedAmount || 0),
    available: totals.available + Number(payment.availableAmount || 0),
  }), { amount: 0, applied: 0, refunded: 0, available: 0 }), [filteredPayments]);
  const filteredInvoiceTotals = useMemo(() => filteredInvoices.reduce((totals, invoice) => ({
    amount: totals.amount + Number(invoice.originalAmount || 0),
    applied: totals.applied + Number(invoice.appliedAmount || 0),
    due: totals.due + Number(invoice.amountDue || 0),
  }), { amount: 0, applied: 0, due: 0 }), [filteredInvoices]);
  const paymentInvoices = useMemo(() => invoices.filter((invoice) => (
    Number(invoice.transactionTypeId) !== 10
    && Number(invoice.amountDue || 0) > 0.001
    && canApplyPaymentToInvoice(invoice)
  )), [canApplyPaymentToInvoice, invoices]);
  const paymentModalInvoices = useMemo(() => {
    if (!activePaymentReview?.allocations?.length) return paymentInvoices;
    const invoiceById = new Map(invoices.map((invoice) => [String(invoice.id), invoice]));
    const rows = [...paymentInvoices];
    const shown = new Set(rows.map((invoice) => String(invoice.id)));
    activePaymentReview.allocations.forEach((allocation) => {
      const id = String(allocation.documentId || '');
      if (!id || shown.has(id)) return;
      const invoice = invoiceById.get(id) || {
        id,
        invoiceNo: id,
        docNo: id,
        date: allocation.appliedAt,
        type: allocation.documentType === 'repair' ? 'Repair Order' : 'Shipping',
        transactionTypeId: allocation.documentType === 'repair' ? 640 : 4,
        originalAmount: 0,
        amountDue: 0,
      };
      rows.push(invoice);
      shown.add(id);
    });
    return rows;
  }, [activePaymentReview, invoices, paymentInvoices]);
  const isAvailablePaymentMode = form.paymentType === AVAILABLE_PAYMENT_TYPE;
  const paymentReceiptDocument = useMemo(() => {
    if (!modal || isAvailablePaymentMode) return null;
    const amount = Number(form.amount || 0);
    const appliedItems = Object.entries(allocations)
      .filter(([key]) => {
        const [invoiceId, lineKey] = key.split('|');
        return !lineKey || allocations[allocationKey(invoiceId)] === undefined;
      })
      .map(([key, value]) => {
        const appliedAmount = Number(value || 0);
        if (appliedAmount <= 0.001) return null;
        const [invoiceId] = key.split('|');
        const invoice = paymentModalInvoices.find((row) => String(row.id) === String(invoiceId));
        if (!invoice) return null;
        return {
          serial: invoice.invoiceNo || invoice.docNo || invoice.id,
          date: invoice.date,
          description: `${invoice.type || 'Invoice'} #${invoice.invoiceNo || invoice.docNo || invoice.id}`,
          quantity: 1,
          price: appliedAmount,
          receiptLineType: 'invoice_application',
        };
      })
      .filter(Boolean);
    const unusedAmount = Math.max(0, amount - allocationTotal);
    const items = [...appliedItems];
    if (unusedAmount > 0.001) items.push({ serial: '', description: 'Unused payment credit', quantity: 1, price: unusedAmount, receiptLineType: 'unused_credit' });
    if (!items.length) items.push({ serial: '', description: 'Payment received', quantity: 1, price: amount, receiptLineType: 'payment_received' });
    const to = {
      name: customerName(selected),
      addr1: selected?.bill_addr_1 || selected?.billaddr1 || selected?.address || '',
      addr2: selected?.bill_addr_2 || selected?.billaddr2 || '',
      addr3: selected?.bill_addr_3 || selected?.billaddr3 || '',
      addr4: selected?.bill_addr_4 || selected?.billaddr4 || '',
      city: selected?.bill_city || selected?.billcity || '',
      state: selected?.bill_state || selected?.billstate || '',
      zip: selected?.bill_zip || selected?.billzip || '',
      country: selected?.bill_country || selected?.billcountry || '',
      phone: selected?.phone || selected?.altphone || '',
      email: selected?.email || '',
    };
    return {
      invoiceNo: activePaymentReview?.id || form.referenceNo || 'New',
      date: form.receivedAt,
      type: 'Payment',
      documentTitle: 'Payment Receipt',
      paymentType: form.paymentType,
      referenceNo: form.referenceNo,
      from: businessAddress,
      businessAddress,
      to,
      billTo: to,
      clientName: to.name,
      customerName: to.name,
      items,
      totalQuantity: items.length,
      totalAmount: amount,
      payments: amount,
      credits: 0,
      balanceDue: 0,
      preparedBy: activePaymentReview?.enteredBy || '',
      memo: [form.paymentType, form.referenceNo ? `Reference #${form.referenceNo}` : '', form.notes].filter(Boolean).join('\n'),
    };
  }, [activePaymentReview, allocationTotal, allocations, businessAddress, form.amount, form.notes, form.paymentType, form.receivedAt, form.referenceNo, isAvailablePaymentMode, modal, paymentModalInvoices, selected]);
  const paymentTypes = useMemo(() => ['All', 'Credit Memo', 'Payment', 'Voided Payment', 'Unapplied Payments'], []);
  const customerHeaderBalanceLabel = tab === 'payments' ? 'Available Credit' : 'Balance';
  const customerHeaderBalance = tab === 'payments'
    ? payments.reduce((sum, payment) => sum + Number(payment.availableAmount || 0), 0)
    : invoices.reduce((sum, invoice) => sum + Number(invoice.amountDue || 0), 0);
  const invoiceStatusFilterValue = selectedInvoiceStatuses.length === INVOICE_STATUS_OPTIONS.length
    ? 'All'
    : selectedInvoiceStatuses.includes('paid') ? 'Paid' : 'Pending';
  const changeInvoiceStatusFilter = (status) => {
    setSelectedInvoiceStatuses(status === 'All' ? INVOICE_STATUS_OPTIONS.map(([value]) => value) : [status.toLowerCase()]);
  };
  const changeInvoiceDatePreset = (preset) => {
    setInvoiceDatePreset(preset);
    setInvoiceDateRange(transactionPresetRange(preset));
  };
  const changePaymentDatePreset = (preset) => {
    setPaymentDatePreset(preset);
    setPaymentDateRange(transactionPresetRange(preset));
  };
  const walletPayments = payments.filter(canApplyAvailablePayment);
  const applyTotal = Object.values(applyModal.amounts).reduce((sum, amount) => sum + Number(amount || 0), 0);
  const selectedPaymentIds = Array.isArray(applyModal.paymentIds) ? applyModal.paymentIds.map(String) : [applyModal.paymentId].filter(Boolean).map(String);
  const selectedWalletPayments = walletPayments.filter((payment) => selectedPaymentIds.includes(String(payment.id)));
  const selectedPaymentAmounts = applyModal.paymentAmounts || {};
  const selectedPaymentCredits = selectedWalletPayments.map((payment) => ({
    ...payment,
    applyAmount: Math.min(Number(payment.availableAmount || 0), Math.max(0, Number(selectedPaymentAmounts[String(payment.id)] || 0))),
  })).filter((payment) => payment.applyAmount > 0.001);
  const selectedCreditTotal = selectedPaymentCredits.reduce((sum, payment) => sum + payment.applyAmount, 0);
  const selectedCreditLabel = selectedWalletPayments.length
    ? `${selectedWalletPayments.length} selected - ${currency(selectedCreditTotal)} to apply`
    : 'Select payment credit';
  const creditApplyPaymentIds = Array.isArray(creditApplyModal.paymentIds) ? creditApplyModal.paymentIds.map(String) : [];
  const creditApplyPaymentAmounts = creditApplyModal.paymentAmounts || {};
  const selectedCreditApplyWalletPayments = walletPayments.filter((payment) => creditApplyPaymentIds.includes(String(payment.id)));
  const selectedCreditApplyOriginalTotal = selectedCreditApplyWalletPayments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
  const selectedCreditApplyOriginalDate = localDateKey(selectedCreditApplyWalletPayments[0]?.receivedAt || creditApplyModal.sourcePayment?.receivedAt || '');
  const selectedCreditApplyPayments = selectedCreditApplyWalletPayments.map((payment) => ({
    ...payment,
    applyAmount: Math.min(Number(payment.availableAmount || 0), Math.max(0, Number(creditApplyPaymentAmounts[String(payment.id)] || 0))),
  })).filter((payment) => payment.applyAmount > 0.001);
  const selectedCreditApplyTotal = selectedCreditApplyPayments.reduce((sum, payment) => sum + payment.applyAmount, 0);
  const creditApplyLabel = selectedCreditApplyWalletPayments.length
    ? `${selectedCreditApplyWalletPayments.length} selected - ${currency(selectedCreditApplyTotal)} to apply`
    : '';
  const applyInvoiceTotal = (applyModal.detail?.items || []).reduce((sum, item) => sum + Number(item.lineTotal || 0), 0);
  const applyInvoicePaymentsCredits = Number(applyModal.detail?.paidAmount || 0);
  const applyInvoiceBalanceDue = Number(applyModal.detail?.amountDue ?? applyModal.invoice?.amountDue ?? 0);
  const applyLinePaymentDetailAvailable = applyModal.detail?.linePaymentDetailAvailable !== false;
  const selectedPaymentInvoiceIds = isAvailablePaymentMode
    ? paymentModalSelectedInvoiceIds.map(String)
    : [...new Set(Object.entries(allocations)
    .filter(([, value]) => Number(value || 0) > 0.001)
    .map(([key]) => String(key).split('|')[0]))];
  const selectedPaymentAmountDue = selectedPaymentInvoiceIds.reduce((sum, invoiceId) => {
    const invoice = paymentModalInvoices.find((row) => String(row.id) === String(invoiceId));
    return sum + (invoice ? invoicePaymentLimit(invoice) : 0);
  }, 0);
  const paymentAllocationWarning = (() => {
    if (isAvailablePaymentMode) return '';
    if (allocationTotal > Number(form.amount || 0) + 0.001) {
      return `Applied amount ${currency(allocationTotal)} exceeds received payment ${currency(form.amount)}.`;
    }
    for (const [key, value] of Object.entries(allocations)) {
      const amount = Number(value || 0);
      if (amount <= 0.001) continue;
      const [documentId, lineKey, serialNo = ''] = String(key).split('|');
      if (lineKey) {
        if (!details[documentId]) continue;
        const item = findAllocationLineItem(documentId, lineKey, serialNo);
        if (!item) continue;
        const limit = Number(item?.paymentLimit ?? item?.dueAmount ?? item?.lineTotal ?? 0);
        if (amount > limit + 0.001) {
          return `Applied amount exceeds the due amount for ${allocationLineWarningTarget(item, lineKey, serialNo)}.`;
        }
        continue;
      }
      const invoice = paymentModalInvoices.find((row) => String(row.id) === String(documentId));
      const limit = invoice ? invoicePaymentLimit(invoice) : 0;
      if (amount > limit + 0.001) {
        return `Applied amount exceeds the due amount for invoice ${invoice?.invoiceNo || invoice?.docNo || documentId}.`;
      }
    }
    return '';
  })();
  const creditApplyAllocationWarning = (() => {
    if (!modal || !isAvailablePaymentMode) return '';
    if (!selectedCreditApplyPayments.length) return '';
    if (allocationTotal > selectedCreditApplyTotal + 0.001) {
      return `Applied amount ${currency(allocationTotal)} exceeds selected payment credits ${currency(selectedCreditApplyTotal)}.`;
    }
    if (selectedPaymentAmountDue > 0.001 && selectedCreditApplyTotal > selectedPaymentAmountDue + 0.001) {
      return `Selected payment credits ${currency(selectedCreditApplyTotal)} exceed the selected invoice amount due ${currency(selectedPaymentAmountDue)}. Reduce the amount to apply or select more invoices.`;
    }
    for (const [key, value] of Object.entries(allocations)) {
      const amount = Number(value || 0);
      if (amount <= 0.001) continue;
      const [documentId, lineKey, serialNo = ''] = String(key).split('|');
      if (lineKey) {
        if (!details[documentId]) continue;
        const item = findAllocationLineItem(documentId, lineKey, serialNo);
        if (!item) continue;
        const limit = Number(item?.paymentLimit ?? item?.dueAmount ?? item?.lineTotal ?? 0);
        if (amount > limit + 0.001) {
          return `Applied amount exceeds the due amount for ${allocationLineWarningTarget(item, lineKey, serialNo)}.`;
        }
        continue;
      }
      const invoice = paymentModalInvoices.find((row) => String(row.id) === String(documentId));
      const limit = invoice ? invoicePaymentLimit(invoice) : 0;
      if (amount > limit + 0.001) {
        return `Applied amount exceeds the due amount for invoice ${invoice?.invoiceNo || invoice?.docNo || documentId}.`;
      }
    }
    return '';
  })();
  const canSavePayment = Boolean(
    (activePaymentReview ? canEditPayment : canReceiveCustomerPayment)
    &&
    selected
    && !isAvailablePaymentMode
    && form.paymentType
    && form.receivedAt
    && Number(form.amount) > 0
    && allocationTotal <= Number(form.amount || 0)
    && !paymentAllocationWarning
  );
  const canSaveCreditApplication = Boolean(
    canReceiveCustomerPayment
    &&
    selected
    && modal
    && isAvailablePaymentMode
    && selectedCreditApplyPayments.length
    && allocationTotal > 0.001
    && allocationTotal <= selectedCreditApplyTotal + 0.001
    && !creditApplyAllocationWarning
  );
  const canSaveUniversalPayment = isAvailablePaymentMode ? canSaveCreditApplication : canSavePayment;
  const paymentAmountValue = isAvailablePaymentMode ? (selectedCreditApplyOriginalTotal ? selectedCreditApplyOriginalTotal.toFixed(2) : '') : form.amount;
  const paymentModalDateValue = isAvailablePaymentMode ? (selectedCreditApplyOriginalDate || form.receivedAt) : form.receivedAt;
  const paymentHeaderReadOnly = isAvailablePaymentMode || isCreditMemoPayment(activePaymentReview);
  const currentPaymentSnapshot = paymentEditSnapshot(form, allocations);
  const paymentHasChanges = !activePaymentReview || currentPaymentSnapshot !== savedPaymentSnapshot;
  const paymentActionDisabled = loading || paymentEmailLoading || !paymentReceiptDocument || (paymentHasChanges && !canSavePayment);
  const paymentReceiptNumber = activePaymentReview?.id || paymentReceiptDocument?.invoiceNo || 'New';
  const listingActionDisabled = tab === 'payments' ? filteredPayments.length === 0 : tab === 'invoices' ? filteredInvoices.length === 0 : true;
  const transactionListingDisabled = filteredTransactions.length === 0;
  const listingGeneratedAt = () => new Date().toLocaleString();
  const currentListingData = (listingType = tab, options = {}) => {
    const customer = customerName(selected);
    if (listingType === 'transactions') {
      const transactionRows = options.transactions || sortedTransactions;
      const transactionTotals = transactionRows.reduce((totals, transaction) => ({
        amount: totals.amount + Number(transaction.amount || 0),
        unappliedAmount: totals.unappliedAmount + Number(transaction.unappliedAmount || 0),
      }), { amount: 0, unappliedAmount: 0 });
      return {
        type: 'transactions',
        title: `${selectedTransactionType} Transactions`,
        customerName: 'Transactions',
        generatedAt: listingGeneratedAt(),
        filters: [
          { label: 'Transaction Type', value: selectedTransactionType },
          selectedTransactionType === 'Invoice'
            ? { label: 'Status', value: transactionInvoiceStatusFilter }
            : ['Received Payment','Voided Payment','Refund'].includes(selectedTransactionType)
              ? { label: 'Payment Method', value: transactionPaymentType }
              : null,
          { label: 'Date', value: transactionDatePreset },
        ].filter(Boolean),
        headers: ['Customer', 'Reference', 'Date', 'Payment Method', 'Amount', 'Unapplied Amount'],
        rows: transactionRows.map((transaction) => [
          transaction.customerName || '-',
          transaction.referenceNo || (transaction.paymentId ? `#${transaction.paymentId}` : '-'),
          transactionDateDisplay(transaction.date),
          transaction.paymentType || '-',
          currency(transaction.amount),
          currency(transaction.unappliedAmount),
        ]),
        totals: ['Totals', '', '', '', currency(transactionTotals.amount), currency(transactionTotals.unappliedAmount)],
      };
    }
    if (listingType === 'payments') {
      return {
        type: 'payments',
        title: `${customer} Payments`,
        customerName: customer,
        generatedAt: listingGeneratedAt(),
        filters: [
          { label: 'Type', value: paymentTypeFilter },
          { label: 'Date', value: paymentDatePreset },
        ],
        headers: ['Date', 'Type', 'Reference', 'Amount', 'Applied', 'Refunded', 'Available'],
        rows: filteredPayments.map((payment) => {
          const dateDisplay = paymentDateDisplay(payment);
          return [
            dateDisplay.secondary ? `${dateDisplay.primary} (${dateDisplay.secondary})` : dateDisplay.primary,
            paymentDisplayType(payment),
            paymentReferenceDisplay(payment),
            currency(payment.amount),
            currency(payment.appliedAmount),
            currency(payment.refundedAmount),
            currency(payment.availableAmount),
          ];
        }),
        totals: ['Totals', '', '', currency(filteredPaymentTotals.amount), currency(filteredPaymentTotals.applied), currency(filteredPaymentTotals.refunded), currency(filteredPaymentTotals.available)],
      };
    }
    return {
      type: 'invoices',
      title: `${customer} Invoices`,
      customerName: customer,
      generatedAt: listingGeneratedAt(),
      filters: [
        { label: 'Status', value: invoiceStatusFilterValue },
        { label: 'Date', value: invoiceDatePreset },
        { label: 'From Date', value: invoiceDateRange.start || 'All' },
        { label: 'To Date', value: invoiceDateRange.end || 'All' },
        ...(invoiceNumberFilter ? [{ label: 'Invoice Search', value: invoiceNumberFilter }] : []),
      ],
      headers: ['Date', 'Invoice', 'Status', 'Transaction Type', 'Amount', 'Applied', 'Amount Due', 'Date Last Modified'],
      rows: filteredInvoices.map((invoice) => [
        transactionDateDisplay(invoice.date, { timeZone: BUSINESS_TIME_ZONE }),
        invoice.docNo || invoice.invoiceNo || invoice.id || '-',
        invoice.closed ? 'Paid' : 'Pending',
        invoice.type || '-',
        currency(invoice.originalAmount),
        currency(invoice.appliedAmount),
        currency(invoice.amountDue),
        transactionDateDisplay(invoice.dateLastModified, { timeZone: BUSINESS_TIME_ZONE }),
      ]),
      totals: ['Totals', '', '', '', currency(filteredInvoiceTotals.amount), currency(filteredInvoiceTotals.applied), currency(filteredInvoiceTotals.due), ''],
    };
  };
  const openListingPreview = async (listingType = tab) => {
    if (listingType === 'transactions') {
      setListingPreviewLoading(true);
      setTransactionsError('');
      try {
        const baseOptions = {
          pageSize: PRINT_TRANSACTION_PAGE_SIZE,
          sortKey: transactionSort.key,
          sortDirection: transactionSort.direction,
          paymentType: ['Received Payment','Voided Payment','Refund'].includes(selectedTransactionType) ? transactionPaymentType : '',
          status: selectedTransactionType === 'Invoice' ? transactionInvoiceStatusFilter : '',
        };
        const firstResult = await fetchPaymentTransactions(transactionDateRange, selectedTransactionType, {
          ...baseOptions,
          page: 1,
        });
        if (firstResult.total > MAX_PRINT_TRANSACTION_ROWS) {
          throw new Error(`Too many transactions matched this print request. Narrow the filters to ${MAX_PRINT_TRANSACTION_ROWS.toLocaleString()} rows or fewer and try again.`);
        }
        const allRows = [...(firstResult.rows || [])];
        const totalPages = Math.ceil(firstResult.total / firstResult.pageSize);
        for (let pageNumber = 2; pageNumber <= totalPages; pageNumber += 1) {
          const pageResult = await fetchPaymentTransactions(transactionDateRange, selectedTransactionType, {
            ...baseOptions,
            page: pageNumber,
          });
          allRows.push(...(pageResult.rows || []));
        }
        const rows = allRows.filter((transaction) => (
          !selectedTransactionCustomers || selectedTransactionCustomers.includes(transaction.customerName || '-')
        ));
        const listing = currentListingData(listingType, { transactions: rows });
        if (!listing.rows.length) return;
        setListingPreview(listing);
      } catch (error) {
        setTransactionsError(error.response?.data?.error || error.message || 'Could not load printable transactions.');
      } finally {
        setListingPreviewLoading(false);
      }
      return;
    }
    const listing = currentListingData(listingType);
    if (!listing.rows.length) return;
    setListingPreview(listing);
  };

  const printableApplyInvoiceDocument = useMemo(() => {
    const document = applyModal.invoice?.customInvoice
      ? customInvoiceDetailToDocument({
        invoice: applyModal.invoice,
        detail: applyModal.detail,
        customer: selected,
        businessAddress,
      })
      : applyModal.invoiceDocument ? mergeImportedDetailIntoInvoiceDocument(applyModal.invoiceDocument, applyModal.detail) : (applyModal.invoice?.importedOnly || applyModal.detail?.importedOnly || applyModal.detail ? importedInvoiceDetailToDocument({
        invoice: applyModal.invoice,
        detail: applyModal.detail,
        customer: selected,
        businessAddress,
      }) : null);
    if (!document) return null;
    const detail = applyModal.detail || {};
    const paidAmount = Number.isFinite(Number(detail.paidAmount))
      ? Number(detail.paidAmount)
      : (Number(document.payments) || 0) + (Number(document.credits) || 0);
    const amountDue = invoiceDocumentBalanceDue({ ...document, credits: 0 }, paidAmount);
    return {
      ...document,
      payments: paidAmount,
      credits: 0,
      balanceDue: amountDue,
      paidDate: detail.paidDate || applyModal.invoice?.paidDate || document.paidDate || null,
    };
  }, [applyModal.detail, applyModal.invoice, applyModal.invoiceDocument, businessAddress, selected]);
  const printableInvoicePreviewDocument = useMemo(() => {
    const document = invoicePreviewModal.invoice?.customInvoice
      ? customInvoiceDetailToDocument({
        invoice: invoicePreviewModal.invoice,
        detail: invoicePreviewModal.detail,
        customer: selected,
        businessAddress,
      })
      : invoicePreviewModal.invoiceDocument ? mergeImportedDetailIntoInvoiceDocument(invoicePreviewModal.invoiceDocument, invoicePreviewModal.detail) : (invoicePreviewModal.invoice?.importedOnly || invoicePreviewModal.detail?.importedOnly || invoicePreviewModal.detail ? importedInvoiceDetailToDocument({
        invoice: invoicePreviewModal.invoice,
        detail: invoicePreviewModal.detail,
        customer: selected,
        businessAddress,
      }) : null);
    if (!document) return null;
    const detail = invoicePreviewModal.detail || {};
    const paidAmount = Number.isFinite(Number(detail.paidAmount))
      ? Number(detail.paidAmount)
      : (Number(document.payments) || 0) + (Number(document.credits) || 0);
    const amountDue = invoiceDocumentBalanceDue({ ...document, credits: 0 }, paidAmount);
    return {
      ...document,
      payments: paidAmount,
      credits: 0,
      balanceDue: amountDue,
      paidDate: detail.paidDate || invoicePreviewModal.invoice?.paidDate || document.paidDate || null,
    };
  }, [businessAddress, invoicePreviewModal.detail, invoicePreviewModal.invoice, invoicePreviewModal.invoiceDocument, selected]);
  const printableCreditMemoDocument = useMemo(() => {
    const document = creditMemoModal.transfer
      ? transferToEmailInvoice(creditMemoModal.transfer)
      : creditMemoModal.invoice;
    if (!document) return null;
    return {
      ...document,
      to: selected && !document.to?.email ? { ...(document.to || {}), email: selected.email } : document.to,
      businessAddress: document.businessAddress || businessAddress,
    };
  }, [businessAddress, creditMemoModal.invoice, creditMemoModal.transfer, selected]);
  const paymentCreditMemoEditing = paymentInvoiceEditor.open
    && creditMemoModal.open
    && String(paymentInvoiceEditor.docNo) === String(creditMemoModal.docNo || printableCreditMemoDocument?.docNo || printableCreditMemoDocument?.invoiceNo || '');
  const applyInvoiceDocumentNumber = applyModal.invoice?.invoiceNo || applyModal.invoice?.docNo || applyModal.invoice?.id || '';
  const applyInvoiceDocumentDate = (() => {
    const value = printableApplyInvoiceDocument?.date || applyModal.invoice?.date || applyModal.detail?.date;
    const date = value ? new Date(value) : null;
    return date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString() : '';
  })();
  const applyInvoiceCustomer = printableApplyInvoiceDocument?.to || {
    name: customerName(selected),
    phone: selected?.phone || selected?.altphone || '',
    email: selected?.email || '',
  };
  const applyInvoiceCustomerLines = [
    applyInvoiceCustomer?.name,
    applyInvoiceCustomer?.phone || applyInvoiceCustomer?.addr3,
    applyInvoiceCustomer?.email,
  ].filter(Boolean);
  const paymentAllocationRows = () => Object.entries(allocations).map(([key, amount]) => {
    const [documentId, lineKey, serialNo = ''] = key.split('|');
    const normalizedSerialNo = allocationSerialNo(lineKey, serialNo);
    return { documentId, documentType: documentId.startsWith('RO-') ? 'repair' : 'invoice', lineKey: lineKey || null, serialNo: normalizedSerialNo || null, amount: Number(amount || 0) };
  }).filter((row) => row.amount > 0.001);
  const expandedPaymentAllocationRows = async () => {
    const grouped = new Map();
    paymentAllocationRows().forEach((row) => {
      const groupKey = `${row.documentType}|${row.documentId}`;
      if (!grouped.has(groupKey)) grouped.set(groupKey, { documentType: row.documentType, documentId: row.documentId, invoiceAmount: 0, lineRows: [] });
      const group = grouped.get(groupKey);
      if (row.lineKey) group.lineRows.push(row);
      else group.invoiceAmount += Number(row.amount || 0);
    });
    const expanded = [];
    for (const group of grouped.values()) {
      const invoiceTotal = Math.max(0, Math.round(Number(group.invoiceAmount || 0) * 100) / 100);
      const lineRows = [...group.lineRows];
      const explicitTotal = group.lineRows.reduce((sum, row) => sum + Number(row.amount || 0), 0);
      const invoiceRemainder = Math.max(0, Math.round((invoiceTotal - explicitTotal) * 100) / 100);
      if (invoiceRemainder > 0.001) {
        const invoice = paymentModalInvoices.find((row) => String(row.id) === String(group.documentId))
          || invoices.find((row) => String(row.id) === String(group.documentId))
          || { id: group.documentId, transactionTypeId: group.documentType === 'repair' ? 640 : 4 };
        let detail = details[group.documentId];
        try { detail = detail || await loadDocumentDetail(invoice); }
        catch { detail = null; }
        lineRows.push(...distributeInvoiceAmountToSerials(invoice, invoiceRemainder, detail, group.lineRows)
          .filter((row) => row.lineKey));
      }
      expanded.push(...lineRows);
      if (invoiceTotal > 0.001) {
        expanded.push({
          documentId: group.documentId,
          documentType: group.documentType,
          lineKey: null,
          serialNo: null,
          amount: invoiceTotal,
        });
      }
    }
    return expanded;
  };
  const savePaymentInPlace = async () => {
    if (activePaymentReview && !paymentHasChanges) return activePaymentReview;
    if (!canSavePayment) {
      setStatus('Enter the required payment fields before continuing.');
      return null;
    }
    setLoading(true);
    setStatus('');
    const allocationRows = await expandedPaymentAllocationRows();
    try {
      if (activePaymentReview) {
        const { data } = await savePaymentReviewChanges(api, activePaymentReview, form, allocationRows);
        const savedPayment = { ...data, allocations: allocationRows };
        setActivePaymentReview(savedPayment);
        setSavedPaymentSnapshot(currentPaymentSnapshot);
        await loadCustomer(selected, { tab });
        if (leftTab === 'transactions') {
          await loadTransactions({ page: transactionPageInfo.page || 1 });
        }
        setStatus('Payment updated.');
        return savedPayment;
      }
      const { data } = await api.post('/payments', { ...form, customerId: customerId(selected), amount: Number(form.amount), allocations: allocationRows });
      const savedPayment = { ...data, allocations: allocationRows };
      setActivePaymentReview(savedPayment);
      setSavedPaymentSnapshot(currentPaymentSnapshot);
      await loadCustomer(selected, { tab });
      setStatus('Payment added.');
      return savedPayment;
    } catch (error) {
      setStatus(error.response?.data?.error || (activePaymentReview ? 'Could not update payment applications.' : 'Could not add payment.'));
      return null;
    } finally {
      setLoading(false);
    }
  };
  const savePayment = async (event, closeAfter = true) => {
    event.preventDefault();
    if (loading) return;
    if (!canSavePayment) {
      setStatus(paymentAllocationWarning || 'Enter the required payment fields before continuing.');
      return;
    }
    setLoading(true); setStatus('');
    const allocationRows = await expandedPaymentAllocationRows();
    try {
      const { data } = await api.post('/payments', { ...form, customerId: customerId(selected), amount: Number(form.amount), allocations: allocationRows, sendReceiptEmail: true });
      if (closeAfter) {
        setModal(false);
        setPaymentModalSelectedInvoiceIds([]);
      } else { setForm(emptyPayment()); setAllocations({}); setDetails({}); setPaymentModalSelectedInvoiceIds([]); }
      await loadCustomer(selected, { tab });
      setStatus(data.autoEmail?.status === 'failed'
        ? data.autoEmail.message
        : data.autoEmail?.status === 'sent'
          ? `Payment added. PDF confirmation emailed to ${data.autoEmail.to}.`
          : 'Payment added.');
    } catch (error) { setStatus(error.response?.data?.error || 'Could not add payment.'); }
    finally { setLoading(false); }
  };
  const savePaymentReview = async (event) => {
    event.preventDefault();
    if (!activePaymentReview || !paymentHasChanges || !canSavePayment || loading) return;
    setLoading(true);
    setStatus('');
    try {
      await savePaymentReviewChanges(api, activePaymentReview, form, await expandedPaymentAllocationRows());
      setModal(false);
      setActivePaymentReview(null);
      setPaymentModalSelectedInvoiceIds([]);
      await loadCustomer(selected, { tab });
      if (leftTab === 'transactions') {
        await loadTransactions({ page: transactionPageInfo.page || 1 });
      }
      setStatus('Payment updated.');
    } catch (error) {
      setStatus(error.response?.data?.error || 'Could not update payment.');
    } finally {
      setLoading(false);
    }
  };
  const savePaymentBeforePrint = async () => {
    const savedPayment = await savePaymentInPlace();
    if (!savedPayment) return;
    await printPaymentReceiptOutput(savedPayment.id);
  };
  const savePaymentBeforeEmail = async () => {
    const savedPayment = await savePaymentInPlace();
    if (!savedPayment) return false;
    return {
      endpoint: `/payments/${savedPayment.id}/email`,
      recipient: selected?.email || '',
    };
  };
  const openDeletePaymentConfirm = () => {
    if (!canVoidPayment) {
      setStatus('You do not have permission to void payments.');
      return;
    }
    if (!activePaymentReview) return;
    setDeletePaymentConfirm({ open:true, payment:activePaymentReview, value:'', error:'', loading:false });
  };
  const closeDeletePaymentConfirm = () => setDeletePaymentConfirm({ open:false, payment:null, value:'', error:'', loading:false });
  const submitDeletePayment = async (event) => {
    event.preventDefault();
    if (!canVoidPayment) {
      setDeletePaymentConfirm((current)=>({...current,error:'You do not have permission to void payments.'}));
      return;
    }
    const expected = String(deletePaymentConfirm.payment?.id || '').trim();
    if (String(deletePaymentConfirm.value || '').trim() !== expected) {
      setDeletePaymentConfirm((current)=>({...current,error:`Type payment number ${expected} to void this payment.`}));
      return;
    }
    setDeletePaymentConfirm((current)=>({...current,loading:true,error:''}));
    try {
      await api.delete(`/payments/${encodeURIComponent(expected)}`, { data: { confirmPaymentId: expected } });
      closeDeletePaymentConfirm();
      setModal(false);
      setActivePaymentReview(null);
      setPaymentModalSelectedInvoiceIds([]);
      await loadCustomer(selected, { tab });
      if (leftTab === 'transactions') {
        await loadTransactions({ page: transactionPageInfo.page || 1 });
      }
      setStatus(`Payment #${expected} voided and unapplied from invoices.`);
    } catch (error) {
      setDeletePaymentConfirm((current)=>({...current,loading:false,error:error.response?.data?.error||'Could not void payment.'}));
    }
  };

  const refund = (payment) => {
    if (!canEditPayment) {
      setStatus('You do not have permission to edit payments.');
      return;
    }
    setRefundModal({
    open:true,
    payment,
    choice:'remaining',
    amount:Number(payment.availableAmount)>0 ? Number(payment.availableAmount).toFixed(2) : '',
    receivedAt:today(),
    method:isCreditMemoPayment(payment) ? 'Check' : '',
    account:"Irene's Wigs",
    referenceNo:'',
    memo:'',
    toBePrinted:true,
    error:'',
      loading:false,
    });
  };
  const submitRefund = async (event) => {
    event.preventDefault();
    if (!canEditPayment) {
      return setRefundModal((current)=>({...current,error:'You do not have permission to edit payments.'}));
    }
    const creditMemoRefund = isCreditMemoPayment(refundModal.payment);
    const amount=creditMemoRefund ? Number(refundModal.amount || 0) : Number(refundModal.payment.availableAmount||0);
    if (amount<=0) return setRefundModal((current)=>({...current,error:'There is no balance available to refund.'}));
    if (creditMemoRefund && amount > Number(refundModal.payment?.availableAmount || 0) + 0.001) {
      return setRefundModal((current)=>({...current,error:'Refund amount cannot exceed the available credit memo balance.'}));
    }
    setRefundModal((current)=>({...current,loading:true,error:''}));
    try { await api.post(`/payments/${refundModal.payment.id}/refund`, {amount,full:false,referenceNo:refundModal.referenceNo || '',memo:refundModal.memo || ''}); setRefundModal({open:false,payment:null,choice:'remaining',error:'',loading:false}); await loadCustomer(selected); setStatus(`${creditMemoRefund?'Credit memo refund':'Remaining balance refund'} recorded.`); }
    catch (error) { setRefundModal((current)=>({...current,loading:false,error:error.response?.data?.error||'Refund failed.'})); }
  };

  const closeCreditApplyModal = () => {
    setModal(false);
    resetCreditApplyState();
    setAllocations({});
    setDetails({});
    setForm(emptyPayment());
    setPaymentModalSelectedInvoiceIds([]);
  };
  const creditApplyNote = () => `Applying Credits for ${customerName(selected)}`;
  const selectPaymentMethod = (paymentType) => {
    if (paymentType === AVAILABLE_PAYMENT_TYPE) {
      if (activePaymentReview) return;
      setForm((current) => ({
        ...current,
        paymentType,
        amount: selectedCreditApplyTotal ? selectedCreditApplyTotal.toFixed(2) : '',
        referenceNo: creditApplyModal.sourcePayment?.referenceNo || creditApplyModal.sourcePayment?.id || current.referenceNo || '',
        notes: creditApplyNote(),
      }));
      setCreditApplyModal((current) => ({...current, open:false, creditMenuOpen:true, error:''}));
      return;
    }
    setForm((current) => ({
      ...current,
      paymentType,
      amount: current.paymentType === AVAILABLE_PAYMENT_TYPE ? '' : current.amount,
      referenceNo: current.paymentType === AVAILABLE_PAYMENT_TYPE ? '' : current.referenceNo,
      notes: current.paymentType === AVAILABLE_PAYMENT_TYPE ? '' : current.notes,
    }));
    resetCreditApplyState();
  };
  const syncCreditApplyFormAmount = (paymentAmounts, paymentIds) => {
    const total = walletPayments
      .filter((payment) => paymentIds.includes(String(payment.id)))
      .reduce((sum, payment) => sum + Math.min(Number(payment.availableAmount || 0), Math.max(0, Number(paymentAmounts[String(payment.id)] || 0))), 0);
    setForm((current) => ({...current, amount: total ? total.toFixed(2) : ''}));
    return total;
  };
  const capCreditApplyPaymentAmountsToDue = (paymentAmounts, paymentIds, amountDue) => {
    const target = Math.max(0, Math.round(Number(amountDue || 0) * 100) / 100);
    if (target <= 0.001) return paymentAmounts;
    let remaining = target;
    const next = { ...paymentAmounts };
    paymentIds.forEach((paymentId) => {
      const id = String(paymentId);
      const payment = walletPayments.find((item) => String(item.id) === id);
      const availableAmount = Math.max(0, Number(payment?.availableAmount || 0));
      const currentAmount = Math.min(availableAmount, Math.max(0, Number(next[id] || 0)));
      const cappedAmount = Math.min(currentAmount, remaining);
      next[id] = cappedAmount > 0.001 ? cappedAmount.toFixed(2) : '';
      remaining = Math.round((remaining - cappedAmount) * 100) / 100;
    });
    return next;
  };
  const syncCreditApplyAmountsToDue = (amountDue) => {
    const ids = Array.isArray(creditApplyModal.paymentIds) ? creditApplyModal.paymentIds.map(String) : [];
    if (!ids.length || Number(amountDue || 0) <= 0.001) return;
    setCreditApplyModal((current) => {
      const currentIds = Array.isArray(current.paymentIds) ? current.paymentIds.map(String) : [];
      const paymentAmounts = capCreditApplyPaymentAmountsToDue(current.paymentAmounts || {}, currentIds, amountDue);
      syncCreditApplyFormAmount(paymentAmounts, currentIds);
      return {...current, paymentAmounts, error:''};
    });
  };
  const openCreditApply = (payment) => {
    if (!canCreatePayment) {
      setStatus('You do not have permission to apply payments.');
      return;
    }
    const id = String(payment.id);
    const amount = Math.max(0, Number(payment.availableAmount || 0));
    setActivePaymentReview(null);
    setPaymentModalMode('full');
    setPaymentModalHighlightedInvoiceId(null);
    setPaymentModalSelectedInvoiceIds([]);
    setForm({...emptyPayment(), paymentType:AVAILABLE_PAYMENT_TYPE, amount: amount ? amount.toFixed(2) : '', referenceNo:payment.referenceNo || payment.id || '', notes:creditApplyNote()});
    setAllocations({});
    setDetails({});
    setStatus('');
    setCreditApplyModal({
      open:false,
      sourcePayment:payment,
      paymentIds:[id],
      paymentAmounts:{ [id]: amount ? amount.toFixed(2) : '' },
      creditMenuOpen:true,
      error:'',
      loading:false,
      returnTab:tab,
    });
    setModal(true);
  };
  const openInvoicePaymentModal = (invoice) => {
    if (!canCreatePayment) {
      setStatus('You do not have permission to apply payments.');
      return;
    }
    if (!canApplyPaymentToInvoice(invoice)) {
      setStatus('You can only apply payments to repair orders.');
      return;
    }
    setActivePaymentReview(null);
    setPaymentModalMode('full');
    setPaymentModalHighlightedInvoiceId(invoice.id);
    setPaymentModalSelectedInvoiceIds([String(invoice.id)]);
    setForm({...emptyPayment(), paymentType:AVAILABLE_PAYMENT_TYPE, amount:'', referenceNo:invoice.docNo || invoice.invoiceNo || invoice.id || '', notes:creditApplyNote()});
    setAllocations({});
    setDetails({});
    setStatus('');
    setApplyModal((current) => ({...current, open:false, creditMenuOpen:false}));
    setCreditApplyModal({ open:false, sourcePayment:null, paymentIds:[], paymentAmounts:{}, creditMenuOpen:true, error:'', loading:false, returnTab:tab });
    setModal(true);
  };
  const toggleCreditApplyPayment = (paymentId) => setCreditApplyModal((current) => {
    const id = String(paymentId);
    const ids = Array.isArray(current.paymentIds) ? current.paymentIds.map(String) : [];
    const selectedId = ids.includes(id);
    const paymentIds = selectedId ? ids.filter((value) => value !== id) : [...ids, id];
    const paymentAmounts = { ...(current.paymentAmounts || {}) };
    if (selectedId) delete paymentAmounts[id];
    else {
      const payment = walletPayments.find((item) => String(item.id) === id);
      const selectedTotal = Object.entries(paymentAmounts)
        .filter(([key]) => paymentIds.includes(key))
        .reduce((sum, [, amount]) => sum + Number(amount || 0), 0);
      const remainingDue = Math.max(0, selectedPaymentAmountDue - selectedTotal);
      const availableAmount = Number(payment?.availableAmount || 0);
      paymentAmounts[id] = Math.min(availableAmount, remainingDue || availableAmount).toFixed(2);
    }
    const cappedPaymentAmounts = capCreditApplyPaymentAmountsToDue(paymentAmounts, paymentIds, selectedPaymentAmountDue);
    const creditTotal = syncCreditApplyFormAmount(cappedPaymentAmounts, paymentIds);
    setAllocations((values) => syncCreditApplyAllocationsToTotal(values, creditTotal, selectedCreditApplyTotal));
    return {...current, paymentIds, paymentAmounts: cappedPaymentAmounts, error:''};
  });
  const setCreditApplyPaymentAmount = (paymentId, value) => setCreditApplyModal((current) => {
    const id = String(paymentId);
    const payment = walletPayments.find((item) => String(item.id) === id);
    const maxAmount = Number(payment?.availableAmount || 0);
    const numericValue = Number(value || 0);
    const nextValue = value === ''
      ? ''
      : (numericValue < 0 ? '0' : (numericValue > maxAmount ? maxAmount.toFixed(2) : value));
    const selectedIds = Array.isArray(current.paymentIds) ? current.paymentIds.map(String) : [];
    const paymentAmounts = { ...(current.paymentAmounts || {}), [id]: nextValue };
    const creditTotal = syncCreditApplyFormAmount(paymentAmounts, selectedIds);
    setAllocations((values) => syncCreditApplyAllocationsToTotal(values, creditTotal, selectedCreditApplyTotal));
    return {...current, paymentAmounts, error:''};
  });
  const saveCreditApplication = async (event) => {
    event.preventDefault();
    if (!canSaveCreditApplication) {
      setCreditApplyModal((current) => ({...current,error:creditApplyAllocationWarning || 'Select credits and enter invoice applications before saving.'}));
      return;
    }
    let paymentApplications = [];
    try {
      paymentApplications = buildPaymentApplications(await expandedPaymentAllocationRows(), selectedCreditApplyPayments);
    } catch (error) {
      setCreditApplyModal((current) => ({...current,error:error.message || 'Could not apply the selected credits.'}));
      return;
    }
    setCreditApplyModal((current) => ({...current,loading:true,error:''}));
    try {
      await api.post('/payments/apply-many', { payments: paymentApplications });
      const returnTab = creditApplyModal.returnTab || tab;
      closeCreditApplyModal();
      await loadCustomer(selected);
      setTab(returnTab);
      setStatus(`Payment credits applied to invoices.`);
    } catch (error) {
      setCreditApplyModal((current) => ({...current,loading:false,error:error.response?.data?.error || 'Could not apply payment credits.'}));
    }
  };

  const move = (payment) => {
    if (!canEditPayment) {
      setStatus('You do not have permission to edit payments.');
      return;
    }
    setMoveModal({open:true,payment,query:'',target:null,error:'',loading:false}); setMoveLookupOpen(true); setMoveLookupIndex(0);
  };
  const selectMoveCustomer = (customer) => { setMoveModal((current)=>({...current,query:customerName(customer),target:customer,error:''})); setMoveLookupOpen(false); setMoveLookupIndex(0); };
  const handleMoveLookupKeyDown = (event) => {
    if (event.key==='ArrowDown') { event.preventDefault(); setMoveLookupOpen(true); setMoveLookupIndex((index)=>Math.min(index+1,Math.max(moveLookupOptions.length-1,0))); }
    else if (event.key==='ArrowUp') { event.preventDefault(); setMoveLookupIndex((index)=>Math.max(index-1,0)); }
    else if (event.key==='Escape') { event.preventDefault(); setMoveLookupOpen(false); }
    else if (event.key==='Enter' && moveLookupOptions[moveLookupIndex]) { event.preventDefault(); selectMoveCustomer(moveLookupOptions[moveLookupIndex]); }
    else if (event.key==='Tab' && moveLookupOpen && moveLookupOptions[moveLookupIndex]) selectMoveCustomer(moveLookupOptions[moveLookupIndex]);
  };
  const submitMove = async (event) => {
    event.preventDefault();
    if (!canEditPayment) return setMoveModal((current)=>({...current,error:'You do not have permission to edit payments.'}));
    if (!moveModal.target) return setMoveModal((current)=>({...current,error:'Select the destination customer from the lookup.'}));
    setMoveModal((current)=>({...current,loading:true,error:''}));
    try { await api.post(`/payments/${moveModal.payment.id}/move`, {customerId:customerId(moveModal.target)}); setMoveModal({open:false,payment:null,query:'',target:null,error:'',loading:false}); await loadCustomer(selected); setStatus(`Payment moved to ${customerName(moveModal.target)}.`); }
    catch (error) { setMoveModal((current)=>({...current,loading:false,error:error.response?.data?.error||'Move failed.'})); }
  };

  const openCustomInvoice = (documentType = 'invoice') => {
    const selectedDocumentType = documentType === 'credit_memo' ? 'credit_memo' : 'invoice';
    if (selectedDocumentType === 'credit_memo' && !canCreateReturn) {
      setStatus('You do not have permission to create credit memos.');
      return;
    }
    if (selectedDocumentType !== 'credit_memo' && !canCreateInvoice) {
      setStatus('You do not have permission to create invoices.');
      return;
    }
    setCustomInvoiceModal({ open:true, ...emptyCustomInvoice(selected), documentType: selectedDocumentType });
    setCustomInvoiceLookupOpen(false);
    setCustomInvoiceLookupIndex(0);
    setStatus('');
  };
  const closeCustomInvoice = () => {
    setCustomInvoiceModal({ open:false, ...emptyCustomInvoice() });
    setCustomInvoiceLookupOpen(false);
    setCustomInvoiceLookupIndex(0);
  };
  const selectCustomInvoiceCustomer = (customer) => {
    setCustomInvoiceModal((current)=>({...current,customer,query:customerName(customer),error:''}));
    setCustomInvoiceLookupOpen(false);
    setCustomInvoiceLookupIndex(0);
  };
  const updateCustomInvoiceItem = (index, field, value) => {
    setCustomInvoiceModal((current) => ({
      ...current,
      error: '',
      items: current.items.map((item, itemIndex) => (itemIndex === index ? {...item, [field]: value} : item)),
    }));
  };
  const addCustomInvoiceItem = () => {
    setCustomInvoiceModal((current) => ({...current, error: '', items: [...current.items, emptyCustomInvoiceItem()]}));
  };
  const removeCustomInvoiceItem = (index) => {
    setCustomInvoiceModal((current) => ({
      ...current,
      error: '',
      items: current.items.length > 1 ? current.items.filter((_, itemIndex) => itemIndex !== index) : [emptyCustomInvoiceItem()],
    }));
  };
  const handleCustomInvoiceLookupKeyDown = (event) => {
    if (event.key==='ArrowDown') { event.preventDefault(); setCustomInvoiceLookupOpen(true); setCustomInvoiceLookupIndex((index)=>Math.min(index+1,Math.max(customInvoiceLookupOptions.length-1,0))); }
    else if (event.key==='ArrowUp') { event.preventDefault(); setCustomInvoiceLookupIndex((index)=>Math.max(index-1,0)); }
    else if (event.key==='Escape') { event.preventDefault(); setCustomInvoiceLookupOpen(false); }
    else if (event.key==='Enter' && customInvoiceLookupOptions[customInvoiceLookupIndex]) { event.preventDefault(); selectCustomInvoiceCustomer(customInvoiceLookupOptions[customInvoiceLookupIndex]); }
    else if (event.key==='Tab' && customInvoiceLookupOpen && customInvoiceLookupOptions[customInvoiceLookupIndex]) selectCustomInvoiceCustomer(customInvoiceLookupOptions[customInvoiceLookupIndex]);
  };
  const submitCustomInvoice = async (event) => {
    event.preventDefault();
    const isCreditMemo = customInvoiceModal.documentType === 'credit_memo';
    const documentLabel = isCreditMemo ? 'credit memo' : 'invoice';
    if ((isCreditMemo && !canCreateReturn) || (!isCreditMemo && !canCreateInvoice)) {
      return setCustomInvoiceModal((current)=>({...current,error:`You do not have permission to create ${documentLabel}s.`}));
    }
    if (!customInvoiceModal.customer) return setCustomInvoiceModal((current)=>({...current,error:'Select a customer from the lookup.'}));
    const items = customInvoiceModal.items.map((item) => {
      const rate = parseCustomInvoiceRateInput(item.rate);
      const baseAmount = parseCustomInvoiceAmountInput(item.amount);
      return {
        item: item.item.trim(),
        description: item.description.trim(),
        rate: rate.value,
        rateValue: rate.value,
        priceType: rate.priceType,
        baseAmount,
        amount: customInvoiceLineTotal(item),
      };
    }).filter((item) => item.item || item.description || item.rate || item.baseAmount);
    if (!items.length) return setCustomInvoiceModal((current)=>({...current,error:`Add at least one ${documentLabel} item.`}));
    if (items.some((item) => !item.item || !item.description || !Number.isFinite(item.rateValue) || item.rateValue <= 0 || !Number.isFinite(item.baseAmount) || item.baseAmount <= 0 || !Number.isFinite(item.amount) || item.amount <= 0)) {
      return setCustomInvoiceModal((current)=>({...current,error:`Each ${documentLabel} item needs an item, description, rate, and amount greater than zero.`}));
    }
    setCustomInvoiceModal((current)=>({...current,loading:true,error:''}));
    try {
      const { data } = await api.post(isCreditMemo ? '/payments/custom-credit-memo' : '/payments/custom-invoice', {
        customerId: customerId(customInvoiceModal.customer),
        items,
      });
      const targetCustomer = customInvoiceModal.customer;
      closeCustomInvoice();
      await loadCustomer(targetCustomer);
      setTab(isCreditMemo ? 'payments' : 'invoices');
      if (isCreditMemo) setPaymentTypeFilter('Credit Memo');
      const emailStatus = data.autoEmail?.status === 'sent'
        ? ` PDF confirmation emailed to ${data.autoEmail.to}.`
        : data.autoEmail?.status === 'failed' ? ` ${data.autoEmail.message}` : '';
      setStatus(`${isCreditMemo ? 'Custom credit memo' : 'Custom invoice'} added for ${customerName(targetCustomer)}.${emailStatus}`);
    } catch (error) {
      setCustomInvoiceModal((current)=>({...current,loading:false,error:error.response?.data?.error||`Could not add custom ${documentLabel}.`}));
    }
  };
  const openDeleteInvoiceConfirm = (invoice) => {
    if (!canDeleteInvoice) {
      setStatus('You do not have permission to delete invoices.');
      return;
    }
    setDeleteInvoiceConfirm({ open:true, invoice, value:'', error:'', loading:false });
  };
  const closeDeleteInvoiceConfirm = () => setDeleteInvoiceConfirm({ open:false, invoice:null, value:'', error:'', loading:false });
  const submitDeleteInvoice = async (event) => {
    event.preventDefault();
    if (!canDeleteInvoice) {
      setDeleteInvoiceConfirm((current)=>({...current,error:'You do not have permission to delete invoices.'}));
      return;
    }
    const targetInvoice = deleteInvoiceConfirm.invoice || {};
    const expected = invoiceDeleteNumber(targetInvoice);
    if (String(deleteInvoiceConfirm.value || '').trim() !== expected) {
      setDeleteInvoiceConfirm((current)=>({...current,error:`Type invoice number ${expected} to delete this invoice.`}));
      return;
    }
    setDeleteInvoiceConfirm((current)=>({...current,loading:true,error:''}));
    try {
      const endpoint = targetInvoice.customInvoice
        ? `/payments/custom-invoice/${encodeURIComponent(expected)}`
        : `/transfers/shipping/${encodeURIComponent(expected)}`;
      const { data: deleteResult } = await api.delete(endpoint);
      closeDeleteInvoiceConfirm();
      if (String(invoicePreviewModal.invoice?.docNo || invoicePreviewModal.invoice?.invoiceNo || invoicePreviewModal.invoice?.id || '').trim() === expected) {
        closeInvoicePreviewModal();
      }
      await loadCustomer(selected);
      if (leftTab === 'transactions') {
        await loadTransactions({ page: transactionPageInfo.page || 1 });
      }
      setTab('invoices');
      const returnedPaymentAmount = Number(deleteResult?.returnedPaymentAmount || 0);
      setStatus(`Invoice #${expected} deleted. Applied payments were returned to available balance.${returnedPaymentAmount > 0 ? ` Returned ${currency(returnedPaymentAmount)}.` : ''}`);
    } catch (error) {
      setDeleteInvoiceConfirm((current)=>({...current,loading:false,error:error.response?.data?.error||'Could not delete invoice.'}));
    }
  };

  const showActivity = async (payment) => {
    try { const { data } = await api.get(`/payments/${payment.id}/activity`); setActivity({ payment, rows: data || [] }); }
    catch { setStatus('Could not load payment activity.'); }
  };
  const activityLogIsCreditMemo = isCreditMemoPayment(activity.payment);
  const activityDetailValue = (value) => {
    const text = String(value || '').trim();
    return text || '-';
  };
  const showInvoiceHistory = async (invoice) => {
    const documentType = Number(invoice.transactionTypeId) === 640 ? 'repair' : 'invoice';
    setInvoiceHistory({ invoice, rows: [], loading: true, error: '' });
    try {
      const { data } = await api.get(`/payments/documents/${documentType}/${encodeURIComponent(invoice.id)}/history`);
      setInvoiceHistory({ invoice, rows: data || [], loading: false, error: '' });
    } catch (error) {
      setInvoiceHistory({ invoice, rows: [], loading: false, error: error.response?.data?.error || `Could not load transaction history for invoice ${invoice.invoiceNo || invoice.docNo || invoice.id}.` });
    }
  };
  const openInvoicePreviewByReference = async (documentIdValue, documentTypeValue = 'invoice') => {
    const documentId = String(documentIdValue || '').trim();
    if (!documentId) return setStatus('Invoice number is not available for this transaction.');
    const isRepair = documentTypeValue === 'repair' || /^RO-/i.test(documentId);
    const normalizedRepairId = isRepair && !/^RO-/i.test(documentId) ? `RO-${documentId}` : documentId;
    const loadedInvoice = invoices.find((invoice) => (
      String(invoice.id) === documentId
      || String(invoice.id) === normalizedRepairId
      || String(invoice.docNo) === documentId
      || String(invoice.docNo) === normalizedRepairId
      || String(invoice.invoiceNo) === documentId
      || String(invoice.invoiceNo) === normalizedRepairId
    ));
    if (loadedInvoice) {
      openInvoicePreviewModal(loadedInvoice);
      return true;
    }
    if (selected) {
      const loaded = await loadCustomer(selected);
      const refreshedInvoice = (loaded?.invoices || []).find((invoice) => (
        String(invoice.id) === documentId
        || String(invoice.id) === normalizedRepairId
        || String(invoice.docNo) === documentId
        || String(invoice.docNo) === normalizedRepairId
        || String(invoice.invoiceNo) === documentId
        || String(invoice.invoiceNo) === normalizedRepairId
      ));
      if (refreshedInvoice) {
        openInvoicePreviewModal(refreshedInvoice);
        return true;
      }
    }
    setStatus(`Could not find invoice ${documentId}.`);
    return false;
  };

  const openPaymentReview = async (payment, options = {}) => {
    if (payment.paymentType === 'Credit Memo' || (payment.readOnly && String(payment.id).startsWith('credit-memo-'))) {
      openCreditMemo(payment);
      return;
    }
    setStatus('');
    try {
      const { data } = await api.get(`/payments/${payment.id}`);
      const allocationRows = data.allocations || [];
      const allocationMap = allocationRows.reduce((current, allocation) => {
        const invoiceKey = allocationKey(allocation.documentId);
        current[invoiceKey] = (Number(current[invoiceKey] || 0) + Number(allocation.amount || 0)).toFixed(2);
        if (!allocation.lineKey) return current;
        const serialNo = allocationSerialNo(allocation.lineKey, allocation.serialNo);
        const key = allocationKey(
          allocation.documentId,
          `${allocation.lineKey}|${serialNo}`
        );
        current[key] = (Number(current[key] || 0) + Number(allocation.amount || 0)).toFixed(2);
        return current;
      }, {});
      const paymentAmount = Number(data.amount ?? data.originalAmount ?? payment.amount ?? payment.originalAmount ?? 0);
      setActivePaymentReview(data);
      setPaymentModalMode('full');
      setPaymentModalHighlightedInvoiceId(options.highlightInvoiceId || null);
      const reviewForm = {
        amount: Number.isFinite(paymentAmount) && paymentAmount > 0 ? paymentAmount.toFixed(2) : '',
        paymentType: data.paymentType || 'Check',
        referenceNo: data.referenceNo || '',
        notes: data.notes || '',
        receivedAt: data.receivedAt ? localDateKey(data.receivedAt) : today(),
      };
      setForm(reviewForm);
      setAllocations(allocationMap);
      setSavedPaymentSnapshot(paymentEditSnapshot(reviewForm, allocationMap));
      setDetails({});
      resetCreditApplyState();
      setApplyModal((current) => ({ ...current, open: false, creditMenuOpen: false }));
      setModal(true);
    } catch (error) {
      setStatus(error.response?.data?.error || 'Could not open payment.');
    }
  };

  const openCreditMemo = async (payment) => {
    const docNo = String(payment.referenceNo || payment.docNo || payment.invoiceNo || payment.id || '').replace(/^credit-memo-/i, '').trim();
    if (!docNo) return setStatus('Credit memo number is not available for this row.');
    setStatus('');
    setCreditMemoModal({ open:true, docNo, transfer:null, invoice:null, loading:true, error:'' });
    try {
      const { data } = await api.get(`/transfers/${encodeURIComponent(docNo)}`, {
        params: { transactionTypeId: 10 },
      });
      setCreditMemoModal({ open:true, docNo, transfer:data, invoice:null, loading:false, error:'' });
    } catch (transferError) {
      try {
        const rowCustomer = customers.find((item)=>String(customerId(item))===String(payment.customerId));
        const modalCustomer = rowCustomer || (String(customerId(selected)) === String(payment.customerId || customerId(selected)) ? selected : null) || {
          id: payment.customerId,
          nccustomerid: payment.customerId,
          name: payment.customerName,
        };
        const params = customerId(modalCustomer) ? { customerId: customerId(modalCustomer) } : {};
        const { data } = await api.get(`/payments/documents/credit_memo/${encodeURIComponent(docNo)}`, { params });
        const originalAmount = Number(data.originalAmount ?? Math.abs(Number(payment.amount || 0)));
        const amountDue = Number(data.amountDue ?? Math.abs(Number(payment.unappliedAmount || 0)));
        const creditMemoDocument = importedInvoiceDetailToDocument({
          invoice: {
            id: docNo,
            invoiceNo: docNo,
            docNo,
            date: data.date || payment.date,
            type: 'Credit Memo',
            transactionTypeId: 10,
            originalAmount,
            appliedAmount: Number(data.paidAmount ?? Math.max(0, originalAmount - amountDue)),
            amountDue,
            importedOnly: data.importedOnly,
            importedBalanceAsOf: data.importedBalanceAsOf,
          },
          detail: data,
          customer: modalCustomer,
          businessAddress,
        });
        setCreditMemoModal({ open:true, docNo, transfer:null, invoice:creditMemoDocument, loading:false, error:'' });
      } catch (error) {
        setCreditMemoModal({ open:true, docNo, transfer:null, invoice:null, loading:false, error:error.response?.data?.error || transferError.response?.data?.error || 'Failed to load credit memo.' });
      }
    }
  };
  const closeCreditMemoModal = () => {
    setCreditMemoModal({ open:false, docNo:'', transfer:null, invoice:null, loading:false, error:'' });
    cancelPaymentInvoiceEditor();
  };
  const printCreditMemoDocument = () => {
    const source = document.querySelector('.payment-credit-memo-modal .shipdoc-invoice-workspace');
    printShipDocInvoiceOutput(source, `Credit Memo #${creditMemoModal.transfer?.docNo || creditMemoModal.invoice?.docNo || creditMemoModal.docNo || ''}`);
  };

  const fetchInvoiceDocument = async (invoice) => {
    const isRepairOrder = Number(invoice.transactionTypeId) === 640;
    let document;
    if (isRepairOrder) {
      const documentId = String(invoice.id || invoice.invoiceNo || invoice.docNo || '');
      const { data } = await api.get(`/invoices/${encodeURIComponent(documentId)}`, {
        params: { transactionTypeId: 640 },
      });
      document = data;
    } else {
      const documentId = invoice.docNo || invoice.invoiceNo || invoice.id;
      const { data } = await api.get(`/invoices/${encodeURIComponent(documentId)}`, {
        params: { transactionTypeId: 4 },
      });
      document = { ...data, documentTitle: data.documentTitle || 'Invoice', transactionTypeId: 4 };
    }
    if (selected?.email && !document?.to?.email) {
      document = { ...document, to: { ...(document?.to || {}), email: selected.email } };
    }
    if (businessAddress && !document?.businessAddress) {
      document = { ...document, businessAddress };
    }
    return document;
  };

  const closeInvoicePreviewModal = () => {
    setInvoicePreviewModal({ open: false, invoice: null, detail: null, invoiceDocument: null, loading: false, error: '' });
    setPaymentInvoiceEditor({ open: false, docNo: '', draft: null, saving: false, error: '' });
    setInvoiceCustomerLookupOpen(false);
    setInvoiceCustomerLookupIndex(0);
  };
  const cancelPaymentInvoiceEditor = () => {
    setPaymentInvoiceEditor({ open: false, docNo: '', draft: null, saving: false, error: '' });
    setInvoiceCustomerLookupOpen(false);
    setInvoiceCustomerLookupIndex(0);
  };
  const openInvoicePreviewModal = async (invoice) => {
    setStatus('');
    setInvoicePreviewModal({ open: true, invoice, detail: null, invoiceDocument: null, loading: true, error: '' });
    try {
      const documentType = Number(invoice.transactionTypeId) === 640 ? 'repair' : 'invoice';
      const { data } = await api.get(`/payments/documents/${documentType}/${encodeURIComponent(invoice.id)}`, { params: documentDetailParams() });
      let invoiceDocument = null;
      if (!invoice.customInvoice && !invoice.importedOnly && !data.importedOnly) {
        try {
          invoiceDocument = await fetchInvoiceDocument(invoice);
        } catch (documentError) {
          invoiceDocument = null;
        }
      }
      setInvoicePreviewModal((current) => ({ ...current, detail: data, invoiceDocument, loading: false }));
    } catch (error) {
      setInvoicePreviewModal((current) => ({
        ...current,
        loading: false,
        error: error.response?.data?.error || `Could not open invoice ${invoice.invoiceNo || invoice.docNo || invoice.id}.`,
      }));
    }
  };

  const paymentInvoiceCanEditTax = (invoice, document) => (
    canEditPaymentInvoice(invoice, document)
      && Number(invoice?.transactionTypeId ?? document?.transactionTypeId) === 4
      && (!invoice?.importedOnly || invoice?.customInvoice)
      && (!document?.importedOnly || document?.customInvoice)
  );
  const paymentInvoiceIsRepair = (invoice, document) => Number(invoice?.transactionTypeId ?? document?.transactionTypeId) === 640;
  const paymentInvoiceCanEdit = (invoice, document) => paymentInvoiceCanEditTax(invoice, document) || (
    paymentInvoiceIsRepair(invoice, document) && document?.repairOrderStatus === 'Closed' && canEditPaymentInvoice(invoice, document)
  );
  const paymentInvoiceCanEditServices = paymentInvoiceCanEdit;
  const paymentInvoiceEditorIsRepair = paymentInvoiceIsRepair(invoicePreviewModal.invoice, printableInvoicePreviewDocument);
  const invoiceServicesAreRepair = paymentInvoiceIsRepair(invoiceServiceModal.source === 'apply' ? applyModal.invoice : invoicePreviewModal.invoice, invoiceServiceModal.source === 'apply' ? printableApplyInvoiceDocument : printableInvoicePreviewDocument);
  const invoiceServiceOptions = otherServiceOptions;
  const invoiceServiceButtonLabel = (document) => (
    Number(document?.transactionTypeId) === 640 ? ((document?.otherServices || []).length ? 'Edit Services' : 'Add Services') : ((document?.otherServices || []).length ? 'Edit Services/Discounts' : 'Add Services/Discounts')
  );
  const paymentInvoiceEditorIsCustom = Boolean(invoicePreviewModal.invoice?.customInvoice || printableInvoicePreviewDocument?.customInvoice);
  const newPaymentInvoiceDraftItem = () => {
    const id = `custom-invoice-line-new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    return {
      id,
      lineKey: id,
      item: '',
      description: '',
      shipDocItemIds: [],
      quantity: 1,
      amount: '0.00',
      rate: '0.00',
    };
  };

  const findInvoiceEditorCustomer = (document) => {
    const id = String(document?.customerId || invoicePreviewModal.invoice?.customerId || '');
    return customers.find((customer) => String(customerId(customer)) === id)
      || (selected && String(customerId(selected)) === id ? selected : null)
      || selected
      || null;
  };

  const openPaymentInvoiceEditor = () => {
    if (!paymentInvoiceCanEdit(invoicePreviewModal.invoice, printableInvoicePreviewDocument)) return;
    const draft = shippingInvoiceEditDraftFromDocument(printableInvoicePreviewDocument, preferencesTaxRate);
    if (!draft.docNo) return;
    const draftCustomer = findInvoiceEditorCustomer(printableInvoicePreviewDocument);
    setPaymentInvoiceEditor({
      open: true,
      docNo: draft.docNo,
      draft: {
        ...draft,
        customer: draftCustomer,
        customerId: customerId(draftCustomer) || printableInvoicePreviewDocument?.customerId || invoicePreviewModal.invoice?.customerId || '',
        customerQuery: draftCustomer ? customerName(draftCustomer) : (printableInvoicePreviewDocument?.billTo?.name || printableInvoicePreviewDocument?.customerName || ''),
        customerName: draftCustomer ? customerName(draftCustomer) : (printableInvoicePreviewDocument?.customerName || printableInvoicePreviewDocument?.billTo?.name || ''),
        billTo: draftCustomer ? customerInvoiceAddress(draftCustomer) : (printableInvoicePreviewDocument?.billTo || printableInvoicePreviewDocument?.to || null),
      },
      saving: false,
      error: '',
    });
    setInvoiceCustomerLookupOpen(false);
    setInvoiceCustomerLookupIndex(0);
  };

  const selectPaymentInvoiceCustomer = (customer) => {
    setPaymentInvoiceEditor((current) => ({
      ...current,
      error: '',
      draft: current.draft ? {
        ...current.draft,
        customer,
        customerId: customerId(customer),
        customerQuery: customerName(customer),
        customerName: customerName(customer),
        billTo: customerInvoiceAddress(customer),
      } : current.draft,
    }));
    setInvoiceCustomerLookupOpen(false);
    setInvoiceCustomerLookupIndex(0);
  };

  const updatePaymentInvoiceCustomerQuery = (value) => {
    setPaymentInvoiceEditor((current) => ({
      ...current,
      error: '',
      draft: current.draft ? {
        ...current.draft,
        customer: null,
        customerId: '',
        customerQuery: value,
      } : current.draft,
    }));
    setInvoiceCustomerLookupOpen(true);
    setInvoiceCustomerLookupIndex(0);
  };

  const handlePaymentInvoiceCustomerLookupKeyDown = (event) => {
    if (event.key==='ArrowDown') { event.preventDefault(); setInvoiceCustomerLookupOpen(true); setInvoiceCustomerLookupIndex((index)=>Math.min(index+1,Math.max(invoiceCustomerLookupOptions.length-1,0))); }
    else if (event.key==='ArrowUp') { event.preventDefault(); setInvoiceCustomerLookupIndex((index)=>Math.max(index-1,0)); }
    else if (event.key==='Escape') { event.preventDefault(); setInvoiceCustomerLookupOpen(false); }
    else if (event.key==='Enter' && invoiceCustomerLookupOptions[invoiceCustomerLookupIndex]) { event.preventDefault(); selectPaymentInvoiceCustomer(invoiceCustomerLookupOptions[invoiceCustomerLookupIndex]); }
    else if (event.key==='Tab' && invoiceCustomerLookupOpen && invoiceCustomerLookupOptions[invoiceCustomerLookupIndex]) selectPaymentInvoiceCustomer(invoiceCustomerLookupOptions[invoiceCustomerLookupIndex]);
  };

  const updatePaymentInvoiceDraftItem = (itemId, field, value) => {
    setPaymentInvoiceEditor((current) => {
      if (!current.draft) return current;
      const items = current.draft.items.map((item) => {
        if (String(item.id) !== String(itemId)) return item;
        if (field === 'item' || field === 'description') {
          return { ...item, [field]: value };
        }
        const quantity = Math.abs(Number(item.quantity) || 0) || 1;
        if (field === 'amount') {
          const amountNumber = Number(value);
          return {
            ...item,
            amount: value,
            rate: Number.isFinite(amountNumber) ? String((Math.round((amountNumber / quantity) * 100) / 100).toFixed(2)) : item.rate,
          };
        }
        const rateNumber = Number(value);
        return {
          ...item,
          rate: value,
          amount: Number.isFinite(rateNumber) ? String((Math.round((rateNumber * quantity) * 100) / 100).toFixed(2)) : item.amount,
        };
      });
      return { ...current, error: '', draft: { ...current.draft, items } };
    });
  };

  const addPaymentInvoiceDraftItem = () => {
    setPaymentInvoiceEditor((current) => ({
      ...current,
      error: '',
      draft: current.draft ? {
        ...current.draft,
        items: [...(current.draft.items || []), newPaymentInvoiceDraftItem()],
      } : current.draft,
    }));
  };

  const removePaymentInvoiceDraftItem = (itemId) => {
    setPaymentInvoiceEditor((current) => {
      if (!current.draft) return current;
      const items = (current.draft.items || []).filter((item) => String(item.id) !== String(itemId));
      if (!items.length) return current;
      return { ...current, error: '', draft: { ...current.draft, items } };
    });
  };

  const updatePaymentInvoiceDraftMemo = (memo) => {
    setPaymentInvoiceEditor((current) => ({
      ...current,
      error: '',
      draft: current.draft ? { ...current.draft, memo } : current.draft,
    }));
  };

  const updatePaymentInvoiceDraftTaxRate = (taxRate) => {
    setPaymentInvoiceEditor((current) => ({
      ...current,
      error: '',
      draft: current.draft ? { ...current.draft, taxRate } : current.draft,
    }));
  };

  const savePaymentInvoiceEditor = async () => {
    const docNo = paymentInvoiceEditor.docNo;
    if (!docNo || !paymentInvoiceEditor.draft) return;
    const targetCustomerId = paymentInvoiceEditor.draft.customerId || customerId(paymentInvoiceEditor.draft.customer);
    if (!targetCustomerId) {
      setPaymentInvoiceEditor((current) => ({ ...current, error: 'Select a customer from the Bill To lookup.' }));
      return;
    }
    const permissionResource = {
      ...invoiceEditPermissionResource(invoicePreviewModal.invoice, printableInvoicePreviewDocument),
      customerId: targetCustomerId,
      customerTypeName: paymentInvoiceEditor.draft.customer?.customerTypeName
        || paymentInvoiceEditor.draft.customer?.customer_type_name
        || customerTypeLabel(paymentInvoiceEditor.draft.customer, customerTypes),
    };
    if (!can(user, 'invoices.edit', permissionResource)) {
      setPaymentInvoiceEditor((current) => ({ ...current, error: 'You do not have permission to edit invoices for this customer.' }));
      return;
    }
    setPaymentInvoiceEditor((current) => ({ ...current, saving: true, error: '' }));
    setStatus('');
    try {
      await api.patch(`/invoices/${encodeURIComponent(docNo)}`, paymentInvoiceEditorIsRepair ? {
        customerId: targetCustomerId, transactionTypeId: 640, documentType: 'repair',
        items: paymentInvoiceEditor.draft.items.map(item => ({ id: item.lineKey, rate: item.rate })),
      } : {
        customerId: targetCustomerId,
        documentType: permissionResource.documentType,
        transactionTypeId: permissionResource.transactionTypeId,
        items: paymentInvoiceEditor.draft.items
          .filter((item) => !item.syntheticQuickBooksLine)
          .map((item) => ({
            id: item.id,
            lineKey: item.lineKey,
            lineNo: item.lineNo,
            itemId: item.itemId,
            item: item.item,
            description: item.description,
            shipDocItemIds: item.shipDocItemIds,
            quantity: item.quantity,
            amount: item.amount,
            rate: item.rate,
          })),
        memo: paymentInvoiceEditor.draft.memo,
        taxRate: paymentInvoiceEditor.draft.taxRate,
      });
      await refreshPaymentInvoiceAfterTax('preview', invoicePreviewModal.invoice);
      if (leftTab === 'transactions') {
        await loadTransactions({ page: transactionPageInfo.page || 1 });
      }
      if (paymentInvoiceEditor.draft.customer) {
        await loadCustomer(paymentInvoiceEditor.draft.customer);
      }
      cancelPaymentInvoiceEditor();
      setStatus(`Invoice #${docNo} saved.`);
    } catch (error) {
      setPaymentInvoiceEditor((current) => ({
        ...current,
        saving: false,
        error: error.response?.data?.error
          || error.response?.data?.message
          || (error.response?.status ? `Failed to save invoice. Server returned ${error.response.status}.` : '')
          || error.message
          || 'Failed to save invoice.',
      }));
    }
  };

  const openPaymentCreditMemoEditor = () => {
    const document = printableCreditMemoDocument;
    if (!document || Number(document.transactionTypeId) !== 10) return;
    const draft = shippingInvoiceEditDraftFromDocument(document, preferencesTaxRate);
    const docNo = draft.docNo || document.docNo || document.invoiceNo || creditMemoModal.docNo;
    if (!docNo) return;
    setPaymentInvoiceEditor({ open: true, docNo: String(docNo), draft, saving: false, error: '' });
  };

  const savePaymentCreditMemoEditor = async () => {
    const docNo = paymentInvoiceEditor.docNo;
    if (!docNo || !paymentInvoiceEditor.draft) return;
    setPaymentInvoiceEditor((current) => ({ ...current, saving: true, error: '' }));
    setStatus('');
    try {
      await api.patch(`/invoices/${encodeURIComponent(docNo)}`, {
        transactionTypeId: 10,
        memo: paymentInvoiceEditor.draft.memo,
      });
      await openCreditMemo({
        docNo,
        referenceNo: docNo,
        customerId: selected ? customerId(selected) : '',
      });
      if (leftTab === 'transactions') {
        await loadTransactions({ page: transactionPageInfo.page || 1 });
      }
      cancelPaymentInvoiceEditor();
      setStatus(`Credit Memo #${docNo} saved.`);
    } catch (error) {
      setPaymentInvoiceEditor((current) => ({
        ...current,
        saving: false,
        error: error.response?.data?.error
          || error.response?.data?.message
          || (error.response?.status ? `Failed to save credit memo. Server returned ${error.response.status}.` : '')
          || error.message
          || 'Failed to save credit memo.',
      }));
    }
  };

  const openTaxEditor = (source) => {
    const document = source === 'apply' ? printableApplyInvoiceDocument : printableInvoicePreviewDocument;
    const invoice = source === 'apply' ? applyModal.invoice : invoicePreviewModal.invoice;
    if (!canEditPaymentInvoice(invoice, document)) {
      setStatus('You do not have permission to edit invoices.');
      return;
    }
    if (!paymentInvoiceCanEditTax(invoice, document)) return;
    setTaxEditor({
      open: true,
      source,
      value: Number(document?.taxAmount || 0) > 0 && preferencesTaxRate > 0 ? String(preferencesTaxRate) : '0',
      saving: false,
      error: '',
    });
  };

  const closeTaxEditor = () => {
    if (taxEditor.saving) return;
    setTaxEditor({ open: false, source: '', value: '0', saving: false, error: '' });
  };

  const openInvoiceServiceModal = (source) => {
    const document = source === 'apply' ? printableApplyInvoiceDocument : printableInvoicePreviewDocument;
    const invoice = source === 'apply' ? applyModal.invoice : invoicePreviewModal.invoice;
    if (!canEditPaymentInvoice(invoice, document)) {
      setStatus('You do not have permission to edit invoices.');
      return;
    }
    if (!paymentInvoiceCanEditServices(invoice, document)) return;
    const selectedServices = (document?.otherServices || []).filter((service) => service.serviceId);
    const selectedDiscounts = (document?.otherServices || []).filter((service) => service.discountId);
    setInvoiceServiceModal({
      open: true,
      source,
      activeTab: 'services',
      serviceIds: selectedServices.map((service) => String(service.serviceId || '')).filter(Boolean),
      discountIds: selectedDiscounts.map((service) => String(service.discountId || '')).filter(Boolean),
      serviceValues: Object.fromEntries(selectedServices.map((service) => [
        String(service.serviceId),
        adjustmentInputValue(service.servicePrice ?? service.price, service.servicePriceType || service.priceType),
      ])),
      serviceBaseAmounts: Object.fromEntries(selectedServices
        .filter((service) => isExplicitBaseServiceName(service.name))
        .map((service) => [
          String(service.serviceId),
          service.serviceBaseAmount ?? service.baseAmount ?? '',
        ])),
      discountValues: Object.fromEntries(selectedDiscounts.map((service) => [
        String(service.discountId),
        adjustmentInputValue(service.servicePrice ?? service.price, service.servicePriceType || service.priceType),
      ])),
      saving: false,
      error: '',
    });
  };

  const closeInvoiceServiceModal = () => {
    if (invoiceServiceModal.saving) return;
    setInvoiceServiceModal({ open: false, source: '', activeTab: 'services', serviceIds: [], discountIds: [], serviceValues: {}, serviceBaseAmounts: {}, discountValues: {}, saving: false, error: '' });
  };

  const setInvoiceServiceModalTab = (activeTab) => {
    setInvoiceServiceModal((current) => ({ ...current, activeTab }));
  };

  const toggleInvoiceService = (serviceId) => {
    const id = String(serviceId);
    const service = otherServiceOptions.find((option) => getServiceKey(option) === id);
    setInvoiceServiceModal((current) => ({
      ...current,
      serviceIds: current.serviceIds.includes(id)
        ? current.serviceIds.filter((currentId) => currentId !== id)
        : [...current.serviceIds, id],
      serviceValues: current.serviceIds.includes(id)
        ? Object.fromEntries(Object.entries(current.serviceValues || {}).filter(([key]) => key !== id))
        : { ...(current.serviceValues || {}), [id]: current.serviceValues?.[id] ?? serviceAdjustmentInput(service) },
      serviceBaseAmounts: current.serviceIds.includes(id)
        ? Object.fromEntries(Object.entries(current.serviceBaseAmounts || {}).filter(([key]) => key !== id))
        : { ...(current.serviceBaseAmounts || {}), [id]: current.serviceBaseAmounts?.[id] ?? '' },
      error: '',
    }));
  };

  const toggleInvoiceDiscount = (discountId) => {
    const id = String(discountId);
    const discount = discounts.find((option) => getDiscountKey(option) === id);
    setInvoiceServiceModal((current) => ({
      ...current,
      discountIds: current.discountIds.includes(id)
        ? current.discountIds.filter((currentId) => currentId !== id)
        : [...current.discountIds, id],
      discountValues: current.discountIds.includes(id)
        ? Object.fromEntries(Object.entries(current.discountValues || {}).filter(([key]) => key !== id))
        : { ...(current.discountValues || {}), [id]: current.discountValues?.[id] ?? discountAdjustmentInput(discount) },
      error: '',
    }));
  };

  const setInvoiceServiceValue = (serviceId, value) => {
    const id = String(serviceId);
    setInvoiceServiceModal((current) => ({
      ...current,
      serviceValues: { ...(current.serviceValues || {}), [id]: value },
      error: '',
    }));
  };

  const setInvoiceServiceBaseAmount = (serviceId, value) => {
    const id = String(serviceId);
    setInvoiceServiceModal((current) => ({
      ...current,
      serviceBaseAmounts: { ...(current.serviceBaseAmounts || {}), [id]: value },
      error: '',
    }));
  };

  const setInvoiceDiscountValue = (discountId, value) => {
    const id = String(discountId);
    setInvoiceServiceModal((current) => ({
      ...current,
      discountValues: { ...(current.discountValues || {}), [id]: value },
      error: '',
    }));
  };

  const refreshPaymentInvoiceAfterTax = async (source, baseInvoice) => {
    const documentId = baseInvoice?.id || baseInvoice?.docNo || baseInvoice?.invoiceNo;
    const isRepair = Number(baseInvoice?.transactionTypeId) === 640 || /^RO-/i.test(String(documentId || ''));
    const documentType = isRepair ? 'repair' : 'invoice';
    const transactionTypeId = isRepair ? 640 : 4;
    const { data: detail } = await api.get(`/payments/documents/${documentType}/${encodeURIComponent(documentId)}`, { params: documentDetailParams() });
    const invoiceDocument = await fetchInvoiceDocument({ ...baseInvoice, id: documentId, docNo: documentId, invoiceNo: documentId, transactionTypeId });
    setDetails((current) => ({ ...current, [String(documentId)]: detail }));
    const nextInvoice = {
      ...baseInvoice,
      originalAmount: Number(detail.originalAmount ?? invoiceDocument.totalAmount ?? baseInvoice?.originalAmount ?? 0),
      amountDue: Number(detail.amountDue ?? baseInvoice?.amountDue ?? 0),
      appliedAmount: Number(detail.paidAmount ?? baseInvoice?.appliedAmount ?? 0),
      totalAmount: Number(detail.originalAmount ?? invoiceDocument.totalAmount ?? baseInvoice?.totalAmount ?? 0),
    };
    if (source === 'apply') {
      setApplyModal((current) => ({ ...current, invoice: nextInvoice, detail, invoiceDocument, amounts: {}, loading: false, error: '' }));
    } else {
      setInvoicePreviewModal((current) => ({ ...current, invoice: nextInvoice, detail, invoiceDocument, loading: false, error: '' }));
    }
    await loadCustomer(selected, { tab });
  };

  const saveInvoiceTax = async (event) => {
    event.preventDefault();
    const source = taxEditor.source;
    const invoice = source === 'apply' ? applyModal.invoice : invoicePreviewModal.invoice;
    const document = source === 'apply' ? printableApplyInvoiceDocument : printableInvoicePreviewDocument;
    const permissionResource = invoiceEditPermissionResource(invoice, document);
    if (!canEditPaymentInvoice(invoice, document)) {
      setTaxEditor((current) => ({ ...current, error: 'You do not have permission to edit invoices.' }));
      return;
    }
    const docNo = invoice?.docNo || invoice?.invoiceNo || invoice?.id || document?.invoiceNo;
    if (!docNo || !paymentInvoiceCanEditTax(invoice, document)) return;
    setTaxEditor((current) => ({ ...current, saving: true, error: '' }));
    setStatus('');
    try {
      await api.patch(`/invoices/${encodeURIComponent(docNo)}/tax`, {
        customerId: permissionResource.customerId,
        taxAmount: taxAmountForRate(document, taxEditor.value),
        documentType: permissionResource.documentType,
        transactionTypeId: permissionResource.transactionTypeId,
      });
      await refreshPaymentInvoiceAfterTax(source, invoice);
      setTaxEditor({ open: false, source: '', value: '0', saving: false, error: '' });
      setStatus(`Invoice #${docNo} tax updated.`);
    } catch (error) {
      setTaxEditor((current) => ({
        ...current,
        saving: false,
        error: error.response?.data?.error || error.message || 'Failed to update invoice tax.',
      }));
    }
  };

  const saveInvoiceServices = async () => {
    const source = invoiceServiceModal.source;
    const invoice = source === 'apply' ? applyModal.invoice : invoicePreviewModal.invoice;
    const document = source === 'apply' ? printableApplyInvoiceDocument : printableInvoicePreviewDocument;
    const permissionResource = invoiceEditPermissionResource(invoice, document);
    if (!canEditPaymentInvoice(invoice, document)) {
      setInvoiceServiceModal((current) => ({ ...current, error: 'You do not have permission to edit invoices.' }));
      return;
    }
    const docNo = invoice?.docNo || invoice?.invoiceNo || invoice?.id || document?.invoiceNo;
    if (!docNo || !paymentInvoiceCanEditServices(invoice, document)) return;
    setInvoiceServiceModal((current) => ({ ...current, saving: true, error: '' }));
    setStatus('');
    try {
      const serviceAdjustments = invoiceServiceModal.serviceIds.map((id) => {
        const service = otherServiceOptions.find((option) => getServiceKey(option) === String(id));
        const parsed = parseAdjustmentInput(invoiceServiceModal.serviceValues?.[String(id)] ?? serviceAdjustmentInput(service), service?.defaultPriceType || service?.priceType);
        const usesExplicitBase = isExplicitBaseServiceName(service?.name);
        const baseAmount = usesExplicitBase ? parseOptionalMoneyInput(invoiceServiceModal.serviceBaseAmounts?.[String(id)], `${service?.name || 'Service'} amount`) : null;
        if (usesExplicitBase && parsed.priceType === 'percent' && baseAmount === null) {
          throw new Error(`Enter the ${service?.name || 'service'} amount before saving.`);
        }
        return { serviceId: Number(id), ...parsed, ...(baseAmount !== null ? { baseAmount } : {}) };
      }).filter((item) => Number.isFinite(item.serviceId));
      const discountAdjustments = invoiceServiceModal.discountIds.map((id) => {
        const discount = discounts.find((option) => getDiscountKey(option) === String(id));
        const parsed = parseAdjustmentInput(invoiceServiceModal.discountValues?.[String(id)] ?? discountAdjustmentInput(discount), discount?.priceType || discount?.price_type);
        return { discountId: Number(id), ...parsed };
      }).filter((item) => Number.isFinite(item.discountId));
      await api.patch(`/invoices/${encodeURIComponent(docNo)}/other-services`, {
        serviceIds: invoiceServiceModal.serviceIds.map(Number).filter(Number.isFinite),
        discountIds: invoiceServiceModal.discountIds.map(Number).filter(Number.isFinite),
        serviceAdjustments,
        discountAdjustments,
        customerId: permissionResource.customerId,
        documentType: permissionResource.documentType,
        transactionTypeId: permissionResource.transactionTypeId,
      });
      await refreshPaymentInvoiceAfterTax(source, invoice);
      setInvoiceServiceModal({ open: false, source: '', activeTab: 'services', serviceIds: [], discountIds: [], serviceValues: {}, serviceBaseAmounts: {}, discountValues: {}, saving: false, error: '' });
      setStatus(`Invoice #${docNo} ${invoiceServicesAreRepair ? 'services' : 'services/discounts'} updated.`);
    } catch (error) {
      setInvoiceServiceModal((current) => ({
        ...current,
        saving: false,
        error: error.response?.data?.error || error.message || 'Failed to update invoice services.',
      }));
    }
  };

  const openTransactionRow = async (transaction) => {
    if (transaction.transactionType === 'Credit Memo') {
      openCreditMemo(transaction);
      return;
    }
    if (transaction.transactionType === 'Invoice') {
      const documentNo = transaction.referenceNo || transaction.paymentId || transaction.id;
      const isRepairInvoice = /^RO-/i.test(String(documentNo || ''));
      const customer = customers.find((item)=>String(customerId(item))===String(transaction.customerId)) || {
        id: transaction.customerId,
        nccustomerid: transaction.customerId,
        name: transaction.customerName,
      };
      if (!customerId(customer)) return setStatus('Could not find the customer for this invoice.');
      const loaded = await loadCustomer(customer);
      if (!loaded) return;
      setTab('invoices');
      const loadedInvoice = (loaded.invoices || []).find((invoice) => String(invoice.id) === String(documentNo))
        || (loaded.invoices || []).find((invoice) => String(invoice.docNo) === String(documentNo))
        || (loaded.invoices || []).find((invoice) => String(invoice.invoiceNo) === String(documentNo));
      const invoiceForPreview = loadedInvoice || {
        id: documentNo,
        docNo: documentNo,
        invoiceNo: documentNo,
        transactionTypeId: isRepairInvoice ? 640 : 4,
        type: isRepairInvoice ? 'Repair Order' : 'Shipping',
        date: transaction.date,
        originalAmount: Number(transaction.amount || 0),
        appliedAmount: Math.max(0, Number(transaction.amount || 0) - Number(transaction.unappliedAmount || 0)),
        amountDue: Number(transaction.unappliedAmount || 0),
      };
      openInvoicePreviewModal(invoiceForPreview);
      return;
    }
    if (transaction.transactionType === 'Received Payment') {
      if (!transaction.paymentId) return setStatus('Payment number is not available for this transaction.');
      const customer = customers.find((item)=>String(customerId(item))===String(transaction.customerId));
      if (!customer) return setStatus('Could not find the customer for this payment.');
      const loaded = await loadCustomer(customer);
      if (!loaded) return;
      setTab('payments');
      await openPaymentReview({
        id: transaction.paymentId,
        paymentType: transaction.paymentType,
        referenceNo: transaction.referenceNo,
      });
      return;
    }
    const customer=customers.find((item)=>String(customerId(item))===String(transaction.customerId));
    if(customer)loadCustomer(customer);
  };

  const renderTransactionReference = (transaction) => {
    const reference = transaction.referenceNo || `#${transaction.paymentId}`;
    if (!['Invoice','Credit Memo','Received Payment','Voided Payment','Refund'].includes(transaction.transactionType)) return reference;
    return (
      <button
        type="button"
        className="payment-transaction-reference"
        onClick={(event)=>{event.stopPropagation();openTransactionRow(transaction);}}
      >
        {reference}
      </button>
    );
  };
  const renderHistoryPaymentReference = (paymentId) => {
    if (!paymentId) return '-';
    return (
      <button
        type="button"
        className="payment-transaction-reference"
        onClick={(event) => {
          event.stopPropagation();
          setActivity({ payment: null, rows: [] });
          setInvoiceHistory({ invoice: null, rows: [], loading: false, error: '' });
          openPaymentReview({ id: paymentId });
        }}
      >
        #{paymentId}
      </button>
    );
  };
  const renderHistoryInvoiceReference = (documentId, documentType = 'invoice') => {
    if (!documentId) return '-';
    return (
      <button
        type="button"
        className="payment-transaction-reference"
        onClick={(event) => {
          event.stopPropagation();
          setActivity({ payment: null, rows: [] });
          setInvoiceHistory({ invoice: null, rows: [], loading: false, error: '' });
          openInvoicePreviewByReference(documentId, documentType);
        }}
      >
        {documentId}
      </button>
    );
  };


  const printPaymentInvoiceOutput = () => {
    const invoice = printableApplyInvoiceDocument;
    if (!invoice) return;
    const source = document.querySelector('.payment-apply-print-preview .shipdoc-invoice-workspace');
    printShipDocInvoiceOutput(source, `${invoice.documentTitle || invoice.type || 'Invoice'} #${invoice.invoiceNo || ''}`);
  };
  const printInvoicePreviewOutput = () => {
    const invoice = printableInvoicePreviewDocument;
    if (!invoice) return;
    const source = document.querySelector('.payment-invoice-preview-modal .shipdoc-invoice-workspace');
    printShipDocInvoiceOutput(source, `${invoice.documentTitle || invoice.type || 'Invoice'} #${invoice.invoiceNo || ''}`);
  };

  const printPaymentReceiptOutput = async (paymentId) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/payments/${paymentId}/receipt.pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(data);
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;width:1px;height:1px;right:0;bottom:0;border:0;';
      iframe.title = `Payment Receipt #${paymentId}`;
      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      };
      iframe.src = url;
      document.body.appendChild(iframe);
      // Keep the PDF alive while the browser's print dialog uses it.
      setTimeout(() => { iframe.remove(); URL.revokeObjectURL(url); }, 300000);
    } catch (error) {
      setStatus('Could not load the payment receipt PDF. Please try Print again.');
    } finally { setLoading(false); }
  };
  const printListingPreviewOutput = () => {
    if (!listingPreview) return;
    const source = document.querySelector('.payment-listing-preview-content');
    if (!source) return;
    const printNode = source.cloneNode(true);
    const printHeader = document.createElement('div');
    printHeader.className = 'payment-listing-print-header';
    const printTitle = document.createElement('h2');
    printTitle.textContent = listingPreview.title;
    const printSubtitle = document.createElement('p');
    printSubtitle.textContent = `${listingPreview.customerName} - ${listingPreview.generatedAt}`;
    printHeader.appendChild(printTitle);
    printHeader.appendChild(printSubtitle);
    const escapePrintHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
    const iframe = document.createElement('iframe');
    iframe.title = `${listingPreview.title} print preview`;
    iframe.style.position = 'fixed';
    iframe.style.left = '-10000px';
    iframe.style.top = '0';
    iframe.style.width = '1100px';
    iframe.style.height = '850px';
    iframe.style.border = '0';
    document.body.appendChild(iframe);
    const cleanup = () => setTimeout(() => iframe.remove(), 250);
    const runPrint = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.addEventListener('afterprint', cleanup, { once: true });
      iframe.contentWindow.print();
      setTimeout(cleanup, 1000);
    };
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(`
      <!doctype html>
      <html>
        <head>
          <title>${escapePrintHtml(listingPreview.title)}</title>
          <style>
            @page { size: portrait; margin: 12mm; }
            * { box-sizing: border-box; }
            body { color: #222; font-family: Arial, Helvetica, sans-serif; margin: 0; padding: 0; }
            .payment-listing-print-header h2 { font-size: 24px; margin: 0 0 8px; }
            .payment-listing-print-header p { font-size: 16px; margin: 0 0 8px; }
            .payment-listing-preview-content { overflow: visible; }
            .payment-listing-preview-table { border-collapse: collapse; font-size: 12px; table-layout: auto; width: 100%; }
            .payment-listing-preview-table th,
            .payment-listing-preview-table td { border: 1px solid #c9cdd1; padding: 6px 7px; text-align: left; vertical-align: top; }
            .payment-listing-preview-table th,
            .payment-listing-preview-table tfoot td { background: #f2f3f4; font-weight: 700; }
            .payment-listing-preview-table td:nth-last-child(-n+3),
            .payment-listing-preview-table th:nth-last-child(-n+3) { text-align: right; }
          </style>
        </head>
        <body>${printHeader.outerHTML}${printNode.outerHTML}</body>
      </html>
    `);
    doc.close();
    setTimeout(runPrint, 100);
  };

  const waitForInvoicePreviewRender = () => new Promise((resolve) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(resolve);
    });
  });

  const closeApplyModal = () => setApplyModal({ open:false, invoice:null, detail:null, paymentIds: [], paymentAmounts:{}, amounts:{}, loading:false, creditMenuOpen:false });
  const toggleApplyPayment = (paymentId) => setApplyModal((current) => {
    const id = String(paymentId);
    const ids = Array.isArray(current.paymentIds) ? current.paymentIds.map(String) : [];
    const selected = ids.includes(id);
    const paymentIds = selected ? ids.filter((value) => value !== id) : [...ids, id];
    const paymentAmounts = { ...(current.paymentAmounts || {}) };
    if (selected) delete paymentAmounts[id];
    else {
      const payment = walletPayments.find((item) => String(item.id) === id);
      const selectedTotal = Object.entries(paymentAmounts)
        .filter(([key]) => paymentIds.includes(key))
        .reduce((sum, [, amount]) => sum + Number(amount || 0), 0);
      const remainingDue = Math.max(0, Number(current.detail?.amountDue || 0) - selectedTotal);
      paymentAmounts[id] = (Math.min(Number(payment?.availableAmount || 0), remainingDue || Number(payment?.availableAmount || 0))).toFixed(2);
    }
    const creditTotal = Object.entries(paymentAmounts)
      .filter(([key]) => paymentIds.includes(key))
      .reduce((sum, [, amount]) => sum + Number(amount || 0), 0);
    const total = Object.values(current.amounts).reduce((sum, amount) => sum + Number(amount || 0), 0);
    return {...current, paymentIds, paymentAmounts, error:total > creditTotal + 0.001
      ? `The applied amount exceeds the selected credit balance of ${currency(creditTotal)}.` : ''};
  });
  const setApplyPaymentAmount = (paymentId, value) => setApplyModal((current) => {
    const id = String(paymentId);
    const payment = walletPayments.find((item) => String(item.id) === id);
    const maxAmount = Number(payment?.availableAmount || 0);
    const numericValue = Number(value || 0);
    const nextValue = value === ''
      ? ''
      : (numericValue < 0 ? '0' : (numericValue > maxAmount ? maxAmount.toFixed(2) : value));
    const paymentAmounts = { ...(current.paymentAmounts || {}), [id]: nextValue };
    const selectedIds = Array.isArray(current.paymentIds) ? current.paymentIds.map(String) : [];
    const creditTotal = Object.entries(paymentAmounts)
      .filter(([key]) => selectedIds.includes(key))
      .reduce((sum, [, currentAmount]) => sum + Number(currentAmount || 0), 0);
    const total = Object.values(current.amounts).reduce((sum, currentAmount) => sum + Number(currentAmount || 0), 0);
    return {...current, paymentAmounts, error:total > creditTotal + 0.001
      ? `The applied amount exceeds the selected credit balance of ${currency(creditTotal)}.` : ''};
  });
  const normalizePaymentApplicationRows = (rows) => {
    const groups = new Map();
    rows.forEach((row) => {
      const documentType = row.documentType === 'repair' ? 'repair' : 'invoice';
      const documentId = String(row.documentId || '').trim();
      if (!documentId) return;
      const groupKey = `${documentType}|${documentId}`;
      if (!groups.has(groupKey)) groups.set(groupKey, { documentType, documentId, invoiceTotal: null, lines: [] });
      const group = groups.get(groupKey);
      const amount = Math.max(0, Math.round(Number(row.amount || 0) * 100) / 100);
      if (amount <= 0.001) return;
      if (!row.lineKey) {
        group.invoiceTotal = Math.round((Number(group.invoiceTotal || 0) + amount) * 100) / 100;
        return;
      }
      group.lines.push({ ...row, documentType, documentId, amount });
    });
    const normalized = [];
    groups.forEach((group) => {
      const lineTotal = group.lines.reduce((sum, row) => Math.round((sum + Number(row.amount || 0)) * 100) / 100, 0);
      normalized.push(...group.lines);
      if (group.invoiceTotal !== null) {
        const remainder = Math.round((Number(group.invoiceTotal || 0) - lineTotal) * 100) / 100;
        if (remainder > 0.001) normalized.push({ documentType: group.documentType, documentId: group.documentId, lineKey: null, serialNo: null, amount: remainder });
      }
    });
    return normalized;
  };
  const buildPaymentApplications = (rows, selectedPayments) => {
    const groups = selectedPayments.map((payment) => ({ paymentId: payment.id, remaining: Number(payment.applyAmount || 0), allocations: [] }));
    normalizePaymentApplicationRows(rows).forEach((row) => {
      let remaining = Number(row.amount || 0);
      groups.forEach((group) => {
        if (remaining <= 0.001 || group.remaining <= 0.001) return;
        const amount = Math.min(remaining, group.remaining);
        const roundedAmount = Math.round(amount * 100) / 100;
        if (roundedAmount <= 0) return;
        group.allocations.push({...row, amount: roundedAmount});
        group.remaining = Math.round((group.remaining - roundedAmount) * 100) / 100;
        remaining = Math.round((remaining - roundedAmount) * 100) / 100;
      });
      if (remaining > 0.001) throw new Error('The applied amount exceeds the selected credit balance.');
    });
    return groups.filter((group) => group.allocations.length > 0);
  };

  const applyCurrentInvoicePayments = async ({ closeAfter = true, printAfter = false } = {}) => {
    const { invoice, detail, amounts } = applyModal;
    const documentType = Number(invoice.transactionTypeId) === 640 ? 'repair' : 'invoice';
    const linePaymentDetailAvailable = detail?.linePaymentDetailAvailable !== false;
    const lineRows = Object.entries(amounts).filter(([, amount]) => Number(amount) > 0).map(([key, amount]) => {
      const item = detail?.items?.find((line) => line.allocationKey === key);
      return { documentId: invoice.id, documentType,
        lineKey: item?.lineKey || null, serialNo: item?.serialNo || null, amount: Number(amount) };
    });
    const invoiceLevelAmount = lineRows.length
      ? 0
      : Math.min(selectedCreditTotal, Number(detail?.amountDue ?? invoice.amountDue ?? 0));
    const rows = linePaymentDetailAvailable
      ? (lineRows.length ? lineRows : [{ documentId: invoice.id, documentType, lineKey: null, serialNo: null, amount: invoiceLevelAmount }].filter((row) => row.amount > 0))
      : [{ documentId: invoice.id, documentType, lineKey: null, serialNo: null, amount: Number(amounts[INVOICE_LEVEL_ALLOCATION_KEY] || invoiceLevelAmount || 0) }]
        .filter((row) => row.amount > 0);
    if (!selectedWalletPayments.length) {
      setApplyModal((current)=>({...current,error:'Select at least one payment credit.'}));
      return false;
    }
    if (!selectedPaymentCredits.length) {
      setApplyModal((current)=>({...current,error:'Enter an amount to use for at least one selected payment credit.'}));
      return false;
    }
    if (!rows.length) {
      setApplyModal((current)=>({...current,error:'Enter an amount to apply.'}));
      return false;
    }
    if (rows.reduce((sum, row) => sum + Number(row.amount || 0), 0) > selectedCreditTotal+0.001) {
      setApplyModal((current)=>({...current,error:`The applied amount exceeds the selected credit balance of ${currency(selectedCreditTotal)}.`}));
      return false;
    }
    let paymentApplications = [];
    try {
      paymentApplications = buildPaymentApplications(rows, selectedPaymentCredits);
    } catch (error) {
      setApplyModal((current)=>({...current,error:error.message || 'Could not apply the selected credits.'}));
      return false;
    }
    setApplyModal((current) => ({ ...current, loading: true }));
    try {
      await api.post('/payments/apply-many', { payments: paymentApplications });
      if (closeAfter) closeApplyModal();
      const { data } = await api.get(`/payments/documents/${documentType}/${encodeURIComponent(invoice.id)}`, { params: documentDetailParams() });
      let invoiceDocument = null;
      if (!invoice.customInvoice && !invoice.importedOnly && !data.importedOnly) {
        try {
          invoiceDocument = await fetchInvoiceDocument(invoice);
        } catch (documentError) {
          invoiceDocument = null;
        }
      }
      if (!closeAfter) {
        setApplyModal((current) => ({
          ...current,
          detail: data,
          invoiceDocument,
          paymentIds: [],
          paymentAmounts: {},
          amounts: {},
          loading: false,
          error: '',
          creditMenuOpen: false,
        }));
      }
      await loadCustomer(selected);
      setTab('invoices');
      setStatus(`Payment applied to invoice ${invoice.invoiceNo}.`);
      if (printAfter) {
        await waitForInvoicePreviewRender();
        printPaymentInvoiceOutput();
      }
      return true;
    } catch (error) {
      setApplyModal((current) => ({ ...current, loading: false, error:error.response?.data?.error || 'Could not apply payment.' }));
      return false;
    }
  };

  const saveInvoiceApplication = async (event) => {
    event.preventDefault();
    await applyCurrentInvoicePayments();
  };

  const applyAndPrintInvoice = async () => {
    if (!applyTotal) {
      printPaymentInvoiceOutput();
      return;
    }
    await applyCurrentInvoicePayments({ closeAfter: false, printAfter: true });
  };

  const payInvoiceTotal = () => {
    if (!applyModal.detail || !selectedWalletPayments.length) return setApplyModal((current)=>({...current,error:'Select at least one payment credit first.'}));
    if (selectedCreditTotal+0.001<Number(applyModal.detail.amountDue)) return setApplyModal((current)=>({...current,error:`Pay Total requires ${currency(applyModal.detail.amountDue)}, but only ${currency(selectedCreditTotal)} is selected.`}));
    setApplyModal((current) => ({...current,amounts:current.detail.linePaymentDetailAvailable === false
      ? { [INVOICE_LEVEL_ALLOCATION_KEY]: Number(current.detail.amountDue).toFixed(2) }
      : Object.fromEntries(current.detail.items
        .filter((item)=>Number(item.dueAmount)>0)
        .map((item)=>[item.allocationKey,Number(item.dueAmount).toFixed(2)])),error:''}));
  };

  const setLineApplyAmount = (allocationKeyValue, value) => {
    setApplyModal((current) => {
      const amounts={...current.amounts,[allocationKeyValue]:value};
      const total=Object.values(amounts).reduce((sum,amount)=>sum+Number(amount||0),0);
      const selectedIds = Array.isArray(current.paymentIds) ? current.paymentIds.map(String) : [];
      const creditTotal = walletPayments
        .filter((payment)=>selectedIds.includes(String(payment.id)))
        .reduce((sum,payment)=>sum+Number(payment.availableAmount||0),0);
      return {...current,amounts,error:total>creditTotal+0.001
        ? `The applied amount exceeds the selected credit balance of ${currency(creditTotal)}.` : ''};
    });
  };
  const availablePaymentsPicker = (
    <div className="credit-apply-picker-field available-payment-picker-field">
      <div ref={creditApplyPickerRef} className={`payment-credit-picker credit-apply-picker ${creditApplyModal.creditMenuOpen?'open':''}`}>
        <button type="button" className="payment-credit-picker-button" onClick={() => setCreditApplyModal((current)=>({...current,creditMenuOpen:!current.creditMenuOpen}))}>{creditApplyLabel}</button>
        {creditApplyModal.creditMenuOpen && <div className="payment-credit-picker-menu">{walletPayments.map((payment) => {
          const id=String(payment.id);
          const checked=creditApplyPaymentIds.includes(id);
          return <div key={payment.id} className={`payment-credit-option ${checked?'selected':''}`}><label><input type="checkbox" checked={checked} onChange={() => toggleCreditApplyPayment(id)} /><span className="payment-credit-option-text"><span>{paymentCreditDate(payment)} - #{payment.referenceNo || payment.id} - {payment.paymentType}</span><strong>{currency(payment.availableAmount)} available</strong></span></label><input className="payment-credit-amount" type="text" inputMode="decimal" min="0" step="0.01" max={Number(payment.availableAmount || 0)} value={creditApplyPaymentAmounts[id] || ''} disabled={!checked} onClick={(event)=>event.stopPropagation()} onChange={(event)=>setCreditApplyPaymentAmount(id,event.target.value)} aria-label={`Amount to use from payment credit ${payment.id}`} /></div>;
        })}{walletPayments.length===0 && <span className="payment-credit-empty">No unapplied credit balance</span>}</div>}
      </div>
    </div>
  );
  const renderPaymentCustomerCard = (customer) => {
    const summary = customer.summary;
    const typeLabel = customerTypeLabel(customer, customerTypes);
    return (
      <button type="button" key={customer.id} className={`payment-summary-card ${String(customerId(selected)) === String(customerId(customer)) ? 'selected' : ''}`} onClick={() => loadCustomer(customer)}>
        <div className="payment-summary-card-name"><strong>{customerName(customer)}</strong><span>{typeLabel || '-'}</span></div>
        <div><small>Last Payment</small><strong>{summary.lastPaymentAmount == null ? '-' : currency(summary.lastPaymentAmount)}</strong><span>{summary.lastPaymentDate ? formatCalendarDate(summary.lastPaymentDate) : 'No payments'}</span></div>
        <div><small>Credit Balance</small><strong className="wallet-value">{currency(summary.walletBalance)}</strong></div>
        <div><small>Amount Owed</small><strong className="owed-value">{currency(customerListBalance(summary))}</strong><span>{summary.oldestOutstandingDate ? `Oldest: ${formatCalendarDate(summary.oldestOutstandingDate)}` : 'No open balance'}</span></div>
      </button>
    );
  };
  const paymentMethodControls = (
    <>
      {PAYMENT_METHOD_OPTIONS.map(([type,icon]) => type === AVAILABLE_PAYMENT_TYPE
        ? <div key={type} className={`available-payment-control ${isAvailablePaymentMode ? 'active' : ''}`}><button type="button" disabled={Boolean(activePaymentReview)} className={`${form.paymentType === type ? 'active' : ''} available-payment-method-button`} onClick={() => selectPaymentMethod(type)}><b>{icon}</b><span>{type}</span></button>{isAvailablePaymentMode && availablePaymentsPicker}</div>
        : <button type="button" key={type} disabled={paymentHeaderReadOnly} className={form.paymentType === type ? 'active' : ''} onClick={() => selectPaymentMethod(type)}><b>{icon}</b><span>{type}</span></button>)}
    </>
  );

  return <><TopNav /><main className="inventory-container payments-page">
    <div className="customers-page-header"><div className="customers-heading-tools"><h2>Payments</h2></div>{selected && canReceiveCustomerPayment && <button className="button small blue" onClick={openPayment}>Receive Payment</button>}</div>
    <div className="payment-split-layout"><aside className="payment-customer-pane"><div className="payment-left-tabs"><div className={`payment-customer-tab-group ${leftTab==='customers'?'active':''}`}><button type="button" className="payment-customers-tab" onClick={()=>setLeftTab('customers')}>Customers</button></div><button type="button" className={`payment-transactions-tab ${leftTab==='transactions'?'active':''}`} onClick={openTransactionsTab}>Transactions</button></div>
    {leftTab==='customers'?<>
    <div className="input-button-group payment-search"><div className="customer-search-typeahead"><input className="input" value={query} onChange={(e) => { setQuery(e.target.value); setLookupOpen(true); setActiveLookupIndex(0); }} onFocus={() => { setLookupOpen(true); setActiveLookupIndex(0); }} onBlur={() => setTimeout(() => setLookupOpen(false), 120)} onKeyDown={handleLookupKeyDown} placeholder="Search by name or phone" autoComplete="off" role="combobox" aria-expanded={lookupOpen} aria-controls="payment-customer-typeahead-menu" />{lookupOpen && lookupOptions.length > 0 && <div ref={lookupMenuRef} className="customer-typeahead-menu" id="payment-customer-typeahead-menu" role="listbox">{lookupOptions.map((customer,index) => <button type="button" key={customer.id} className={index === activeLookupIndex ? 'active' : ''} onMouseDown={(event) => event.preventDefault()} onMouseMove={() => setActiveLookupIndex(index)} onClick={() => loadCustomer(customer)} role="option" aria-selected={index === activeLookupIndex}><span>{customerName(customer)}</span><small>{customerTypeLabel(customer, customerTypes) || '-'}</small></button>)}</div>}</div><button type="button" className="customer-type-filter-button" onClick={() => setTypeFilterOpen(true)} aria-label="Filter customer types" title="Filter customer types"><FilterIcon /></button><button type="button" className="payment-search-clear-button" onClick={() => { setQuery(''); setSelectedCustomerTypeIds(customerTypes.map((type)=>String(type.id))); setSelected(null); setPayments([]); setInvoices([]); setLookupOpen(false); setTypeFilterOpen(false); }} aria-label="Clear customer search" title="Clear customer search"><ClearIcon /></button></div>
    <div className="payment-customer-list-header"><button type="button" onClick={()=>sortCustomerList('name')} aria-label="Sort customers by name" aria-pressed={customerListSort.key==='name'}><span>Name</span><span className="payment-sort-arrow sort-arrow-button" data-direction={customerListSort.key==='name'&&customerListSort.direction==='asc'?'asc':'desc'} aria-hidden="true" /></button><button type="button" onClick={()=>sortCustomerList('balance')} aria-label="Sort customers by balance" aria-pressed={customerListSort.key==='balance'}><span>Balance</span><span className="payment-sort-arrow sort-arrow-button" data-direction={customerListSort.key==='balance'&&customerListSort.direction==='asc'?'asc':'desc'} aria-hidden="true" /></button></div>
    <div className="payment-summary-card-list" ref={customerListRef} onScroll={(event)=>setCustomerListScrollTop(event.currentTarget.scrollTop)}>{rankedCustomers.length>0 ? <div className="payment-summary-card-virtual-space" style={{height:customerListHeight}}><div className="payment-summary-card-virtual-window" style={{transform:`translateY(${customerListOffset}px)`}}>{visibleCustomers.map(renderPaymentCustomerCard)}</div></div> : <div className="empty-state">No customers match the selected filters.</div>}</div>
    </>:<div className="payment-transaction-types"><h4>Transaction Types</h4>{['Received Payment','Voided Payment','Refund','Invoice','Credit Memo'].map((type)=><label key={type}><input type="radio" name="payment-transaction-type" checked={selectedTransactionType===type} onChange={()=>changeTransactionType(type)}/><span>{type==='Received Payment'?'Received Payments':type==='Voided Payment'?'Voided Payments':type==='Refund'?'Refunds':type==='Invoice'?'Invoices':'Credit Memos'}</span></label>)}</div>}
    </aside><section className="payment-main-pane">{leftTab==='transactions'?<div className="payment-transactions-workspace"><header><div><h2>Transactions</h2><span>{transactionsLoading ? 'Loading transactions...' : `${transactionPageInfo.total} transactions`}</span></div></header><div className="payment-transaction-filters">{selectedTransactionType==='Invoice'?<label><span>Status</span><select value={transactionInvoiceStatusFilter} onChange={(event)=>{ setTransactionInvoiceStatusFilter(event.target.value); loadTransactions({ status: event.target.value, page: 1 }); }}><option>Pending</option><option>Paid</option><option>All</option></select></label>:['Received Payment','Voided Payment','Refund'].includes(selectedTransactionType)?<label><span>Payment Method</span><select value={transactionPaymentType} onChange={(event)=>{ setTransactionPaymentType(event.target.value); loadTransactions({ paymentType: event.target.value, page: 1 }); }}>{transactionPaymentTypes.map((type)=><option key={type}>{type}</option>)}</select></label>:null}<label><span>Date</span><select value={transactionDatePreset} onChange={(event)=>changeTransactionDatePreset(event.target.value)}>{TRANSACTION_DATE_PRESETS.map((preset)=><option key={preset}>{preset}</option>)}</select></label><div className="payment-listing-toolbar-actions"><button type="button" className="button small blue" onClick={()=>openListingPreview('transactions')} disabled={transactionListingDisabled || listingPreviewLoading}>{listingPreviewLoading?'Preparing...':'Print'}</button></div></div>{transactionsError&&<div className="payment-warning">{transactionsError}</div>}<div className="payment-pagination"><span>{transactionPageInfo.total ? `Showing ${transactionPageStart}-${transactionPageEnd} of ${transactionPageInfo.total}` : 'No transactions'}</span><div><button type="button" onClick={()=>changeTransactionPage(transactionPageInfo.page - 1)} disabled={transactionsLoading || transactionPageInfo.page<=1}>Previous</button><span>Page {transactionPageInfo.page} of {transactionTotalPages}</span><button type="button" onClick={()=>changeTransactionPage(transactionPageInfo.page + 1)} disabled={transactionsLoading || transactionPageInfo.page>=transactionTotalPages}>Next</button></div></div><div className={`payment-transaction-table-wrap ${transactionCustomerFilterOpen?'filter-open':''} ${transactionsLoading?'loading':''}`}>{transactionsLoading&&<div className="payment-table-loading"><span></span><strong>Loading results...</strong></div>}<table className="inventory-table payment-transaction-table"><thead><tr><th className={`filter-header payment-transaction-customer-filter ${transactionCustomerFilterOpen?'filter-open':''}`} onClick={toggleTransactionCustomerFilter}><div className="inventory-filter-trigger"><span>Customer</span><button type="button" className="payment-filter-arrow filter-icon-button" aria-label="Filter transactions by customer"><svg viewBox="0 0 24 24" aria-hidden="true" className="filter-icon"><path d="M4 5h16l-6.5 7.5V18l-3 1v-6.5L4 5z" /></svg></button>{transactionCustomerFilterOpen&&<div className="filter-dropdown" onClick={(event)=>event.stopPropagation()}><input type="search" className="payment-transaction-filter-search" value={transactionCustomerFilterSearch} onChange={(event)=>setTransactionCustomerFilterSearch(event.target.value)} placeholder="Search Customer" aria-label="Search Customer filter options" autoComplete="off" autoFocus/><div className="filter-actions"><button type="button" onClick={toggleAllTransactionCustomerFilterOptions}>{allTransactionCustomersSelected?'Unselect All':'Select All'}</button><button type="button" onClick={clearTransactionCustomerFilter}>Clear</button></div><div className="filter-options">{visibleTransactionCustomerOptions.map((value)=><label key={value || '__blank__'} className="filter-option"><input type="checkbox" checked={transactionCustomerFilterSelection.includes(value)} onChange={()=>toggleTransactionCustomerFilterValue(value)}/><span className="filter-value">{value || '-'}</span></label>)}{visibleTransactionCustomerOptions.length===0&&<div className="payment-transaction-filter-empty">No matches</div>}</div></div>}</div></th><th>Reference</th><th>Type</th><th className="payment-sort-header" aria-sort={transactionSort.key==='date'?(transactionSort.direction==='asc'?'ascending':'descending'):'none'}><span>Date</span><button type="button" className="payment-sort-button sort-arrow-button" data-direction={transactionSortArrow('date')} onClick={()=>sortTransactionsBy('date')} aria-label="Sort transactions by date" /></th><th>Payment Method</th><th className="payment-sort-header" aria-sort={transactionSort.key==='amount'?(transactionSort.direction==='asc'?'ascending':'descending'):'none'}><span>Amount</span><button type="button" className="payment-sort-button sort-arrow-button" data-direction={transactionSortArrow('amount')} onClick={()=>sortTransactionsBy('amount')} aria-label="Sort transactions by amount" /></th><th className="payment-sort-header" aria-sort={transactionSort.key==='unappliedAmount'?(transactionSort.direction==='asc'?'ascending':'descending'):'none'}><span>Unapplied Amount</span><button type="button" className="payment-sort-button sort-arrow-button" data-direction={transactionSortArrow('unappliedAmount')} onClick={()=>sortTransactionsBy('unappliedAmount')} aria-label="Sort transactions by unapplied amount" /></th></tr></thead><tbody>{sortedTransactions.map((transaction)=><tr key={transaction.id} onClick={()=>openTransactionRow(transaction)}><td>{transaction.customerName}</td><td>{renderTransactionReference(transaction)}</td><td>{transaction.transactionType}</td><td>{transactionDateDisplay(transaction.date)}</td><td>{transaction.paymentType}</td><td>{currency(transaction.amount)}</td><td>{currency(transaction.unappliedAmount)}</td></tr>)}{filteredTransactions.length===0&&<tr><td colSpan="7" className="payment-empty-row">No transactions match these filters.</td></tr>}</tbody><tfoot><tr><td colSpan="5">Page Totals</td><td>{currency(filteredTransactionTotals.amount)}</td><td>{currency(filteredTransactionTotals.unappliedAmount)}</td></tr></tfoot></table></div></div>:<div className="payment-customer-grid">
      <section className="payment-workspace">{!selected ? <div className="empty-state">Search for and select a customer to view their credit balance.</div> : <>
        <header className="payment-customer-heading"><div className="payment-customer-heading-details"><h2>{customerName(selected)}</h2>{customerAddressLine(selected) && <span className="payment-customer-detail-line"><strong>Billing:</strong> {customerAddressLine(selected)}</span>}{customerShippingAddressLine(selected) && <span className="payment-customer-detail-line"><strong>Shipping:</strong> {customerShippingAddressLine(selected)}</span>}{customerContactLines(selected).length > 0 && <span className="payment-customer-detail-line payment-customer-contact-line">{customerContactLines(selected).map((item) => <React.Fragment key={`${item.label}-${item.value}`}><strong>{item.label}:</strong> {item.value}</React.Fragment>).reduce((nodes, node, index) => (index === 0 ? [node] : [...nodes, <span key={`sep-${index}`} className="payment-customer-detail-separator">|</span>, node]), [])}</span>}</div><div className="wallet-balance"><small>{customerHeaderBalanceLabel}</small><strong>{currency(customerHeaderBalance)}</strong></div></header>
        <div className="customer-tabs"><button className={tab === 'invoices' ? 'active' : ''} onClick={() => setTab('invoices')}>Invoices</button><button className={tab === 'payments' ? 'active' : ''} onClick={() => setTab('payments')}>Payments</button>{can(user, 'customers.read') && <button type="button" onClick={() => window.open(`/customers?customerId=${encodeURIComponent(customerId(selected))}&tab=collectionReport`, '_blank', 'noopener,noreferrer')}>Collection Report</button>}<button className={tab === 'reports' ? 'active' : ''} onClick={() => setTab('reports')}>Reports</button>{canViewCustomerCrm && <button type="button" className={tab === 'crm' ? 'active' : ''} onClick={() => setTab('crm')}>CRM</button>}<button type="button" className={`payment-tab-refresh ${refreshingCustomerTab ? 'refreshing' : ''}`} onClick={refreshSelectedCustomerTab} disabled={loading || refreshingCustomerTab} title={refreshingCustomerTab ? "Refreshing..." : "Refresh selected customer"} aria-label={refreshingCustomerTab ? "Refreshing selected customer data" : "Refresh selected customer data"}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 11a8.1 8.1 0 0 0-15.5-2M4 5v4h4m-4 4a8.1 8.1 0 0 0 15.5 2M20 19v-4h-4" /></svg></button></div>
        {loading ? <p>Loading...</p> : tab === 'crm' && canViewCustomerCrm ? <CustomerCrm key={customerId(selected)} customerId={customerId(selected)} focusMessageId={searchParams.get('messageId')} showRefresh={false} /> : tab === 'payments' ? <div className="payment-tab-panel"><div className="payment-transaction-filters payment-detail-filters"><label><span>Type</span><select value={paymentTypeFilter} onChange={(event)=>setPaymentTypeFilter(event.target.value)}>{paymentTypes.map((type)=><option key={type}>{type}</option>)}</select></label><label><span>Date</span><select value={paymentDatePreset} onChange={(event)=>changePaymentDatePreset(event.target.value)}>{TRANSACTION_DATE_PRESETS.map((preset)=><option key={preset}>{preset}</option>)}</select></label>{status && <p className="payment-status payment-main-status">{status}</p>}<div className="payment-listing-toolbar-actions"><button type="button" className="button small blue" onClick={()=>openListingPreview('payments')} disabled={listingActionDisabled}>Print</button><EmailActions endpoint="/payments/listing-email" templateKey="payment" templateValues={{document:'Payments Listing',documentNumber:'',invoiceNo:'',recipientName:customerName(selected),customerName:customerName(selected)}} recipient={selected?.email || ''} subject={`${customerName(selected)} Payments`} quickTitle="Send Payments Listing" quickPrompt="Send payments listing to:" attachmentPdf={`Payments_${customerName(selected).replace(/[^a-z0-9]+/gi,'_')}.pdf`} attachmentExcel={`Payments_${customerName(selected).replace(/[^a-z0-9]+/gi,'_')}.xls`} disabled={listingActionDisabled} loading={listingEmailLoading} onLoadingChange={setListingEmailLoading} buildPayload={()=>({listing:currentListingData('payments')})} successMessage={(to)=>`Payments listing emailed to ${to}.`} onError={(error)=>setStatus(error?.message || error || 'Could not email payments listing.')} />{canCreateReturn && <button type="button" className="button small blue" onClick={()=>openCustomInvoice('credit_memo')}>Add Credit Memo</button>}</div></div><table className="inventory-table payment-detail-table"><thead><tr><th className="payment-sort-header" aria-sort={paymentSort.key==='date'?(paymentSort.direction==='asc'?'ascending':'descending'):'none'}><span>Date</span><button type="button" className="payment-sort-button sort-arrow-button" data-direction={paymentSortArrow('date')} onClick={()=>sortPaymentsBy('date')} aria-label="Sort payments by date" /></th><th>Type</th><th>Reference</th><th className="payment-sort-header" aria-sort={paymentSort.key==='amount'?(paymentSort.direction==='asc'?'ascending':'descending'):'none'}><span>Amount</span><button type="button" className="payment-sort-button sort-arrow-button" data-direction={paymentSortArrow('amount')} onClick={()=>sortPaymentsBy('amount')} aria-label="Sort payments by amount" /></th><th className="payment-sort-header" aria-sort={paymentSort.key==='applied'?(paymentSort.direction==='asc'?'ascending':'descending'):'none'}><span>Applied</span><button type="button" className="payment-sort-button sort-arrow-button" data-direction={paymentSortArrow('applied')} onClick={()=>sortPaymentsBy('applied')} aria-label="Sort payments by applied amount" /></th><th className="payment-sort-header" aria-sort={paymentSort.key==='refunded'?(paymentSort.direction==='asc'?'ascending':'descending'):'none'}><span>Refunded</span><button type="button" className="payment-sort-button sort-arrow-button" data-direction={paymentSortArrow('refunded')} onClick={()=>sortPaymentsBy('refunded')} aria-label="Sort payments by refunded amount" /></th><th className="payment-sort-header" aria-sort={paymentSort.key==='available'?(paymentSort.direction==='asc'?'ascending':'descending'):'none'}><span>Available</span><button type="button" className="payment-sort-button sort-arrow-button" data-direction={paymentSortArrow('available')} onClick={()=>sortPaymentsBy('available')} aria-label="Sort payments by available amount" /></th><th>Entered By</th><th>Actions</th></tr></thead><tbody>{filteredPayments.map((p) => { const dateDisplay = paymentDateDisplay(p); const isCreditMemo = isCreditMemoPayment(p); const hasCreditMemoLog = isCreditMemo && !p.readOnly; const hasAvailableCreditMemo = hasCreditMemoLog && Number(p.availableAmount || 0) > 0.001; const hasAvailablePayment = Number(p.availableAmount || 0) > 0.001; const displayType = paymentDisplayType(p); return <tr key={p.id} onClick={() => openPaymentReview(p)}><td>{dateDisplay.primary}{dateDisplay.secondary && <small className="payment-date-note">{dateDisplay.secondary}</small>}</td><td>{displayType}{p.imported && <small className="payment-import-note">{quickBooksImportLabel(p)}</small>}</td><td>{isCreditMemo ? <button type="button" className="payment-credit-memo-number" onClick={(event) => { event.stopPropagation(); openCreditMemo(p); }}>{paymentReferenceDisplay(p)}</button> : paymentReferenceDisplay(p)}</td><td>{currency(p.amount)}</td><td>{currency(p.appliedAmount)}</td><td>{currency(p.refundedAmount)}</td><td>{currency(p.availableAmount)}</td><td>{p.enteredBy}</td><td className="payment-actions">{isCreditMemo ? <><span className="payment-action-slot">{canCreatePayment && hasAvailableCreditMemo ? <button type="button" onClick={(event) => { event.stopPropagation(); openCreditApply(p); }}>Apply to Invoice</button> : <span className="payment-action-placeholder">Apply to Invoice</span>}</span><span className="payment-action-slot"><span className="payment-action-placeholder">Move Payment</span></span><span className="payment-action-slot">{canEditPayment && hasAvailableCreditMemo ? <button type="button" onClick={(event) => { event.stopPropagation(); refund(p); }}>Issue Refund</button> : <span className="payment-action-placeholder">Issue Refund</span>}</span><span className="payment-action-slot">{hasCreditMemoLog ? <button type="button" onClick={(event) => { event.stopPropagation(); showActivity(p); }}>History</button> : <span className="payment-action-placeholder">History</span>}</span></> : p.readOnly ? <><span className="payment-action-slot"><span className="payment-read-only-note">Read only</span></span><span className="payment-action-slot"><span className="payment-action-placeholder">Move Payment</span></span><span className="payment-action-slot"><span className="payment-action-placeholder">Issue Refund</span></span><span className="payment-action-slot"><span className="payment-action-placeholder">History</span></span></> : <><span className="payment-action-slot">{canCreatePayment ? <button type="button" onClick={(event) => { event.stopPropagation(); openCreditApply(p); }} disabled={!hasAvailablePayment}>Apply to Invoice</button> : <span className="payment-action-placeholder">Apply to Invoice</span>}</span><span className="payment-action-slot">{canEditPayment ? <button type="button" onClick={(event) => { event.stopPropagation(); move(p); }} disabled={p.imported}>Move Payment</button> : <span className="payment-action-placeholder">Move Payment</span>}</span><span className="payment-action-slot">{canEditPayment ? <button type="button" onClick={(event) => { event.stopPropagation(); refund(p); }} disabled={p.imported || Number(p.refundedAmount)>=Number(p.amount)}>Issue Refund</button> : <span className="payment-action-placeholder">Issue Refund</span>}</span><span className="payment-action-slot"><button type="button" onClick={(event) => { event.stopPropagation(); showActivity(p); }}>History</button></span></>}</td></tr>})}{filteredPayments.length===0&&<tr><td colSpan="9" className="payment-empty-row">{payments.length ? 'No payments match these filters.' : 'No payments found for this customer.'}</td></tr>}</tbody><tfoot><tr><td colSpan="3">Totals</td><td>{currency(filteredPaymentTotals.amount)}</td><td>{currency(filteredPaymentTotals.applied)}</td><td>{currency(filteredPaymentTotals.refunded)}</td><td>{currency(filteredPaymentTotals.available)}</td><td colSpan="2"></td></tr></tfoot></table></div>
          : tab === 'invoices' ? <div className="payment-invoices-panel customer-tab-panel"><div className="customer-transfer-date-range payment-invoice-toolbar"><label><span>Status</span><select value={invoiceStatusFilterValue} onChange={(event)=>changeInvoiceStatusFilter(event.target.value)}><option>Pending</option><option>Paid</option><option>All</option></select></label><label><span>Date</span><select value={invoiceDatePreset} onChange={(event)=>changeInvoiceDatePreset(event.target.value)}>{invoiceDatePreset === 'Custom' && <option>Custom</option>}{TRANSACTION_DATE_PRESETS.map((preset)=><option key={preset}>{preset}</option>)}</select></label><label><span>From Date</span><input type="date" value={invoiceDateRange.start} max={invoiceDateRange.end || undefined} onChange={(event) => { setInvoiceDatePreset('Custom'); setInvoiceDateRange((current) => ({...current,start:event.target.value})); }} /></label><label><span>To Date</span><input type="date" value={invoiceDateRange.end} min={invoiceDateRange.start || undefined} onChange={(event) => { setInvoiceDatePreset('Custom'); setInvoiceDateRange((current) => ({...current,end:event.target.value})); }} /></label>{(invoiceDateRange.start || invoiceDateRange.end !== today()) && <button type="button" className="button small yellow customer-transfer-date-clear" onClick={() => { setInvoiceDatePreset('All'); setInvoiceDateRange(transactionPresetRange('All')); }}>Clear</button>}{status && <p className="payment-status payment-main-status">{status}</p>}<div className="payment-listing-toolbar-actions"><button type="button" className="button small blue" onClick={()=>openListingPreview('invoices')} disabled={listingActionDisabled}>Print</button><EmailActions endpoint="/payments/listing-email" templateKey="payment" templateValues={{document:'Invoices Listing',documentNumber:'',invoiceNo:'',recipientName:customerName(selected),customerName:customerName(selected)}} recipient={selected?.email || ''} subject={`${customerName(selected)} Invoices`} quickTitle="Send Invoices Listing" quickPrompt="Send invoices listing to:" attachmentPdf={`Invoices_${customerName(selected).replace(/[^a-z0-9]+/gi,'_')}.pdf`} attachmentExcel={`Invoices_${customerName(selected).replace(/[^a-z0-9]+/gi,'_')}.xls`} disabled={listingActionDisabled} loading={listingEmailLoading} onLoadingChange={setListingEmailLoading} buildPayload={()=>({listing:currentListingData('invoices')})} successMessage={(to)=>`Invoices listing emailed to ${to}.`} onError={(error)=>setStatus(error?.message || error || 'Could not email invoices listing.')} />{canCreateInvoice && <button type="button" className="button small blue payment-add-custom-invoice" onClick={openCustomInvoice}>Add Custom Invoice</button>}</div></div><div className="payment-invoices-table-wrap"><table className="inventory-table payment-shipdoc-table"><thead><tr><th className="payment-sort-header" aria-sort={invoiceSort.key==='date'?(invoiceSort.direction==='asc'?'ascending':'descending'):'none'}><span>Date</span><button type="button" className="payment-sort-button sort-arrow-button" data-direction={invoiceSortArrow('date')} onClick={()=>sortInvoicesBy('date')} aria-label="Sort invoices by date" /></th><th className="collection-report-serial-header filter-header" aria-sort={invoiceSort.key==='number'?(invoiceSort.direction==='asc'?'ascending':'descending'):'none'}><div className="collection-report-header-control"><input type="search" value={invoiceNumberFilter} placeholder="Invoices" onChange={(event) => setInvoiceNumberFilter(event.target.value)} aria-label="Look up by invoice number" autoComplete="off" /><button type="button" className="payment-sort-button sort-arrow-button" data-direction={invoiceSortArrow('number')} onClick={() => sortInvoicesBy('number')} aria-label="Sort by invoice number" /></div></th><th className={`filter-header ${invoiceStatusFilterOpen?'filter-open':''}`} onClick={toggleInvoiceStatusFilter}><div className="inventory-filter-trigger"><span>Status</span><button type="button" className="payment-filter-arrow filter-icon-button" aria-label="Filter status"><svg viewBox="0 0 24 24" aria-hidden="true" className="filter-icon"><path d="M4 5h16l-6.5 7.5V18l-3 1v-6.5L4 5z" /></svg></button>{invoiceStatusFilterOpen && <div className="filter-dropdown" onClick={(event)=>event.stopPropagation()}><div className="filter-actions"><button type="button" onClick={()=>setSelectedInvoiceStatuses(['pending','paid'])}>Select All</button><button type="button" onClick={()=>setSelectedInvoiceStatuses(['pending'])}>Clear</button></div><div className="filter-options">{INVOICE_STATUS_OPTIONS.map(([value,label])=><label key={value} className="filter-option"><input type="checkbox" checked={selectedInvoiceStatuses.includes(value)} onChange={()=>setSelectedInvoiceStatuses((current)=>current.includes(value)?current.filter((item)=>item!==value):[...current,value])}/><span className="filter-value">{label}</span></label>)}</div></div>}</div></th><th className={`filter-header ${invoiceTypeFilterOpen?'filter-open':''}`} onClick={toggleInvoiceTypeFilter}><div className="inventory-filter-trigger"><span>Transaction Type</span><button type="button" className="payment-filter-arrow filter-icon-button" aria-label="Filter transaction type"><svg viewBox="0 0 24 24" aria-hidden="true" className="filter-icon"><path d="M4 5h16l-6.5 7.5V18l-3 1v-6.5L4 5z" /></svg></button>{invoiceTypeFilterOpen && <div className="filter-dropdown" onClick={(event)=>event.stopPropagation()}><div className="filter-actions"><button type="button" onClick={()=>setSelectedInvoiceTypes(['sale','repair','credit','custom'])}>Select All</button><button type="button" onClick={()=>setSelectedInvoiceTypes([])}>Clear</button></div><div className="filter-options">{INVOICE_TYPE_OPTIONS.map(([value,label])=><label key={value} className="filter-option"><input type="checkbox" checked={selectedInvoiceTypes.includes(value)} onChange={()=>setSelectedInvoiceTypes((current)=>current.includes(value)?current.filter((item)=>item!==value):[...current,value])}/><span className="filter-value">{label}</span></label>)}</div></div>}</div></th><th className="payment-sort-header" aria-sort={invoiceSort.key==='amount'?(invoiceSort.direction==='asc'?'ascending':'descending'):'none'}><span>Amount</span><button type="button" className="payment-sort-button sort-arrow-button" data-direction={invoiceSortArrow('amount')} onClick={()=>sortInvoicesBy('amount')} aria-label="Sort invoices by amount" /></th><th className="payment-sort-header" aria-sort={invoiceSort.key==='applied'?(invoiceSort.direction==='asc'?'ascending':'descending'):'none'}><span>Applied</span><button type="button" className="payment-sort-button sort-arrow-button" data-direction={invoiceSortArrow('applied')} onClick={()=>sortInvoicesBy('applied')} aria-label="Sort invoices by applied amount" /></th><th className="payment-sort-header" aria-sort={invoiceSort.key==='due'?(invoiceSort.direction==='asc'?'ascending':'descending'):'none'}><span>Amount Due</span><button type="button" className="payment-sort-button sort-arrow-button" data-direction={invoiceSortArrow('due')} onClick={()=>sortInvoicesBy('due')} aria-label="Sort invoices by amount due" /></th><th>Date Last Modified</th><th>Created By</th><th>Last Modified By</th><th>Actions</th></tr></thead><tbody>{filteredInvoices.map((invoice) => <tr key={invoice.id}><td>{(() => { const dateDisplay = tableDateDisplay(invoice.date, { timeZone: BUSINESS_TIME_ZONE }); return <>{dateDisplay.primary}{dateDisplay.secondary && <small className="payment-date-note">{dateDisplay.secondary}</small>}</>; })()}</td><td><button type="button" className="location-doc-link" onClick={() => Number(invoice.transactionTypeId) === 10 ? openCreditMemo({ referenceNo: invoice.docNo }) : openInvoicePreviewModal(invoice)}>{invoice.docNo}</button></td><td><span className={`payment-status-pill ${invoice.closed ? 'paid' : 'pending'}`}>{invoice.closed ? 'Paid' : 'Pending'}</span></td><td>{invoice.type}</td><td>{currency(invoice.originalAmount)}</td><td>{currency(invoice.appliedAmount)}</td><td>{currency(invoice.amountDue)}</td><td>{(() => { const dateDisplay = tableDateDisplay(invoice.dateLastModified, { timeZone: BUSINESS_TIME_ZONE }); return <>{dateDisplay.primary}{dateDisplay.secondary && <small className="payment-date-note">{dateDisplay.secondary}</small>}</>; })()}</td><td>{invoice.createdBy || '-'}</td><td>{invoice.lastModifiedBy || '-'}</td><td className="payment-invoice-actions"><span className="payment-invoice-action-slot">{canCreatePayment && Number(invoice.transactionTypeId) !== 10 && canApplyPaymentToInvoice(invoice) ? <button type="button" onClick={(event) => { event.stopPropagation(); openInvoicePaymentModal(invoice); }}>Apply Payment</button> : <span className="payment-action-placeholder">Apply Payment</span>}</span><span className="payment-invoice-action-slot">{Number(invoice.appliedAmount || 0) > 0.001 ? <button type="button" onClick={(event) => { event.stopPropagation(); showInvoiceHistory(invoice); }}>History</button> : <span className="payment-action-placeholder">History</span>}</span><span className="payment-invoice-action-slot">{canDeleteInvoice && invoice.customInvoice ? <button type="button" className="button small red" onClick={(event) => { event.stopPropagation(); openDeleteInvoiceConfirm(invoice); }}>Delete</button> : <span className="payment-action-placeholder">Delete</span>}</span></td></tr>)}{filteredInvoices.length === 0 && <tr><td colSpan="11" className="payment-empty-row">No invoices match these filters.</td></tr>}</tbody><tfoot><tr><td colSpan="4">Totals</td><td>{currency(filteredInvoiceTotals.amount)}</td><td>{currency(filteredInvoiceTotals.applied)}</td><td>{currency(filteredInvoiceTotals.due)}</td><td colSpan="4"></td></tr></tfoot></table></div></div>
          : <div className="payment-report"><h3>Customer Payment Summary</h3><p>Total received: <strong>{currency(payments.reduce((s,p) => s + p.amount, 0))}</strong></p><p>Total applied: <strong>{currency(payments.reduce((s,p) => s + p.appliedAmount, 0))}</strong></p><p>Total refunded: <strong>{currency(payments.reduce((s,p) => s + p.refundedAmount, 0))}</strong></p></div>}
      </>}</section>
    </div>}</section></div>
  </main>
  {listingPreview && <div className="invoice-modal-overlay payment-listing-preview-overlay" onClick={()=>setListingPreview(null)}><section className="payment-modal payment-listing-preview-modal" onClick={(event)=>event.stopPropagation()}><header><div><h2>{listingPreview.title}</h2><p>{listingPreview.customerName} - {listingPreview.generatedAt}</p></div><div className="payment-listing-preview-actions"><button type="button" className="button small blue" onClick={printListingPreviewOutput}>Print</button><button type="button" className="button small red" onClick={()=>setListingPreview(null)}>Close</button></div></header><div className="payment-listing-preview-content"><table className="inventory-table payment-listing-preview-table"><thead><tr>{listingPreview.headers.map((header)=><th key={header}>{header}</th>)}</tr></thead><tbody>{listingPreview.rows.map((row,rowIndex)=><tr key={rowIndex}>{row.map((cell,cellIndex)=><td key={cellIndex}>{cell}</td>)}</tr>)}</tbody>{listingPreview.totals?.length>0&&<tfoot><tr>{listingPreview.totals.map((cell,index)=><td key={index}>{cell}</td>)}</tr></tfoot>}</table></div></section></div>}
  {typeFilterOpen && <div className="transfer-confirm-overlay" onClick={() => setTypeFilterOpen(false)}><div className="transfer-confirm-modal customer-type-filter-modal" onClick={(event) => event.stopPropagation()}><button type="button" className="transfer-modal-close" onClick={() => setTypeFilterOpen(false)}>X</button><h3>Customer Types</h3><div className="customer-type-filter-options">{customerTypes.map((type) => { const id=String(type.id); return <label key={id} className="customer-type-filter-option"><input type="checkbox" checked={selectedCustomerTypeIds.includes(id)} onChange={() => setSelectedCustomerTypeIds((current) => current.includes(id)?current.filter((value)=>value!==id):[...current,id])} /><span>{type.name}</span></label>})}</div><div className="transfer-confirm-actions"><button type="button" className="button small transfer-confirm-cancel" onClick={() => setSelectedCustomerTypeIds([])}>Clear</button><button type="button" className="button small yellow" onClick={() => setSelectedCustomerTypeIds(customerTypes.map((type)=>String(type.id)))}>Select All</button><button type="button" className="button small blue" onClick={() => setTypeFilterOpen(false)}>Done</button></div></div></div>}
  {invoicePreviewModal.open && <div className="invoice-modal-overlay print-modal-overlay" onClick={closeInvoicePreviewModal}><div className="invoice-modal print-modal payment-invoice-preview-modal" onClick={(event) => event.stopPropagation()}><section className="shipdoc-invoice-workspace"><div className="invoice-modal-header"><strong>{printableInvoicePreviewDocument ? `${printableInvoicePreviewDocument.documentTitle || printableInvoicePreviewDocument.type || 'Invoice'} #${printableInvoicePreviewDocument.invoiceNo}` : `Invoice #${invoicePreviewModal.invoice?.invoiceNo || invoicePreviewModal.invoice?.docNo || invoicePreviewModal.invoice?.id || ''}`}</strong><div className="invoice-modal-actions"><InvoicePrintEmailActions invoice={printableInvoicePreviewDocument} disabled={!printableInvoicePreviewDocument || invoicePreviewModal.loading} loading={invoiceEmailLoading} onLoadingChange={setInvoiceEmailLoading} onPrint={printInvoicePreviewOutput} printLabel="Print" successMessage={(to)=>`${printableInvoicePreviewDocument?.documentTitle || printableInvoicePreviewDocument?.type || 'Invoice'} #${printableInvoicePreviewDocument?.invoiceNo} emailed to ${to}.`} onError={(error)=>setStatus(error?.message || error || 'Could not email invoice.')} />{paymentInvoiceCanEditServices(invoicePreviewModal.invoice, printableInvoicePreviewDocument) && <button type="button" className="button small blue" onClick={() => openInvoiceServiceModal('preview')} disabled={invoicePreviewModal.loading || paymentInvoiceEditor.open}>{invoiceServiceButtonLabel(printableInvoicePreviewDocument)}</button>}{paymentInvoiceCanEdit(invoicePreviewModal.invoice, printableInvoicePreviewDocument) && <button type="button" className="button small yellow" onClick={paymentInvoiceEditor.open ? savePaymentInvoiceEditor : openPaymentInvoiceEditor} disabled={invoicePreviewModal.loading || paymentInvoiceEditor.saving}>{paymentInvoiceEditor.open ? paymentInvoiceEditor.saving ? 'Saving...' : 'Save Invoice' : 'Edit Invoice'}</button>}<button type="button" className="button small red" onClick={paymentInvoiceEditor.open ? cancelPaymentInvoiceEditor : closeInvoicePreviewModal} disabled={paymentInvoiceEditor.open && paymentInvoiceEditor.saving}>{paymentInvoiceEditor.open ? 'Cancel' : 'Close'}</button></div></div>{invoicePreviewModal.loading && <p className="invoice-modal-status">Loading invoice...</p>}{!invoicePreviewModal.loading && invoicePreviewModal.error && <p className="invoice-modal-status error">{invoicePreviewModal.error}</p>}{paymentInvoiceEditor.open && paymentInvoiceEditor.error && <p className="shipdoc-invoice-edit-error">{paymentInvoiceEditor.error}</p>}{!invoicePreviewModal.loading && printableInvoicePreviewDocument && <ShipDocInvoiceDocument invoice={printableInvoicePreviewDocument} editable={paymentInvoiceEditor.open} canEditTax={!paymentInvoiceEditorIsRepair} canEditMemo={!paymentInvoiceEditorIsRepair} canEditLineAmount={!paymentInvoiceEditorIsRepair} editDraft={paymentInvoiceEditor.open ? paymentInvoiceEditor.draft : null} taxOptions={invoiceEditTaxOptions(preferencesTaxRate, paymentInvoiceEditor.draft?.taxRate)} billToLookup={paymentInvoiceEditor.open && !paymentInvoiceEditorIsRepair ? { query: paymentInvoiceEditor.draft?.customerQuery || '', open: invoiceCustomerLookupOpen, options: invoiceCustomerLookupOptions, activeIndex: invoiceCustomerLookupIndex, onQueryChange: updatePaymentInvoiceCustomerQuery, onFocus: () => setInvoiceCustomerLookupOpen(true), onBlur: () => setTimeout(() => setInvoiceCustomerLookupOpen(false), 120), onKeyDown: handlePaymentInvoiceCustomerLookupKeyDown, onSelect: selectPaymentInvoiceCustomer, onActiveIndexChange: setInvoiceCustomerLookupIndex, getLabel: customerName, getMeta: (customer) => customerPhoneLine(customer) || customerTypeLabel(customer, customerTypes) } : null} onDraftItemChange={updatePaymentInvoiceDraftItem} onDraftItemAdd={addPaymentInvoiceDraftItem} onDraftItemRemove={removePaymentInvoiceDraftItem} onDraftMemoChange={updatePaymentInvoiceDraftMemo} onDraftTaxRateChange={updatePaymentInvoiceDraftTaxRate} canEditLineText={paymentInvoiceEditor.open && paymentInvoiceEditorIsCustom} canAddInvoiceLines={paymentInvoiceEditor.open && paymentInvoiceEditorIsCustom} />}{canDeleteShipDocInvoice(invoicePreviewModal.invoice, printableInvoicePreviewDocument) && <div className="invoice-modal-bottom-actions"><button type="button" className="button small red" onClick={() => openDeleteInvoiceConfirm({...invoicePreviewModal.invoice, docNo: invoiceDeleteNumber(invoicePreviewModal.invoice, printableInvoicePreviewDocument)})} disabled={invoicePreviewModal.loading || paymentInvoiceEditor.saving}>Delete Invoice</button></div>}</section></div></div>}
  {taxEditor.open && <div className="invoice-alert-overlay" onClick={closeTaxEditor}><form className="invoice-tax-modal" onSubmit={saveInvoiceTax} onClick={(event) => event.stopPropagation()}><div className="invoice-tax-modal-title"><h3>Edit Invoice Tax</h3><button type="button" onClick={closeTaxEditor} disabled={taxEditor.saving}>x</button></div><label>Tax<select value={taxEditor.value} onChange={(event)=>setTaxEditor((current)=>({...current,value:event.target.value,error:''}))} autoFocus required><option value="0">0%</option>{preferencesTaxRate > 0 && <option value={String(preferencesTaxRate)}>{String(preferencesTaxRate)}%</option>}</select></label>{taxEditor.error && <p className="invoice-tax-error">{taxEditor.error}</p>}<div className="invoice-tax-actions"><button type="button" className="button small gray" onClick={closeTaxEditor} disabled={taxEditor.saving}>Cancel</button><button type="submit" className="button small blue" disabled={taxEditor.saving}>{taxEditor.saving ? 'Applying...' : 'Apply Tax'}</button></div></form></div>}
  {deleteInvoiceConfirm.open && <div className="transfer-confirm-overlay" onClick={closeDeleteInvoiceConfirm}><form className="transfer-confirm-modal payment-delete-invoice-confirm" onSubmit={submitDeleteInvoice} onClick={(event)=>event.stopPropagation()}><button type="button" className="transfer-modal-close" onClick={closeDeleteInvoiceConfirm}>x</button><h3>Delete Invoice</h3><p>Type invoice number <strong>{deleteInvoiceConfirm.invoice?.docNo}</strong> to return applied payments and delete this invoice.</p><input className="input" value={deleteInvoiceConfirm.value} onChange={(event)=>setDeleteInvoiceConfirm((current)=>({...current,value:event.target.value,error:''}))} autoFocus />{deleteInvoiceConfirm.error&&<p className="transfer-confirm-error">{deleteInvoiceConfirm.error}</p>}<div className="transfer-confirm-actions"><button type="button" className="button small transfer-confirm-cancel" onClick={closeDeleteInvoiceConfirm}>Cancel</button><button type="submit" className="button small red" disabled={deleteInvoiceConfirm.loading || String(deleteInvoiceConfirm.value || '').trim() !== String(deleteInvoiceConfirm.invoice?.docNo || '').trim()}>{deleteInvoiceConfirm.loading?'Deleting...':'Delete'}</button></div></form></div>}
  {deletePaymentConfirm.open && <div className="transfer-confirm-overlay payment-delete-payment-overlay" onClick={closeDeletePaymentConfirm}><form className="transfer-confirm-modal payment-delete-payment-confirm" onSubmit={submitDeletePayment} onClick={(event)=>event.stopPropagation()}><button type="button" className="transfer-modal-close" onClick={closeDeletePaymentConfirm}>x</button><h3>Void Payment</h3><p>Type payment number <strong>{deletePaymentConfirm.payment?.id}</strong> to void this payment and unapply it from all invoices.</p><input className="input" value={deletePaymentConfirm.value} onChange={(event)=>setDeletePaymentConfirm((current)=>({...current,value:event.target.value,error:''}))} autoFocus />{deletePaymentConfirm.error&&<p className="transfer-confirm-error">{deletePaymentConfirm.error}</p>}<div className="transfer-confirm-actions"><button type="button" className="button small transfer-confirm-cancel" onClick={closeDeletePaymentConfirm}>Cancel</button><button type="submit" className="button small red" disabled={deletePaymentConfirm.loading || String(deletePaymentConfirm.value || '').trim() !== String(deletePaymentConfirm.payment?.id || '').trim()}>{deletePaymentConfirm.loading?'Voiding...':'Void Payment'}</button></div></form></div>}
  {customInvoiceModal.open && <div className="invoice-modal-overlay" onClick={closeCustomInvoice}><form className="payment-modal move-payment-modal custom-invoice-modal" onSubmit={submitCustomInvoice} onClick={(event)=>event.stopPropagation()}><header><div><h2>{customInvoiceModal.documentType === 'credit_memo' ? 'Add Custom Credit Memo' : 'Add Custom Invoice'}</h2><p>{customInvoiceModal.documentType === 'credit_memo' ? 'This credit memo will not reserve inventory or link to serial numbers.' : 'This invoice will not reserve inventory or link to serial numbers.'}</p></div><button type="button" onClick={closeCustomInvoice}>x</button></header><label className="move-customer-label">Customer<div className="customer-search-typeahead"><input className="input" value={customInvoiceModal.query} onChange={(event)=>{setCustomInvoiceModal((current)=>({...current,query:event.target.value,customer:null,error:''}));setCustomInvoiceLookupOpen(true);setCustomInvoiceLookupIndex(0);}} onFocus={()=>setCustomInvoiceLookupOpen(true)} onBlur={()=>setTimeout(()=>setCustomInvoiceLookupOpen(false),120)} onKeyDown={handleCustomInvoiceLookupKeyDown} placeholder="Search by name or phone" autoComplete="off" role="combobox" aria-expanded={customInvoiceLookupOpen} aria-controls="custom-invoice-customer-typeahead-menu"/>{customInvoiceLookupOpen&&customInvoiceLookupOptions.length>0&&<div className="customer-typeahead-menu" id="custom-invoice-customer-typeahead-menu" role="listbox">{customInvoiceLookupOptions.map((customer,index)=><button type="button" key={customer.id} className={index===customInvoiceLookupIndex?'active':''} onMouseDown={(event)=>event.preventDefault()} onMouseMove={()=>setCustomInvoiceLookupIndex(index)} onClick={()=>selectCustomInvoiceCustomer(customer)} role="option" aria-selected={index===customInvoiceLookupIndex}><span>{customerName(customer)}</span>{(customer.phone||customer.altphone)&&<small>{customer.phone||customer.altphone}</small>}</button>)}</div>}</div></label>{customInvoiceModal.customer&&<div className="move-customer-selection"><strong>{customerName(customInvoiceModal.customer)}</strong><span>{customInvoiceModal.customer.phone||customInvoiceModal.customer.altphone||''}</span></div>}<div className="custom-invoice-items"><div className="custom-invoice-items-header"><strong>Items</strong><button type="button" className="button small blue" onClick={addCustomInvoiceItem}>Add Item</button></div><div className="custom-invoice-items-table"><div className="custom-invoice-item-row custom-invoice-item-heading"><span>Item</span><span>Description</span><span>Rate</span><span>Amount</span><span></span></div>{customInvoiceModal.items.map((line,index)=><div className="custom-invoice-item-row" key={index}><input value={line.item} onChange={(event)=>updateCustomInvoiceItem(index,'item',event.target.value)} aria-label={`Item ${index+1}`} /><textarea rows="2" value={line.description} onChange={(event)=>updateCustomInvoiceItem(index,'description',event.target.value)} aria-label={`Description ${index+1}`} /><input type="text" inputMode="decimal" value={line.rate} onChange={(event)=>updateCustomInvoiceItem(index,'rate',event.target.value)} aria-label={`Rate ${index+1}`} /><input type="text" inputMode="decimal" min="0.01" step="0.01" value={line.amount} onChange={(event)=>updateCustomInvoiceItem(index,'amount',event.target.value)} aria-label={`Amount ${index+1}`} /><button type="button" className="button small red custom-invoice-remove-item" onClick={()=>removeCustomInvoiceItem(index)} aria-label={`Remove item ${index+1}`}>Remove</button></div>)}</div><div className="custom-invoice-total"><span>Total</span><strong>{currency(customInvoiceTotal)}</strong></div></div>{customInvoiceModal.error&&<p className="apply-payment-error" role="alert">{customInvoiceModal.error}</p>}<footer><button type="button" className="button small yellow" onClick={closeCustomInvoice}>Cancel</button><button className="button small blue" disabled={customInvoiceModal.loading}>{customInvoiceModal.loading?'Adding...':customInvoiceModal.documentType === 'credit_memo' ? 'Add Credit Memo' : 'Add Invoice'}</button></footer></form></div>}
  {moveModal.open && <div className="invoice-modal-overlay" onClick={()=>setMoveModal({open:false,payment:null,query:'',target:null,error:'',loading:false})}><form className="payment-modal move-payment-modal" onSubmit={submitMove} onClick={(event)=>event.stopPropagation()}><header><div><h2>Move Payment</h2><p>Payment #{moveModal.payment?.id} - {currency(moveModal.payment?.amount)} from {customerName(selected)}</p></div><button type="button" onClick={()=>setMoveModal({open:false,payment:null,query:'',target:null,error:'',loading:false})}>x</button></header><label className="move-customer-label">Move to customer<div className="customer-search-typeahead"><input className="input" value={moveModal.query} onChange={(event)=>{setMoveModal((current)=>({...current,query:event.target.value,target:null,error:''}));setMoveLookupOpen(true);setMoveLookupIndex(0);}} onFocus={()=>setMoveLookupOpen(true)} onBlur={()=>setTimeout(()=>setMoveLookupOpen(false),120)} onKeyDown={handleMoveLookupKeyDown} placeholder="Search by name or phone" autoComplete="off" role="combobox" aria-expanded={moveLookupOpen} aria-controls="move-payment-customer-typeahead-menu"/>{moveLookupOpen&&moveLookupOptions.length>0&&<div className="customer-typeahead-menu" id="move-payment-customer-typeahead-menu" role="listbox">{moveLookupOptions.map((customer,index)=><button type="button" key={customer.id} className={index===moveLookupIndex?'active':''} onMouseDown={(event)=>event.preventDefault()} onMouseMove={()=>setMoveLookupIndex(index)} onClick={()=>selectMoveCustomer(customer)} role="option" aria-selected={index===moveLookupIndex}><span>{customerName(customer)}</span>{(customer.phone||customer.altphone)&&<small>{customer.phone||customer.altphone}</small>}</button>)}</div>}</div></label>{moveModal.target&&<div className="move-customer-selection"><strong>{customerName(moveModal.target)}</strong><span>{moveModal.target.phone||moveModal.target.altphone||''}</span></div>}<p className="move-payment-warning">Moving this payment will remove its current invoice and serial-number applications.</p>{moveModal.error&&<p className="apply-payment-error" role="alert">{moveModal.error}</p>}<footer><button type="button" className="button small yellow" onClick={()=>setMoveModal({open:false,payment:null,query:'',target:null,error:'',loading:false})}>Cancel</button><button className="button small blue" disabled={moveModal.loading}>{moveModal.loading?'Moving...':'Move Payment'}</button></footer></form></div>}
  {refundModal.open && <div className="invoice-modal-overlay"><form className={`payment-modal move-payment-modal refund-payment-modal ${isCreditMemoPayment(refundModal.payment) ? 'credit-memo-refund-modal' : ''}`} onSubmit={submitRefund} onClick={(event)=>event.stopPropagation()}><header><div><h2>{isCreditMemoPayment(refundModal.payment) ? 'Issue a Refund' : 'Issue Refund'}</h2><p>{isCreditMemoPayment(refundModal.payment) ? `Credit Memo #${refundModal.payment?.referenceNo || refundModal.payment?.id} - ${currency(refundModal.payment?.availableAmount)} available` : `Payment #${refundModal.payment?.id} - ${currency(refundModal.payment?.amount)} from ${customerName(selected)}`}</p></div><button type="button" onClick={()=>setRefundModal({open:false,payment:null,choice:'remaining',error:'',loading:false})}>x</button></header>{isCreditMemoPayment(refundModal.payment) ? <section className="credit-memo-refund-fields"><label>A refund is due to<input readOnly value={customerName(selected)} /></label><label>Issue this refund via<select value={refundModal.method || 'Check'} onChange={(event)=>setRefundModal((current)=>({...current,method:event.target.value}))}><option>Check</option><option>Cash</option><option>Credit Card</option><option>Zelle</option><option>Wire</option><option>Shopify</option></select></label><label>Refund Amount<input type="text" inputMode="decimal" min="0.01" step="0.01" max={Number(refundModal.payment?.availableAmount || 0)} value={refundModal.amount || ''} onChange={(event)=>setRefundModal((current)=>({...current,amount:event.target.value,error:''}))} /></label><label>Account<select value={refundModal.account || "Irene's Wigs"} onChange={(event)=>setRefundModal((current)=>({...current,account:event.target.value}))}><option>Irene's Wigs</option></select></label><label>Date<input type="date" value={refundModal.receivedAt || today()} onChange={(event)=>setRefundModal((current)=>({...current,receivedAt:event.target.value}))} /></label><label>Ref/Check No.<input value={refundModal.referenceNo || ''} onChange={(event)=>setRefundModal((current)=>({...current,referenceNo:event.target.value}))} /></label><label className="credit-memo-refund-address">Address<textarea readOnly value={[selected?.billaddr1 || selected?.bill_addr_1 || selected?.address || '', selected?.billaddr2 || selected?.bill_addr_2 || '', [selected?.billcity || selected?.bill_city, selected?.billstate || selected?.bill_state, selected?.billzip || selected?.bill_zip].filter(Boolean).join(', ')].filter(Boolean).join('\n')} /></label><label className="credit-memo-refund-memo">Memo<input value={refundModal.memo || ''} onChange={(event)=>setRefundModal((current)=>({...current,memo:event.target.value}))} /></label><label className="credit-memo-refund-print"><input type="checkbox" checked={refundModal.toBePrinted !== false} onChange={(event)=>setRefundModal((current)=>({...current,toBePrinted:event.target.checked}))} /><span>To be printed</span></label></section> : <><fieldset className="refund-options"><legend>Refund amount</legend><label><input type="radio" name="refundChoice" value="full" checked={refundModal.choice==='full'} onChange={()=>setRefundModal((current)=>({...current,choice:'full',error:''}))}/><span><strong>Full Refund</strong><small>{currency(Number(refundModal.payment?.amount||0)-Number(refundModal.payment?.refundedAmount||0))} - removes all invoice and serial-number applications</small></span></label><label><input type="radio" name="refundChoice" value="remaining" checked={refundModal.choice==='remaining'} disabled={!Number(refundModal.payment?.availableAmount||0)} onChange={()=>setRefundModal((current)=>({...current,choice:'remaining',error:''}))}/><span><strong>Remaining Balance</strong><small>{currency(refundModal.payment?.availableAmount)} - refunds only the unapplied credit balance</small></span></label></fieldset>{refundModal.choice==='full'&&Number(refundModal.payment?.appliedAmount||0)>0&&<p className="move-payment-warning">Full Refund will remove {currency(refundModal.payment?.appliedAmount)} from its current invoice and serial-number applications.</p>}</>}{refundModal.error&&<p className="apply-payment-error" role="alert">{refundModal.error}</p>}<footer><button type="button" className="button small yellow" onClick={()=>setRefundModal({open:false,payment:null,choice:'remaining',error:'',loading:false})}>Cancel</button><button className="button small blue" disabled={refundModal.loading}>{refundModal.loading?'Refunding...':`Refund ${currency(isCreditMemoPayment(refundModal.payment) ? refundModal.amount : refundModal.choice==='full'?Number(refundModal.payment?.amount||0)-Number(refundModal.payment?.refundedAmount||0):refundModal.payment?.availableAmount)}`}</button></footer></form></div>}
  {creditApplyModal.open && <div className="invoice-modal-overlay payment-overlay" onClick={closeCreditApplyModal}><form className="payment-modal qb-payment-modal credit-apply-payment-modal" onSubmit={saveCreditApplication} onClick={(event)=>event.stopPropagation()}><header><h2>Apply Payment/Credits</h2><div className="qb-customer-balance"><small>CUSTOMER BALANCE</small><strong>{currency(paymentModalInvoices.reduce((sum, invoice) => sum + Number(invoice.amountDue || 0), 0))}</strong></div><button type="button" onClick={closeCreditApplyModal}>x</button></header><section className="qb-payment-top"><div className="qb-payment-fields"><label>Received From<input readOnly value={customerName(selected)} /></label><label>Payment Amount<input readOnly value={selectedCreditApplyOriginalTotal ? selectedCreditApplyOriginalTotal.toFixed(2) : ''} /></label><label>Date<input type="date" readOnly value={selectedCreditApplyOriginalDate || today()} /></label><label>Reference #<input readOnly value={creditApplyModal.sourcePayment?.referenceNo || creditApplyModal.sourcePayment?.id || ''} /></label></div><div className="qb-methods credit-apply-methods">{[['Cash','$'],['Check','✓'],['Credit Card','▣'],['Zelle','Z'],['Wire','⇄'],['Shopify','S']].map(([type,icon]) => <button type="button" key={type} disabled className={type === 'Check' ? 'active' : ''}><b>{icon}</b><span>{type}</span></button>)}<div className="credit-apply-picker-field"><span className="credit-apply-picker-label">Available Payments</span><div ref={creditApplyPickerRef} className={`payment-credit-picker credit-apply-picker ${creditApplyModal.creditMenuOpen?'open':''}`}><button type="button" className="payment-credit-picker-button" onClick={() => setCreditApplyModal((current)=>({...current,creditMenuOpen:!current.creditMenuOpen}))}>{creditApplyLabel}</button>{creditApplyModal.creditMenuOpen && <div className="payment-credit-picker-menu">{walletPayments.map((payment) => { const id=String(payment.id); const checked=creditApplyPaymentIds.includes(id); return <div key={payment.id} className={`payment-credit-option ${checked?'selected':''}`}><label><input type="checkbox" checked={checked} onChange={() => toggleCreditApplyPayment(id)} /><span className="payment-credit-option-text"><span>{paymentCreditDate(payment)} - #{payment.referenceNo || payment.id} - {payment.paymentType}</span><strong>{currency(payment.availableAmount)} available</strong></span></label><input className="payment-credit-amount" type="text" inputMode="decimal" min="0" step="0.01" max={Number(payment.availableAmount || 0)} value={creditApplyPaymentAmounts[id] || ''} disabled={!checked} onClick={(event)=>event.stopPropagation()} onChange={(event)=>setCreditApplyPaymentAmount(id,event.target.value)} aria-label={`Amount to use from payment credit ${payment.id}`} /></div>})}{walletPayments.length===0 && <span className="payment-credit-empty">No unapplied credit balance</span>}</div>}</div></div></div></section><label className="qb-notes">Notes<textarea readOnly value={`Applying credits for ${customerName(selected)}`} /></label><div className="qb-invoice-table-wrap"><table className="qb-invoice-table"><thead><tr><th>Apply to Invoice</th><th>Date</th><th>Number</th><th>Type</th><th>Orig. Amt.</th><th>Amt. Due</th><th>Payment</th></tr></thead><tbody>{paymentModalInvoices.map((invoice) => <React.Fragment key={invoice.id}><tr><td><input type="checkbox" checked={isAvailablePaymentMode ? paymentModalSelectedInvoiceIds.map(String).includes(String(invoice.id)) : Number(allocations[allocationKey(invoice.id)] || 0) > 0} onChange={(event)=>toggleInvoiceAllocation(invoice,event.target.checked)} /></td><td>{formatCalendarDate(invoice.date)}</td><td><button type="button" className="payment-invoice-link" onClick={() => detailFor(invoice)}>{invoice.invoiceNo}</button></td><td>{invoice.type}</td><td>{currency(invoice.originalAmount)}</td><td>{currency(invoicePaymentLimit(invoice))}</td><td><input aria-label={`Apply credits to invoice ${invoice.invoiceNo}`} type="text" inputMode="decimal" min="0" step="0.01" max={invoicePaymentLimit(invoice)} value={allocations[allocationKey(invoice.id)] || ''} onChange={(event)=>setPaymentAllocation(allocationKey(invoice.id),event.target.value)} /></td></tr>{details[invoice.id]?.items?.map((item,index) => { const key=allocationKey(invoice.id, item.allocationKey || `${item.lineKey ?? item.id ?? index}|${item.serialNo || item.serial || ''}`); return <tr className="payment-line-row" key={key}><td></td><td colSpan="3">-&gt; {item.referenceNo || item.serialNo || item.serial || 'No serial'} - {item.description}</td><td>{currency(Number(item.quantity || 1)*Number(item.price || 0))}</td><td>{currency(item.paymentLimit ?? item.dueAmount ?? 0)}</td><td><input type="text" inputMode="decimal" min="0" step="0.01" max={Number(item.paymentLimit ?? item.dueAmount ?? item.lineTotal ?? 0)} placeholder="Apply" value={allocations[key] || ''} onChange={(event)=>setPaymentAllocation(key,event.target.value)} /></td></tr>})}</React.Fragment>)}</tbody><tfoot><tr><td colSpan="4">Totals</td><td>{currency(paymentModalInvoices.reduce((sum, invoice)=>sum+Number(invoice.originalAmount||0),0))}</td><td>{currency(paymentModalInvoices.reduce((sum, invoice)=>sum+invoicePaymentLimit(invoice),0))}</td><td>{currency(allocationTotal)}</td></tr></tfoot></table></div><section className="qb-payment-summary"><div><small>AMOUNTS FOR SELECTED INVOICES</small><p>Amount Due <strong>{currency(selectedPaymentAmountDue)}</strong></p><p>Applied <strong>{currency(allocationTotal)}</strong></p></div></section>{creditApplyAllocationWarning&&<p className="apply-payment-error" role="alert">{creditApplyAllocationWarning}</p>}<footer><div className="qb-payment-footer-left"></div><div className="qb-payment-footer-actions"><button type="button" className="button small yellow" onClick={closeCreditApplyModal}>Cancel</button><button type="submit" className="button small blue" disabled={creditApplyModal.loading || !canSaveCreditApplication}>{creditApplyModal.loading?'Saving...':'Save Changes'}</button></div></footer></form></div>}
  {modal && <div className={`invoice-modal-overlay payment-overlay ${paymentModalMode === 'compact' ? 'compact-payment-overlay' : ''}`} onClick={closePaymentModal}><form className={`payment-modal qb-payment-modal ${paymentModalMode === 'compact' ? 'compact-qb-payment-modal' : ''} ${isAvailablePaymentMode ? 'available-payment-modal' : ''}`} onSubmit={activePaymentReview ? savePaymentReview : isAvailablePaymentMode ? saveCreditApplication : savePayment} onClick={(e) => e.stopPropagation()}><header><h2>{isAvailablePaymentMode ? 'Apply Payment/Credits' : 'Customer Payment'}</h2><div className="qb-customer-balance"><small>CUSTOMER BALANCE</small><strong>{currency(paymentModalInvoices.reduce((sum, invoice) => sum + Number(invoice.amountDue || 0), 0))}</strong></div><button type="button" onClick={closePaymentModal}>x</button></header><section className="qb-payment-top"><div className="qb-payment-fields"><label>Received From<input readOnly value={customerName(selected)} /></label><label>Payment Amount<input type="text" inputMode="decimal" min="0.01" step="0.01" required readOnly={paymentHeaderReadOnly} value={paymentAmountValue} onChange={(e) => setForm({...form, amount:e.target.value})} /></label><label>Date<input type="date" value={paymentModalDateValue} readOnly={paymentHeaderReadOnly} onChange={(e) => setForm({...form,receivedAt:e.target.value})} /></label><label>Reference #<input value={form.referenceNo} readOnly={paymentHeaderReadOnly} onChange={(e) => setForm({...form,referenceNo:e.target.value})} /></label></div><div className={`qb-methods ${isAvailablePaymentMode ? 'available-payment-methods' : ''}`}>{paymentMethodControls}</div></section><label className="qb-notes">Notes<textarea value={form.notes} readOnly={paymentHeaderReadOnly} onChange={(e) => setForm({...form,notes:e.target.value})} /></label>{!isAvailablePaymentMode && <div className="qb-payment-table-actions"><button type="button" className="button small blue" onClick={savePaymentBeforePrint} disabled={paymentActionDisabled}>Print</button><EmailActions endpoint={activePaymentReview ? `/payments/${activePaymentReview.id}/email` : ``} onBeforeOpen={savePaymentBeforeEmail} templateKey="payment" templateValues={{document: `Payment Receipt`, documentNumber: ` #${paymentReceiptNumber}`, invoiceNo: paymentReceiptNumber, recipientName: customerName(selected), customerName: customerName(selected)}} recipient={selected?.email || ``} subject={`Payment Receipt #${paymentReceiptNumber}`} quickTitle="Send Payment Receipt" quickPrompt="Send payment receipt to:" attachmentPdf={`Payment_Receipt_${paymentReceiptNumber}.pdf`} attachmentExcel={`Payment_Receipt_${paymentReceiptNumber}.xls`} disabled={paymentActionDisabled} loading={paymentEmailLoading} onLoadingChange={setPaymentEmailLoading} successMessage={(to)=>`Payment Receipt #${paymentReceiptNumber} emailed to ${to}.`} onError={(error)=>setStatus(error?.message || error || `Could not email payment receipt.`)} /></div>}<div className="qb-invoice-table-wrap"><table className="qb-invoice-table"><thead><tr><th>Apply to Invoice</th><th>Date</th><th>Number</th><th>Type</th><th>Orig. Amt.</th><th>Amt. Due</th><th>Payment</th></tr></thead><tbody>{paymentModalInvoices.map((invoice) => <React.Fragment key={invoice.id}><tr className={String(paymentModalHighlightedInvoiceId || '') === String(invoice.id) ? 'highlighted-payment-invoice' : ''}><td><input type="checkbox" checked={isAvailablePaymentMode ? paymentModalSelectedInvoiceIds.map(String).includes(String(invoice.id)) : Number(allocations[allocationKey(invoice.id)] || 0) > 0} onChange={(e) => toggleInvoiceAllocation(invoice, e.target.checked)} /></td><td>{formatCalendarDate(invoice.date)}</td><td><button type="button" className="payment-invoice-link" onClick={() => detailFor(invoice)}>{invoice.invoiceNo}</button></td><td>{invoice.type}</td><td>{currency(invoice.originalAmount)}</td><td>{currency(invoicePaymentLimit(invoice))}</td><td><input aria-label={`Apply to invoice ${invoice.invoiceNo}`} type="text" inputMode="decimal" min="0" step="0.01" max={invoicePaymentLimit(invoice)} value={allocations[allocationKey(invoice.id)] || ''} onChange={(e) => setPaymentAllocation(allocationKey(invoice.id), e.target.value)} /></td></tr>{details[invoice.id]?.items?.map((item,index) => { const key=allocationKey(invoice.id, item.allocationKey || `${item.lineKey ?? item.id ?? index}|${item.serialNo || item.serial || ''}`); return <tr className="payment-line-row" key={key}><td></td><td colSpan="3">-> {item.referenceNo || item.serialNo || item.serial || 'No serial'} - {item.description}</td><td>{currency(Number(item.quantity || 1)*Number(item.price || 0))}</td><td>{currency(item.paymentLimit ?? item.dueAmount ?? 0)}</td><td><input type="text" inputMode="decimal" min="0" step="0.01" max={Number(item.paymentLimit ?? item.dueAmount ?? item.lineTotal ?? 0)} placeholder="Apply" value={allocations[key] || ''} onChange={(e) => setPaymentAllocation(key, e.target.value)} /></td></tr>})}</React.Fragment>)}</tbody><tfoot><tr><td colSpan="4">Totals</td><td>{currency(paymentModalInvoices.reduce((s,i)=>s+Number(i.originalAmount||0),0))}</td><td>{currency(paymentModalInvoices.reduce((s,i)=>s+invoicePaymentLimit(i),0))}</td><td>{currency(allocationTotal)}</td></tr></tfoot></table></div><section className="qb-payment-summary"><div><small>AMOUNTS FOR SELECTED INVOICES</small><p>Amount Due <strong>{currency(selectedPaymentAmountDue)}</strong></p><p>Applied <strong>{currency(allocationTotal)}</strong></p></div></section>{(isAvailablePaymentMode ? creditApplyAllocationWarning : paymentAllocationWarning)&&<p className="apply-payment-error" role="alert">{isAvailablePaymentMode ? creditApplyAllocationWarning : paymentAllocationWarning}</p>}{creditApplyModal.error&&isAvailablePaymentMode&&!creditApplyAllocationWarning&&<p className="apply-payment-error" role="alert">{creditApplyModal.error}</p>}{status&&modal&&<p className="payment-modal-status" role="alert">{status}</p>}<footer><div className="qb-payment-footer-left">{canVoidPayment && <button type="button" className="button small red" onClick={openDeletePaymentConfirm} disabled={!activePaymentReview}>Void Payment</button>}</div>{activePaymentReview ? <div className="qb-payment-footer-actions"><button type="button" className="button small yellow" onClick={closePaymentModal}>Cancel</button><button type="button" className="button small blue" disabled={loading || !canSavePayment || !paymentHasChanges} onClick={savePaymentReview}>{loading?'Saving...':'Save Changes'}</button></div> : isAvailablePaymentMode ? <div className="qb-payment-footer-actions"><button type="button" className="button small yellow" onClick={closePaymentModal}>Cancel</button><button type="submit" className="button small blue" disabled={creditApplyModal.loading || !canSaveUniversalPayment}>{creditApplyModal.loading?'Saving...':'Save Changes'}</button></div> : <div className="qb-payment-footer-actions"><button type="button" className="button small yellow" onClick={closePaymentModal}>Clear</button><button type="button" className="button small blue" disabled={loading || !canSaveUniversalPayment} onClick={(event) => savePayment(event,false)}>{loading?'Saving...':'Save & New'}</button><button type="button" className="button small blue" disabled={loading || !canSaveUniversalPayment} onClick={(event) => savePayment(event,true)}>{loading?'Saving...':'Save & Close'}</button></div>}</footer></form></div>}
  {applyModal.open && <div className="invoice-modal-overlay payment-apply-overlay" onClick={closeApplyModal}><form className="payment-modal apply-payment-modal" onSubmit={saveInvoiceApplication} onClick={(event) => event.stopPropagation()}><header><div><h2>Invoice</h2></div><div className="apply-payment-document-actions"><InvoicePrintEmailActions invoice={printableApplyInvoiceDocument} disabled={!printableApplyInvoiceDocument || applyModal.loading} loading={invoiceEmailLoading} onLoadingChange={setInvoiceEmailLoading} onPrint={applyAndPrintInvoice} printLabel="Print" successMessage={(to)=>`${printableApplyInvoiceDocument?.type || 'Invoice'} #${printableApplyInvoiceDocument?.invoiceNo} emailed to ${to}.`} onError={(error)=>setStatus(error?.message || 'Could not email invoice.')} />{paymentInvoiceCanEditServices(applyModal.invoice, printableApplyInvoiceDocument) && <button type="button" className="button small blue" onClick={() => openInvoiceServiceModal('apply')} disabled={applyModal.loading}>{invoiceServiceButtonLabel(printableApplyInvoiceDocument)}</button>}{paymentInvoiceCanEditTax(applyModal.invoice, printableApplyInvoiceDocument) && <button type="button" className="button small yellow" onClick={() => openTaxEditor('apply')} disabled={applyModal.loading}>Edit Tax</button>}</div><button type="button" onClick={closeApplyModal}>x</button></header>{applyModal.loading && !applyModal.detail ? <p>Loading invoice...</p> : <><section className="apply-payment-document-info"><div className="apply-payment-customer-box"><h3>Customer Information</h3><div>{applyInvoiceCustomerLines.map((line,index)=><span key={index}>{line}</span>)}</div></div><table className="apply-payment-meta-box"><thead><tr><th>Date</th><th>Invoice #</th></tr></thead><tbody><tr><td>{applyInvoiceDocumentDate}</td><td>{applyInvoiceDocumentNumber}</td></tr></tbody></table></section><div className="apply-payment-summary"><button type="button" className="button small blue apply-receive-payment-button" onClick={openPaymentForApplyInvoice} disabled={!canApplyPaymentToInvoice(applyModal.invoice)}>Receive Payment</button><label>Apply Payment/Credits<div ref={creditPickerRef} className={`payment-credit-picker ${applyModal.creditMenuOpen?'open':''}`}><button type="button" className="payment-credit-picker-button" onClick={() => setApplyModal((current)=>({...current,creditMenuOpen:!current.creditMenuOpen}))}>{selectedCreditLabel}</button>{applyModal.creditMenuOpen && <div className="payment-credit-picker-menu">{walletPayments.map((payment) => { const id=String(payment.id); const checked=selectedPaymentIds.includes(id); return <div key={payment.id} className={`payment-credit-option ${checked?'selected':''}`}><label><input type="checkbox" checked={checked} onChange={() => toggleApplyPayment(id)} /><span>{paymentCreditDate(payment)} - #{payment.id} - {payment.paymentType} - {currency(payment.availableAmount)} available</span></label><input className="payment-credit-amount" type="text" inputMode="decimal" min="0" step="0.01" max={Number(payment.availableAmount || 0)} value={selectedPaymentAmounts[id] || ''} disabled={!checked} onClick={(event)=>event.stopPropagation()} onChange={(event)=>setApplyPaymentAmount(id,event.target.value)} aria-label={`Amount to use from payment ${payment.id}`} /></div>})}{walletPayments.length===0 && <span className="payment-credit-empty">No unapplied credit balance</span>}</div>}</div></label><div className="apply-payment-action-area"><div className="apply-payment-action-buttons"><button className="button small blue" disabled={applyModal.loading || !selectedPaymentCredits.length || !canApplyPaymentToInvoice(applyModal.invoice)}>Apply Payment</button></div></div></div>{walletPayments.length === 0 && <p className="payment-status">This customer has no unapplied credit balance.</p>}{applyModal.error && <p className="apply-payment-error" role="alert">{applyModal.error}</p>}<div className="apply-payment-toolbar"><button type="button" className="button small blue" onClick={payInvoiceTotal} disabled={!selectedWalletPayments.length || !Number(applyModal.detail?.amountDue) || !canApplyPaymentToInvoice(applyModal.invoice)}>Pay Total {currency(applyModal.detail?.amountDue)}</button></div><table className="inventory-table apply-payment-lines"><thead><tr><th>Serial #</th><th>Item / Service</th><th>Rate</th><th>Paid</th><th>Balance</th><th>Apply Payment</th></tr></thead><tbody>{applyModal.detail?.items?.map((item) => applyLinePaymentDetailAvailable ? <tr key={item.allocationKey} className={item.paidAmount>0&&item.dueAmount>0?'partially-paid':''}><td>{paymentLineSerial(item) || 'No serial'}</td><td>{item.description || 'Item'}</td><td>{currency(item.lineTotal)}</td><td>{currency(item.paidAmount)}</td><td>{currency(item.dueAmount)}</td><td>{item.dueAmount>0?<input type="text" inputMode="decimal" min="0" step="0.01" max={Number(item.dueAmount)} value={applyModal.amounts[item.allocationKey] || ''} onChange={(event) => setLineApplyAmount(item.allocationKey,event.target.value)} />:<span className="paid-in-full">Paid</span>}</td></tr> : <tr key={item.allocationKey}><td>{paymentLineSerial(item) || 'No serial'}</td><td>{item.description || 'Item'}</td><td>{currency(item.lineTotal)}</td><td></td><td></td><td></td></tr>)}</tbody></table><div className="apply-payment-invoice-footer"><div><span>Total</span><strong>{currency(applyInvoiceTotal)}</strong></div><div><span>Payments/Credits</span><strong>{currency(applyInvoicePaymentsCredits)}</strong></div><div><span>Balance Due</span><strong>{currency(applyInvoiceBalanceDue)}</strong></div></div></>}</form></div>}
  {applyModal.open && printableApplyInvoiceDocument && <div className="invoice-modal-overlay print-modal-overlay payment-apply-print-preview" aria-hidden="true"><div className="invoice-modal print-modal" onClick={(event) => event.stopPropagation()}><section className="shipdoc-invoice-workspace"><ShipDocInvoiceDocument invoice={printableApplyInvoiceDocument} /></section></div></div>}
  {invoiceServiceModal.open && (
    <div className="invoice-alert-overlay">
      <div className="invoice-service-modal" onClick={(event) => event.stopPropagation()}>
        <div className="invoice-service-modal-title">
          <h3>{invoiceServicesAreRepair ? 'Select Services' : 'Select Services/Discounts'}</h3>
          <strong>{invoiceServiceModal.serviceIds.length + invoiceServiceModal.discountIds.length} selected</strong>
        </div>
        <div className="invoice-service-tabs-row">
          <div className="invoice-service-tabs" role="tablist" aria-label="Invoice adjustments">
            <button
              type="button"
              role="tab"
              aria-selected={invoiceServiceModal.activeTab === 'services'}
              className={invoiceServiceModal.activeTab === 'services' ? 'active' : ''}
              onClick={() => setInvoiceServiceModalTab('services')}
            >
              Services
              {invoiceServiceModal.serviceIds.length > 0 && <span>{invoiceServiceModal.serviceIds.length}</span>}
            </button>
            {!invoiceServicesAreRepair && <button
              type="button"
              role="tab"
              aria-selected={invoiceServiceModal.activeTab === 'discounts'}
              className={invoiceServiceModal.activeTab === 'discounts' ? 'active' : ''}
              onClick={() => setInvoiceServiceModalTab('discounts')}
            >
              Discounts
              {invoiceServiceModal.discountIds.length > 0 && <span>{invoiceServiceModal.discountIds.length}</span>}
            </button>}
          </div>
          <div className="invoice-service-modal-actions">
            <button type="button" className="button small gray" onClick={closeInvoiceServiceModal} disabled={invoiceServiceModal.saving}>Cancel</button>
            <button type="button" className="button small blue" onClick={saveInvoiceServices} disabled={invoiceServiceModal.saving}>{invoiceServiceModal.saving ? 'Saving...' : 'Save'}</button>
          </div>
        </div>
        <div className="invoice-service-list">
          {invoiceServiceModal.activeTab === 'services' && (
            <>
              {invoiceServiceOptions.map((service) => {
                const id = getServiceKey(service);
                const selectedService = invoiceServiceModal.serviceIds.includes(id);
                const usesExplicitBase = isExplicitBaseServiceName(service.name);
                return (
                  <div key={id} className={`invoice-service-option${usesExplicitBase ? ' invoice-service-option-cc' : ''}`}>
                    <input type="checkbox" checked={selectedService} onChange={() => toggleInvoiceService(id)} />
                    <span>{service.name}</span>
                    <label className="invoice-adjustment-value">
                      <small>{String(invoiceServiceModal.serviceValues?.[id] || serviceAdjustmentInput(service)).includes('%') ? '%' : '$'}</small>
                      <input
                        type="text"
                        disabled={!selectedService}
                        value={invoiceServiceModal.serviceValues?.[id] ?? serviceAdjustmentInput(service)}
                        onChange={(event) => setInvoiceServiceValue(id, event.target.value)}
                        aria-label={`${service.name} invoice value`}
                      />
                    </label>
                    {usesExplicitBase && (
                      <label className="invoice-adjustment-value invoice-adjustment-base">
                        <small>$</small>
                        <input
                          type="text"
                          disabled={!selectedService}
                          value={invoiceServiceModal.serviceBaseAmounts?.[id] ?? ''}
                          onChange={(event) => setInvoiceServiceBaseAmount(id, event.target.value)}
                          aria-label={`${service.name} amount`}
                          placeholder="Amount"
                        />
                      </label>
                    )}
                  </div>
                );
              })}
              {otherServiceOptions.length === 0 && <p className="invoice-service-empty">No Other Services found.</p>}
            </>
          )}
          {invoiceServiceModal.activeTab === 'discounts' && (
            <>
              {discounts.map((discount) => {
                const id = getDiscountKey(discount);
                const selectedDiscount = invoiceServiceModal.discountIds.includes(id);
                return (
                  <div key={id} className="invoice-service-option">
                    <input type="checkbox" checked={selectedDiscount} onChange={() => toggleInvoiceDiscount(id)} />
                    <span>{discount.item}</span>
                    <label className="invoice-adjustment-value">
                      <small>{String(invoiceServiceModal.discountValues?.[id] || discountAdjustmentInput(discount)).includes('%') ? '%' : '$'}</small>
                      <input
                        type="text"
                        disabled={!selectedDiscount}
                        value={invoiceServiceModal.discountValues?.[id] ?? discountAdjustmentInput(discount)}
                        onChange={(event) => setInvoiceDiscountValue(id, event.target.value)}
                        aria-label={`${discount.item} invoice value`}
                      />
                    </label>
                  </div>
                );
              })}
              {discounts.length === 0 && <p className="invoice-service-empty">No discounts found.</p>}
            </>
          )}
        </div>
        {invoiceServiceModal.error && <p className="invoice-tax-error">{invoiceServiceModal.error}</p>}
      </div>
    </div>
  )}
  {creditMemoModal.open && <div className="invoice-modal-overlay print-modal-overlay" onClick={closeCreditMemoModal}><div className="invoice-modal print-modal payment-credit-memo-modal" onClick={(event) => event.stopPropagation()}><div className="invoice-modal-header"><strong>{creditMemoModal.transfer ? `${creditMemoModal.transfer.documentTitle || 'Credit Memo'} #${creditMemoModal.transfer.docNo}` : `Credit Memo #${creditMemoModal.invoice?.docNo || creditMemoModal.docNo}`}</strong><div className="invoice-modal-actions"><InvoicePrintEmailActions invoice={printableCreditMemoDocument} disabled={!printableCreditMemoDocument} loading={creditMemoEmailLoading} onPrint={printCreditMemoDocument} onLoadingChange={setCreditMemoEmailLoading} successMessage={(to)=>`${printableCreditMemoDocument?.documentTitle || 'Credit Memo'} #${printableCreditMemoDocument?.docNo || printableCreditMemoDocument?.invoiceNo || creditMemoModal.docNo} emailed to ${to}.`} onError={(error)=>setStatus(error?.message || 'Could not email credit memo.')} />{printableCreditMemoDocument && Number(printableCreditMemoDocument.transactionTypeId) === 10 && <button type="button" className="button small yellow" onClick={paymentCreditMemoEditing ? savePaymentCreditMemoEditor : openPaymentCreditMemoEditor} disabled={creditMemoModal.loading || paymentInvoiceEditor.saving}>{paymentCreditMemoEditing ? paymentInvoiceEditor.saving ? 'Saving...' : 'Save Memo' : 'Edit Memo'}</button>}<button type="button" className="button small red" onClick={paymentCreditMemoEditing ? cancelPaymentInvoiceEditor : closeCreditMemoModal} disabled={paymentCreditMemoEditing && paymentInvoiceEditor.saving}>{paymentCreditMemoEditing ? 'Cancel' : 'Close'}</button></div></div>{creditMemoModal.loading && <p className="invoice-modal-status">Loading credit memo...</p>}{!creditMemoModal.loading && creditMemoModal.error && <p className="invoice-modal-status error">{creditMemoModal.error}</p>}{paymentCreditMemoEditing && paymentInvoiceEditor.error && <p className="shipdoc-invoice-edit-error">{paymentInvoiceEditor.error}</p>}{!creditMemoModal.loading && creditMemoModal.transfer && <section className="shipdoc-invoice-workspace"><ShipDocInvoiceDocument transfer={creditMemoModal.transfer} editable={paymentCreditMemoEditing} editDraft={paymentCreditMemoEditing ? paymentInvoiceEditor.draft : null} taxOptions={invoiceEditTaxOptions(preferencesTaxRate, paymentInvoiceEditor.draft?.taxRate)} onDraftItemChange={updatePaymentInvoiceDraftItem} onDraftMemoChange={updatePaymentInvoiceDraftMemo} onDraftTaxRateChange={updatePaymentInvoiceDraftTaxRate} canEditAmounts={false} canEditTax={false} /></section>}{!creditMemoModal.loading && !creditMemoModal.transfer && creditMemoModal.invoice && <section className="shipdoc-invoice-workspace"><ShipDocInvoiceDocument invoice={creditMemoModal.invoice} editable={paymentCreditMemoEditing} editDraft={paymentCreditMemoEditing ? paymentInvoiceEditor.draft : null} taxOptions={invoiceEditTaxOptions(preferencesTaxRate, paymentInvoiceEditor.draft?.taxRate)} onDraftItemChange={updatePaymentInvoiceDraftItem} onDraftMemoChange={updatePaymentInvoiceDraftMemo} onDraftTaxRateChange={updatePaymentInvoiceDraftTaxRate} canEditAmounts={false} canEditTax={false} /></section>}</div></div>}
  {activity.payment && <div className="invoice-modal-overlay" onClick={() => setActivity({payment:null,rows:[]})}><section className="payment-modal activity-modal" onClick={(e) => e.stopPropagation()}><header><h2>{activityLogIsCreditMemo ? `Credit Memo #${activity.payment.referenceNo || activity.payment.id} History` : `Payment #${activity.payment.id} History`}</h2><button onClick={() => setActivity({payment:null,rows:[]})}>x</button></header><table className="inventory-table payment-activity-table"><thead><tr><th>Date / Time</th><th>Activity</th><th>Invoice</th><th>Serial #</th><th>Amount</th>{activityLogIsCreditMemo && <th>Ref/Check No.</th>}{activityLogIsCreditMemo && <th>Memo</th>}<th>User</th><th>Reason</th></tr></thead><tbody>{activity.rows.map(row => <tr key={row.id}><td>{historyTimestampDisplay(row.date)}</td><td>{row.type.replaceAll('_',' ')}</td><td>{renderHistoryInvoiceReference(row.details?.documentId, row.details?.documentType)}</td><td>{row.details?.serialNo || (row.details?.lineKey ? 'No serial' : row.details?.documentId ? 'Entire invoice' : '-')}</td><td>{row.amount ? currency(row.amount) : '-'}</td>{activityLogIsCreditMemo && <td>{activityDetailValue(row.details?.referenceNo)}</td>}{activityLogIsCreditMemo && <td>{activityDetailValue(row.details?.memo)}</td>}<td>{row.actor}</td><td>{row.details?.reason || '-'}</td></tr>)}</tbody></table></section></div>}
  {invoiceHistory.invoice && <div className="invoice-modal-overlay" onClick={() => setInvoiceHistory({invoice:null,rows:[],loading:false,error:''})}><section className="payment-modal activity-modal" onClick={(e) => e.stopPropagation()}><header><h2>Invoice #{invoiceHistory.invoice.invoiceNo || invoiceHistory.invoice.docNo || invoiceHistory.invoice.id} Transaction History</h2><button onClick={() => setInvoiceHistory({invoice:null,rows:[],loading:false,error:''})}>x</button></header><table className="inventory-table payment-activity-table"><thead><tr><th>Date / Time</th><th>Transaction</th><th>Payment</th><th>Invoice</th><th>Serial #</th><th>Amount</th><th>User</th><th>Reason</th></tr></thead><tbody>{invoiceHistory.loading ? <tr><td colSpan="8" className="payment-empty-row">Loading transaction history...</td></tr> : invoiceHistory.error ? <tr><td colSpan="8" className="payment-empty-row apply-payment-error">{invoiceHistory.error}</td></tr> : <>{invoiceHistory.rows.map(row => <tr key={row.id}><td>{historyTimestampDisplay(row.date)}</td><td>{String(row.type || '').replaceAll('_',' ')}</td><td>{renderHistoryPaymentReference(row.details?.paymentId)}</td><td>{renderHistoryInvoiceReference(row.details?.documentId || invoiceHistory.invoice.docNo || invoiceHistory.invoice.id, row.details?.documentType || (Number(invoiceHistory.invoice.transactionTypeId) === 640 ? 'repair' : 'invoice'))}</td><td>{row.details?.serialNo || (row.details?.lineKey ? 'No serial' : row.details?.documentId ? 'Entire invoice' : '-')}</td><td>{row.amount ? currency(row.amount) : '-'}</td><td>{row.actor || '-'}</td><td>{row.details?.reason || row.details?.paymentType || '-'}</td></tr>)}{invoiceHistory.rows.length===0 && <tr><td colSpan="8" className="payment-empty-row">No transaction history found for this invoice.</td></tr>}</>}</tbody></table></section></div>}
  </>;
}
